import React, { createContext, useContext, useState } from "react";
import { getPlatformName } from "../utils/platformIcons";

type Language = "pt" | "en";

type TranslationVars = Record<string, string | number>;

interface LanguageContextProps {
  language: Language;
  setLanguage: (lang: Language) => void;
  /** Traduz texto. Suporta placeholders `{nome}` via segundo argumento. */
  t: (text: string, vars?: TranslationVars) => string;
}

const LanguageContext = createContext<LanguageContextProps | undefined>(
  undefined,
);

// Dicionário de tradução PT -> EN
const translations: Record<string, string> = {
  // Telas de autenticação
  "Redirecionando...": "Redirecting...",
  "Você será levado de volta ao aplicativo de origem.":
    "You will be taken back to the app you came from.",
  "Mínimo 8 caracteres, com letras, número e símbolo":
    "At least 8 characters, with letters, a number and a symbol",
  "Letras maiúsculas e minúsculas": "Upper and lower case letters",
  "Ao menos um caractere especial (!@#$...)":
    "At least one special character (!@#$...)",
  "Pelo menos uma letra minúscula": "At least one lowercase letter",
  "Seu cadastro foi enviado e aguarda a aprovação de um administrador.":
    "Your sign-up was submitted and is awaiting administrator approval.",
  "Cadastro registrado para:": "Sign-up registered for:",
  "• O administrador analisará a sua solicitação.":
    "• An administrator will review your request.",
  "• Depois da aprovação, você poderá entrar com este e-mail e a senha cadastrada.":
    "• After approval, you can sign in with this email and the password you registered.",
  "Ir para a tela de login": "Go to the sign-in screen",
  "Corrigir dados ou tentar outro e-mail": "Fix details or try another email",
  "Existem campos obrigatórios não preenchidos.":
    "There are required fields left blank.",
  "O endereço de e-mail informado não possui um formato válido.":
    "The email address entered is not in a valid format.",
  "Informe um telefone válido, com DDD.":
    "Enter a valid phone number, including area code.",
  "A senha não atende a todos os requisitos de segurança exigidos.":
    "The password does not meet all required security criteria.",
  "As senhas digitadas não coincidem.": "The passwords entered do not match.",
  "Ao criar sua conta corporativa, você concorda com nossos":
    "By creating your corporate account, you agree to our",
  "Política de Privacidade (LGPD)": "Privacy Policy (LGPD)",
  "Sessão expirada": "Session expired",
  "Anúncios e leads": "Ads and leads",
  "Campanhas guiadas em Meta, Google e LinkedIn, com formulário de leads, funil Kanban e atribuição por campanha.":
    "Guided Meta, Google and LinkedIn campaigns, with a lead form, Kanban funnel and campaign attribution.",
  "Publicações com IA e agendamento": "Posts with AI and scheduling",
  "Crie posts para Facebook, Instagram e LinkedIn e programe com o Cronograma IA.":
    "Create posts for Facebook, Instagram and LinkedIn and schedule them with the AI Schedule.",
  "Anúncios do briefing ao resultado": "Ads from brief to result",
  "Leads e receita": "Leads and revenue",
  "Conteúdo e estratégia com IA": "Content and strategy with AI",
  "Studio de Vídeo com clonagem de voz, e-books, mapeamento de público (ICP) e recomendações baseadas em resultados reais.":
    "Video Studio with voice cloning, ebooks, audience (ICP) mapping and recommendations based on real results.",
  "Crie e programe conteúdo, gerencie campanhas e acompanhe leads e resultados sem trocar de ferramenta. Use o mesmo login em todas as soluções Virtus.":
    "Create and schedule content, manage campaigns and follow leads and results without switching tools. Use the same login across every Virtus solution.",
  "Bem-vindo de volta": "Welcome back",
  "Acesse sua conta corporativa para gerenciar suas redes e campanhas.":
    "Sign in to your corporate account to manage your social networks and campaigns.",
  "Endereço de e-mail": "Email address",
  "Senha de acesso": "Password",
  "joao.silva@empresa.com": "john.smith@company.com",
  "Sua senha corporativa": "Your corporate password",
  "Manter-me conectado neste dispositivo": "Keep me signed in on this device",
  "Entrar na plataforma": "Sign in to the platform",
  "Ainda não possui uma conta?": "Don't have an account yet?",
  "Criar conta corporativa": "Create corporate account",
  "Comece a usar e impulsione sua operação.":
    "Start using and boost your operation.",
  "(11) 91234-5678": "(555) 123-4567",
  "Mínimo 8 caracteres, com letra e número":
    "At least 8 characters, with a letter and a number",
  "Digite a mesma senha novamente": "Type the same password again",
  "Criando sua conta corporativa...": "Creating your corporate account...",
  "Finalizar cadastro": "Complete sign-up",
  "Já possui uma conta corporativa?": "Already have a corporate account?",
  "Fazer login": "Sign in",
  "Sistemas Operacionais": "Systems operational",
  "Instabilidade detectada": "Instability detected",
  "Verificando sistemas...": "Checking systems...",
  "Ao criar sua conta, você concorda com nossos":
    "By creating your account, you agree to our",
  "Termos de Uso": "Terms of Use",
  "Português e inglês": "Portuguese and English",
  "Plataforma de Marketing Digital": "Digital Marketing Platform",
  "Conta única Virtus": "Single Virtus account",
  "Redes sociais e anúncios em": "Social media and ads in",
  "um só lugar": "one place",
  "Virtus SMM — ir para o início": "Virtus SMM — go to start",
  "Conexão Segura": "Secure connection",
  "Erro ao entrar": "Sign-in error",
  "Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.":
    "Could not connect to the server. Check your connection and try again.",
  "Mínimo de 8 caracteres": "At least 8 characters",
  "Ao menos uma letra": "At least one letter",
  "Ao menos um número": "At least one number",
  "Requisitos da senha:": "Password requirements:",
  "Senhas conferem": "Passwords match",
  "As senhas devem ser idênticas": "Passwords must match",
  "Próximos passos:": "Next steps:",
  "Tudo pronto!": "All set!",

  // User Profile
  "Seu Perfil": "Your Profile",
  "Gerencie seus dados pessoais, altere sua senha e configure suas preferências de acesso.":
    "Manage your personal data, change your password, and configure your access preferences.",
  "Nome Completo": "Full Name",
  "Seu nome completo": "Your full name",
  "Email Institucional": "Institutional Email",
  "O email não pode ser alterado por segurança":
    "Email cannot be changed for security reasons",
  "Empresa / Organização": "Company / Organization",
  "Nome da sua empresa": "Your company name",
  "Telefone / WhatsApp": "Phone / WhatsApp",
  "Alteração de Senha": "Password Change",
  "Senha Atual": "Current Password",
  "Sua senha atual": "Your current password",
  "Nova Senha": "New Password",
  "Entre 8 e 64 caracteres": "Between 8 and 64 characters",
  "Requisitos de Senha Forte": "Strong Password Requirements",
  "Pelo menos uma letra maiúscula": "At least one uppercase letter",
  "Pelo menos um número": "At least one number",
  "Um caractere especial (ex: !@#$%^&*)":
    "One special character (e.g., !@#$%^&*)",
  "Confirmar Nova Senha": "Confirm New Password",
  "Repita a nova senha": "Repeat the new password",
  "Salvar Alterações": "Save Changes",
  "Membro Desde & Último Acesso": "Member Since & Last Access",
  "Membro Desde": "Member Since",
  "Último Acesso": "Last Access",
  Recente: "Recent",
  "Informações Adicionais": "Additional Information",
  Empresa: "Company",
  "Não informada": "Not provided",
  Telefone: "Phone",
  "Não informado": "Not provided",
  "Tipo de Verificação": "Verification Type",
  "Email Standard": "Standard Email",
  "Proteção & Privacidade": "Protection & Privacy",
  "Sua conta está protegida por criptografia de ponta a ponta. Mantenha suas credenciais seguras e não as compartilhe.":
    "Your account is protected by end-to-end encryption. Keep your credentials safe and do not share them.",
  "SSL Ativo": "Active SSL",
  "Sessão Segura": "Secure Session",
  "Precisa de Ajuda?": "Need Help?",
  "Nossa equipe está disponível para tirar qualquer dúvida sobre as configurações de sua conta.":
    "Our team is available to answer any questions about your account settings.",
  "Contatar Suporte": "Contact Support",
  "Senha atual incorreta": "Incorrect current password",
  "A senha não cumpre todos os requisitos":
    "Password does not meet all requirements",
  "Número de telefone inválido": "Invalid phone number",
  "O nome deve ter pelo menos 2 caracteres":
    "Name must have at least 2 characters",

  // Navigation / Sidebar
  Dashboard: "Dashboard",
  Conteúdo: "Content",
  Biblioteca: "Library",
  Ebooks: "Ebooks",
  Insights: "Insights",
  "Insights de Conteúdo": "Content Insights",
  "Posts (Orgânico)": "Posts (Organic)",
  Postagens: "Posts",
  "Estúdio de Vídeo & Voz": "Video & Voice Studio",
  "Cronograma IA": "AI Schedule",
  Calendário: "Calendar",
  "Desempenho de posts orgânicos": "Organic posts performance",
  "Anúncios (Pago)": "Ads (Paid)",
  "Novo Anúncio": "New Ad",
  Anúncios: "Ads",
  Leads: "Leads",
  "Virtus Leadflow": "Virtus Leadflow",
  "Virtus Chat": "Virtus Chat",
  "Mapa de Empatia": "Empathy Map",
  "Respostas Automáticas": "Automated Replies",
  Inteligência: "Intelligence",
  "Analytics Geral": "General Analytics",
  "Inteligência de Receita": "Revenue Intelligence",
  "Aprendizado IA": "AI Learning",
  Estratégia: "Strategy",
  "Estratégia de Marketing": "Marketing Strategy",
  "Estratégia de Campanha": "Campaign Strategy",
  "Minha Organização": "My Organization",
  "Gerenciar Usuários": "Manage Users",
  "Empresas Cliente": "Client Companies",
  Configurações: "Settings",
  Perfil: "Profile",
  "Ajuda & Suporte": "Help & Support",
  "Menu principal": "Main menu",
  "Fechar menu": "Close menu",
  "Recolher menu": "Collapse menu",
  "Expandir menu": "Expand menu",
  Publicar: "Publish",
  "Público e vendas": "Audience & sales",
  "Apps Virtus": "Virtus apps",
  "{count} posts com falha": "{count} failed posts",
  "Gerenciar Leads": "Manage Leads",
  "Analytics & IA": "Analytics & AI",
  // Tooltips da sidebar
  "Resumo dos seus posts, anúncios e atividades":
    "Summary of your posts, ads and activity",
  "Textos, links e ebooks que servem de base para os posts":
    "Texts, links and ebooks your posts are built from",
  "Textos e links salvos para usar nos posts":
    "Saved texts and links to use in posts",
  "Crie e organize seus ebooks": "Create and organize your ebooks",
  "Uso e desempenho da biblioteca e dos ebooks":
    "Library and ebook usage and performance",
  "Crie, agende e acompanhe posts nas redes":
    "Create, schedule and track social posts",
  "Todos os posts, com filtros por status": "All posts, filterable by status",
  "Clone sua voz e gere vídeos a partir de roteiros":
    "Clone your voice and turn scripts into videos",
  "Sugestões de posts geradas pela IA": "Post suggestions generated by AI",
  "Posts agendados, dia a dia": "Scheduled posts, day by day",
  "Desempenho dos posts por rede": "Post performance by network",
  "Crie e acompanhe campanhas pagas": "Create and track paid campaigns",
  "Crie uma campanha no Meta ou no Google":
    "Create a campaign on Meta or Google",
  "Campanhas ativas, gastos e resultados":
    "Active campaigns, spend and results",
  "Conversões, custo por lead e retorno":
    "Conversions, cost per lead and return",
  "IA que responde comentários e mensagens":
    "AI that replies to comments and messages",
  "Leads vindos dos anúncios e do LeadFlow": "Leads from your ads and LeadFlow",
  "Personas e perfil de cliente ideal (ICP)":
    "Personas and ideal customer profile (ICP)",
  "Leads mais quentes e campanhas que geram vendas":
    "Hottest leads and campaigns that drive sales",
  "Recomendações da IA para orgânico e pago":
    "AI recommendations for organic and paid",
  "Tendências, sentimento e hashtags do seu conteúdo":
    "Trends, sentiment and hashtags for your content",
  "Capta leads dos anúncios e envia aos CRMs · abre com seu login":
    "Captures ad leads and sends them to CRMs · opens with your login",
  "CRM de WhatsApp · abre com seu login":
    "WhatsApp CRM · opens with your login",
  "Prospecção e funil de vendas B2B · abre com seu login":
    "B2B prospecting and sales pipeline · opens with your login",
  "Landing pages criadas com IA · abre com seu login":
    "AI-built landing pages · opens with your login",
  "Indicadores por empresa, filial e cliente · abre com seu login":
    "KPIs by company, branch and client · opens with your login",
  "Central de Ajuda": "Help Center",
  "Notificações (em breve)": "Notifications (coming soon)",
  "Alternar menu lateral": "Toggle sidebar",
  "Pesquisar posts, campanhas, conteúdos...":
    "Search posts, campaigns, content...",
  Resultados: "Results",
  "Acesso rápido": "Quick access",
  "Nenhum resultado encontrado": "No results found",
  Notificações: "Notifications",
  Sair: "Logout",
  "Minha Conta": "My Account",
  "Visão geral do sistema": "System overview",
  "Biblioteca de conteúdos e ebooks": "Content and ebooks library",
  "Criar e editar conteúdos da biblioteca": "Create and edit library content",
  "Gerenciar ebooks": "Manage ebooks",
  "Métricas de conteúdo e ebooks": "Content and ebooks metrics",
  "Gestão de posts e calendário orgânico":
    "Posts management and organic calendar",
  "Lista de posts orgânicos": "List of organic posts",
  "Clonagem de voz ElevenLabs e roteiros de vídeos em MP4":
    "ElevenLabs voice cloning and MP4 video scripts",
  "Sugestões de posts geradas por IA": "AI-generated post suggestions",
  "Visualizar agendamentos no calendário": "View schedule in calendar",
  "Lançamento de anúncios e gestão de leads": "Ad launch and lead management",
  "Criar novas campanhas Meta ou Google": "Create new Meta or Google campaigns",
  "Dashboard de anúncios": "Ads dashboard",
  "Gerenciar leads gerados via anúncios": "Manage leads generated via ads",
  "Analytics avançado de conversão e ROI":
    "Advanced conversion & ROI analytics",
  "Gestão de integrações e captação de leads":
    "Integration & lead acquisition management",
  "CRM de WhatsApp — login automático (SSO)":
    "WhatsApp CRM — automatic login (SSO)",
  "Personas e mapa de empatia": "Personas and empathy mapping",
  "Respostas automáticas e comentários": "Auto-responses and comments",
  "Dashboard geral de analytics": "General analytics dashboard",
  "Modelos e aprendizado de máquina": "Models and machine learning",
  "Estratégias recomendadas pela IA": "AI-recommended strategies",
  "Gerenciar usuários da organização": "Manage organization users",
  Organização: "Organization",
  Usuários: "Users",
  Empresas: "Companies",
  Mapeamentos: "Mappings",
  "Jornada do Lead": "Lead Journey",
  "Meta Insights": "Meta Insights",
  "Nova Empresa Cliente": "New Client Company",
  "Detalhes da Empresa": "Company Details",
  Autenticação: "Authentication",
  "Visão geral": "Overview",
  Geral: "General",
  Salvar: "Save",
  Cancelar: "Cancel",
  Editar: "Edit",
  Excluir: "Delete",
  Deletar: "Delete",
  Adicionar: "Add",
  Confirmar: "Confirm",
  Suporte: "Support",
  Idioma: "Language",
  "Carregando...": "Loading...",
  Voltar: "Back",
  Criar: "Create",
  "Filtrar por": "Filter by",
  "Buscar...": "Search...",
  Total: "Total",
  Ações: "Actions",
  Ativo: "Active",
  Inativo: "Inactive",
  Pendentes: "Pending",
  Agendados: "Scheduled",
  Publicados: "Published",
  Falhados: "Failed",
  Rascunhos: "Drafts",
  Todos: "All",
  "Visão geral do desempenho das suas redes sociais. Acompanhe métricas em tempo real, engajamento e performance dos seus posts.":
    "Overview of your social networks performance. Track real-time metrics, engagement, and performance of your posts.",
  "Última atualização:": "Last update:",
  "Total de Posts": "Total Posts",
  "Todos os tempos": "All time",
  "Próximas 24h": "Next 24h",
  "Últimas 24h": "Last 24h",
  Erros: "Errors",
  "Posts por Plataforma": "Posts by Platform",
  "Nenhum post por plataforma ainda": "No posts by platform yet",
  "Atividade Recente": "Recent Activity",
  "Nenhuma atividade recente": "No recent activity",
  "Ações Rápidas": "Quick Actions",
  "Agendar Post": "Schedule Post",
  "Criar Conteúdo": "Create Content",
  "Ver Analytics": "View Analytics",
  "Monitorar IA": "Monitor AI",
  "Resumo do Dia": "Day Summary",
  "Posts agendados hoje": "Posts scheduled today",
  "Posts publicados": "Published posts",
  "Pendentes aprovação": "Pending approval",
  "Conteúdos processados": "Processed contents",
  "Meu Perfil": "My Profile",
  "Painel Admin (Auditoria)": "Admin Panel (Audit)",
  "Encerrar Sessão": "Logout",
  "Sessão Ativa": "Active Session",
  "Organizações Globais": "Global Organizations",
  Usuário: "User",
  "Menu do usuário": "User menu",
  "Insights de Posts": "Posts Insights",
  "Insights de Anúncios": "Ads Insights",
  "Redes Sociais": "Social Media",
  "WhatsApp CRM": "WhatsApp CRM",

  "Conteúdo excluído com sucesso!": "Content deleted successfully!",
  "Erro ao excluir conteúdo.": "Error deleting content.",
  "Conteúdo criado com sucesso!": "Content created successfully!",
  "Gerenciar Conteúdos": "Manage Contents",
  "Visualize, organize e gerencie sua biblioteca de conteúdos criados.":
    "View, organize and manage your created content library.",
  "Buscar conteúdos...": "Search contents...",
  URLs: "URLs",
  Textos: "Texts",
  "Prompts IA": "AI Prompts",
  Arquivos: "Files",
  "Mais recentes": "Most recent",
  "Nome (A-Z)": "Name (A-Z)",
  "Por tipo": "By type",
  Exibindo: "Showing",
  de: "of",
  conteúdos: "contents",
  "Limpar filtros": "Clear filters",
  "Nenhum conteúdo salvo": "No content saved",
  "Crie seu primeiro conteúdo para começar a gerar posts e calendários de publicação nas redes sociais.":
    "Create your first content to start generating posts and publishing calendars on social networks.",
  "Criar Primeiro Conteúdo": "Create First Content",
  "Nenhum conteúdo encontrado": "No content found",
  "Não foram encontrados conteúdos com os filtros aplicados.":
    "No content found with applied filters.",
  "Busca:": "Search:",
  "Limpar Filtros": "Clear Filters",
  "Criar Novo Conteúdo": "Create New Content",
  "Confirmar Exclusão": "Confirm Deletion",
  "Esta ação removerá permanentemente o material da biblioteca":
    "This action will permanently remove the material from the library",
  "Manter Conteúdo": "Keep Content",
  "Sim, Excluir": "Yes, Delete",
  "Tem certeza que deseja excluir o conteúdo":
    "Are you sure you want to delete the content",
  "Atenção: Esta ação é irreversível": "Attention: This action is irreversible",
  "Conteúdo editado com sucesso!": "Content edited successfully!",
  "Erro ao editar conteúdo.": "Error editing content.",
  "🎯 Análise Automática de ICP": "🎯 Automatic ICP Analysis",
  Fechar: "Close",
  "Recomendações aplicadas!": "Recommendations applied!",
  "Aplicar Recomendações": "Apply Recommendations",
  Posts: "Posts",
  "Crie, edite, aprove e publique suas campanhas em múltiplas plataformas.":
    "Create, edit, approve and publish your campaigns on multiple platforms.",
  "Buscar nos posts...": "Search in posts...",
  Filtros: "Filters",
  "Deep Research": "Deep Research",
  Viral: "Viral",
  "Novo Post": "New Post",
  Novo: "New",
  "Nenhum post": "No post",
  "Não há posts": "There are no posts",
  "no momento.": "at the moment.",
  "Criar Post": "Create Post",
  "Filtros Avançados": "Advanced Filters",
  Plataforma: "Platform",
  "Todas as Plataformas": "All Platforms",
  LinkedIn: "LinkedIn",
  Facebook: "Facebook",
  Instagram: "Instagram",
  "Facebook Stories": "Facebook Stories",
  "Instagram Stories": "Instagram Stories",
  "Google My Business": "Google My Business",
  "Google Ads": "Google Ads",
  YouTube: "YouTube",
  TikTok: "TikTok",
  Messenger: "Messenger",
  Remover: "Remove",
  Pronto: "Ready",
  "Abrindo...": "Opening...",
  Templates: "Templates",
  Status: "Status",
  Recomendado: "Recommended",
  Clone: "Clone",
  Ativa: "Active",
  "Post publicado no {platform}": "Post published on {platform}",
  "Post agendado para {platform}": "Post scheduled for {platform}",
  "Erro na publicação {platform}": "Error publishing to {platform}",
  "Conteúdo processado": "Content processed",
  "{n} seg atrás": "{n} sec ago",
  "{n} min atrás": "{n} min ago",
  "{n}h atrás": "{n}h ago",
  "{n} dia atrás": "{n} day ago",
  "{n} dias atrás": "{n} days ago",
  "Seu saldo está baixo ({balance} restantes). Algumas ações premium podem falhar se os créditos terminarem durante o processo.":
    "Your balance is low ({balance} remaining). Some premium actions may fail if credits run out during the process.",
  "Análise concluída! {count} oportunidades de otimização encontradas.":
    "Analysis complete! {count} optimization opportunities found.",
  "✅ Agendamento do post {postId} removido com sucesso!":
    "✅ Post {postId} schedule removed successfully!",
  "✅ Post {postId} cancelado com sucesso!":
    "✅ Post {postId} cancelled successfully!",
  "✅ Post {postId} retornou ao calendário com sucesso!":
    "✅ Post {postId} returned to the calendar successfully!",
  '🔄 Retornar post ao calendário?\n\nData: {date}\nHorário: {time}\n\nO post será reativado e aparecerá na aba "Agendados".':
    '🔄 Return post to the calendar?\n\nDate: {date}\nTime: {time}\n\nThe post will be reactivated and will appear in the "Scheduled" tab.',
  '🔄 Deseja criar uma cópia deste post para o {platform}?\n\nO conteúdo e mídia serão copiados para um novo post do {platform} no seu "Banco de Aprovados".':
    '🔄 Do you want to create a copy of this post for {platform}?\n\nContent and media will be copied to a new {platform} post in your "Approved Bank".',
  "Agendar no {platform}": "Schedule on {platform}",
  "Copiar para {platform}": "Copy to {platform}",
  "✅ Post copiado para {platform} com sucesso! Verifique seu Banco de Aprovados.":
    "✅ Post copied to {platform} successfully! Check your Approved Bank.",
  "Posts do dia {date}": "Posts for {date}",
  "Plataforma: {platform}": "Platform: {platform}",
  "Erro ao carregar dados do dashboard": "Error loading dashboard data",
  "Todos os direitos reservados.": "All rights reserved.",
  Termos: "Terms",
  Privacidade: "Privacy",
  OFFLINE: "OFFLINE",
  "Tipo de Postagem": "Post Type",
  "Todos os Tipos": "All Types",
  Orgânica: "Organic",
  Paga: "Paid",
  "Gerado por IA": "AI Generated",
  "Criado Manualmente": "Manually Created",
  "posts criados com sucesso! Eles aparecerão na lista de pendentes.":
    "posts created successfully! They will appear in the pending list.",
  "Post criado com sucesso! Ele aparecerá na lista de pendentes.":
    "Post created successfully! It will appear in the pending list.",
  "Erro ao criar post": "Error creating post",
  "Métricas sincronizadas com sucesso!": "Metrics synchronized successfully!",
  "Erro ao sincronizar métricas": "Error synchronizing metrics",
  Pausado: "Paused",
  Rascunho: "Draft",
  Erro: "Error",
  Cancelado: "Cancelled",
  Pendente: "Pending",
  Finalizado: "Finished",
  "Não foi possível carregar suas campanhas. Tente novamente mais tarde.":
    "Could not load your campaigns. Try again later.",
  "Erro ao carregar campanhas": "Error loading campaigns",
  "Campanha pausada com sucesso!": "Campaign paused successfully!",
  "Campanha ativada com sucesso!": "Campaign activated successfully!",
  "Erro ao alterar status da campanha": "Error changing campaign status",
  "Tem certeza que deseja excluir esta campanha? Esta ação não pode ser desfeita.":
    "Are you sure you want to delete this campaign? This action cannot be undone.",
  "Campanha excluída com sucesso!": "Campaign deleted successfully!",
  "Erro ao excluir campanha": "Error deleting campaign",
  "campanha(s) pausada(s).": "campaign(s) paused.",
  "Erro ao pausar campanhas": "Error pausing campaigns",
  "campanha(s)?": "campaign(s)?",
  "campanha(s) excluída(s).": "campaign(s) deleted.",
  "Erro ao excluir campanhas": "Error deleting campaigns",
  "Sem Nome": "No Name",
  "Lista de Anúncios": "Ads List",
  "Gerencie e acompanhe a performance de todas as suas campanhas pagas criadas na Virtus.":
    "Manage and track the performance of all your paid campaigns created in Virtus.",
  "Total Investido": "Total Invested",
  "Gasto total em campanhas": "Total spent on campaigns",
  "Leads Gerados": "Leads Generated",
  "Conversões capturadas": "Conversions captured",
  "CPL Médio": "Average CPL",
  "Custo por lead": "Cost per lead",
  Ativas: "Active",
  "Campanhas rodando agora": "Campaigns running now",
  "Buscar campanhas por nome...": "Search campaigns by name...",
  "Meta (FB/IG)": "Meta (FB/IG)",
  "LinkedIn Ads": "LinkedIn Ads",
  "TikTok Ads": "TikTok Ads",
  "Todos status": "All status",
  Pausadas: "Paused",
  "Com erro": "With error",
  "Maior gasto": "Highest spend",
  "Mais leads": "More leads",
  "Mais impressões": "More impressions",
  Sincronizado: "Synchronized",
  "Sincronizar agora": "Synchronize now",
  "Lançar Campanha": "Launch Campaign",
  Lançar: "Launch",
  campanhas: "campaigns",
  "Tentar Novamente": "Try Again",
  "Campanhas pausadas automaticamente pela Meta":
    "Campaigns paused automatically by Meta",
  "As campanhas marcadas com": "The campaigns marked with",
  "Pausada pela Meta": "Paused by Meta",
  "foram pausadas na plataforma e sincronizadas aqui. Verifique no Gerenciador de Anúncios o motivo e reactive quando pronto.":
    "were paused on the platform and synchronized here. Check the Ads Manager for the reason and reactivate when ready.",
  "Sincronizando campanhas...": "Synchronizing campaigns...",
  Anúncio: "Ad",
  Veiculação: "Delivery",
  "Leads Form.": "Leads Form.",
  CPL: "CPL",
  Orçamento: "Budget",
  "Valor usado": "Amount used",
  Impressões: "Impressions",
  "Pausar na plataforma": "Pause on platform",
  "Ativar na plataforma": "Activate on platform",
  "Clique para ampliar o criativo": "Click to enlarge creative",
  "Erro de veiculação": "Delivery error",
  Pausar: "Pause",
  Ativar: "Activate",
  "Leads (formulário)": "Leads (form)",
  "Cliques no link": "Link clicks",
  Conversões: "Conversions",
  "Leads form.": "Leads form.",
  "Sync pendente": "Sync pending",
  "Por lead": "Per lead",
  Diário: "Daily",
  "Nenhuma campanha encontrada": "No campaign found",
  "Você ainda não lançou nenhuma campanha de anúncios ou seus filtros não retornaram resultados.":
    "You haven't launched any ad campaigns yet or your filters didn't return any results.",
  "Criar minha primeira campanha": "Create my first campaign",
  selecionados: "selected",
  selecionado: "selected",
  "Criativo do anúncio": "Ad creative",

  "Erro ao carregar dados": "Error loading data",
  "Configuração atualizada": "Configuration updated",
  "Erro ao atualizar configuração": "Error updating configuration",
  "Configurações do Agente IA atualizadas!": "AI Agent configurations updated!",
  "Erro ao salvar configurações do Agente IA":
    "Error saving AI Agent configurations",
  "Digite o texto do cliente para simular": "Enter customer text to simulate",
  "Sem resposta gerada": "No generated response",
  "Erro ao gerar resposta IA": "Error generating AI response",
  "Classificação salva": "Classification saved",
  "Erro ao salvar classificação": "Error saving classification",
  "Tag criada": "Tag created",
  "Erro ao criar tag": "Error creating tag",
  "Tag removida": "Tag removed",
  "Erro ao remover tag": "Error removing tag",
  "Resposta aprovada com sucesso": "Response approved successfully",
  "Erro ao aprovar resposta": "Error approving response",
  "⚠️ Instagram Direct requer a permissão 'instagram_manage_messages' no app Meta.\n\nSe esta permissão não estiver ativa, o envio falhará com erro (#3).\n\nPara ativar:\n1. Acesse o Meta Developer Portal → App 'Virtus Bot'\n2. App Review → Permissions → adicione 'instagram_manage_messages'\n3. Reconecte via Autenticação Automática nas configurações\n\nDeseja tentar enviar mesmo assim?":
    "⚠️ Instagram Direct requires 'instagram_manage_messages' permission in the Meta app.\n\nIf this permission is not active, sending will fail with error (#3).\n\nTo activate:\n1. Go to Meta Developer Portal → App 'Virtus Bot'\n2. App Review → Permissions → add 'instagram_manage_messages'\n3. Reconnect via Automatic Authentication in settings\n\nDo you want to try sending anyway?",
  "Resposta enviada com sucesso": "Response sent successfully",
  "Erro ao enviar resposta": "Error sending response",
  "Resposta atualizada com sucesso": "Response updated successfully",
  "Erro ao atualizar resposta": "Error updating response",
  "Resposta rejeitada": "Response rejected",
  "Erro ao rejeitar resposta": "Error rejecting response",
  "Monitoramento disparado. Novas mensagens aparecerão em instantes.":
    "Monitoring triggered. New messages will appear shortly.",
  "Erro ao disparar monitoramento": "Error triggering monitoring",
  Monitoramento: "Monitoring",
  Histórico: "History",
  "Base de Conhecimento": "Knowledge Base",
  Configuração: "Configuration",
  "Conectando ao Instagram... Aguarde.":
    "Connecting to Instagram... Please wait.",
  "Instagram conectado com sucesso!": "Instagram connected successfully!",
  "Erro ao conectar Instagram: ": "Error connecting to Instagram: ",
  Desconhecido: "Unknown",
  "Erro na integração Meta: ": "Error in Meta integration: ",
  "Auto-Responder IA": "AI Auto-Responder",
  "Configure respostas inteligentes para comentários e mensagens nas suas redes sociais com IA.":
    "Configure intelligent responses for comments and messages on your social networks with AI.",
  "Verificar novas mensagens agora": "Check new messages now",
  "Atualizar lista": "Update list",
  "Erro na API": "API Error",
  "Clique para ver todas as interações": "Click to see all interactions",
  "Respostas geradas pela IA": "AI generated responses",
  "Clique para ver as mensagens pendentes": "Click to see pending messages",
  "Aguardando sua revisão": "Awaiting your review",
  "Confiança média da IA": "Average AI confidence",
  "Média por interação": "Average per interaction",
  "Mais recentes no topo": "Newest on top",
  "Mais antigas no topo": "Oldest on top",
  "Visualização em lista": "List view",
  "Visualização Kanban": "Kanban view",
  "Selecionar todos os pendentes": "Select all pending",
  "Nenhuma interação encontrada": "No interaction found",
  "Nenhum template criado": "No template created",
  "Cor da tag": "Tag color",
  "Remover tag": "Remove tag",
  "Detalhes da Interação": "Interaction Details",
  "Post de Origem": "Source Post",
  "ex: comment_123456...": "ex: comment_123456...",
  "Como funciona o produto X?": "How does product X work?",
  "Buscar por conteúdo ou gatilho...": "Search by content or trigger...",
  "Nome da tag (ex: VIP, Urgente)": "Tag name (ex: VIP, Urgent)",
  "Nenhuma atividade registrada": "No registered activity",
  "Teste Rápido": "Quick Test",
  Sentimento: "Sentiment",
  "Tipo de Mensagem": "Message Type",
  "Canal de Origem": "Source Channel",
  "Comentário Recebido": "Received Comment",
  "Resposta Sugerida": "Suggested Response",
  "Configuração Global do Agente": "Global Agent Configuration",
  "Status por Plataforma": "Status by Platform",
  "Gerenciador de Tags": "Tag Manager",
  "Histórico da Conversa": "Conversation History",
  "Gatilho do Usuário": "User Trigger",
  "Resposta Gerada pela IA": "AI Generated Response",
  "Ver Tudo": "See All",
  "Gerando...": "Generating...",
  "Gerar Resposta IA": "Generate AI Response",
  Aprovar: "Approve",
  Rejeitar: "Reject",
  Enviar: "Send",
  "Novo Template": "New Template",
  Desativar: "Disable",
  Ligado: "On",
  Desligado: "Off",
  "Apenas Aprovar": "Only Approve",
  "Aprovar e Enviar": "Approve and Send",
  "Enviar Resposta": "Send Response",

  "Erro ao carregar organização": "Error loading organization",
  "Nome é obrigatório": "Name is required",
  Nome: "Name",
  CNPJ: "CNPJ",
  "O link do logotipo deve começar com http:// ou https:// (ex: https://site.com/logo.png)":
    "The logo link must start with http:// or https:// (ex: https://site.com/logo.png)",
  "URL do Logotipo": "Logo URL",
  "Link público para a imagem da logo da empresa (PNG, JPG ou SVG recomendado).":
    "Public link to the company's logo image (PNG, JPG or SVG recommended).",
  "Organização atualizada": "Organization updated",
  "Organização criada": "Organization created",
  "Erro ao salvar": "Error saving",
  "Configuração salva": "Configuration saved",
  "Credenciais verificadas": "Credentials verified",
  "Falha na verificação": "Verification failed",
  "Erro ao verificar": "Error verifying",
  "Sede Corporativa": "Corporate Headquarters",
  "Centralize as informações da sua organização e gerencie as chaves mestras de integração.":
    "Centralize your organization's information and manage integration master keys.",
  "Contexto de Empresa Ativo": "Active Company Context",
  "Esta página gerencia as configurações globais da sua Organização. Para acessá-la, por favor selecione a conta da sua Organização na barra de navegação.":
    "This page manages the global settings of your Organization. To access it, please select your Organization account in the navigation bar.",
  ATIVO: "ACTIVE",
  "SEM CNPJ": "NO CNPJ",
  "Editar Organização": "Edit Organization",
  "Configurar Organização": "Configure Organization",
  "Ajuste de Credenciais Institucionais":
    "Institutional Credentials Adjustment",
  "Configure os dados base da sua nova sede":
    "Configure the base data of your new headquarters",
  "Identificação da Organização": "Organization Identification",
  "Nome oficial da sua empresa ou grupo organizacional conforme registro legal.":
    "Official name of your company or organizational group according to legal registration.",
  "Ex: Virtus Soluções Integradas": "Ex: Virtus Integrated Solutions",
  "CNPJ (Oficial)": "CNPJ (Official)",
  "Cadastro Nacional da Pessoa Jurídica. Use apenas números ou formato 00.000.000/0001-00.":
    "National Registry of Legal Entities. Use only numbers or 00.000.000/0001-00 format.",
  "Segmento / Setor": "Segment / Sector",
  "Área de atuação principal da empresa. Usado para contextualizar a identificação do ICP.":
    "Main area of operation of the company. Used to contextualize ICP identification.",
  "Ex: Automação Industrial, Paisagismo, Marketing Digital":
    "Ex: Industrial Automation, Landscaping, Digital Marketing",
  "Telefone de Contato": "Contact Phone",
  "Número de telefone principal da empresa. Usado como fallback para Call Assets (Anúncios de Chamada) no Google Ads.":
    "Main phone number of the company. Used as fallback for Call Assets in Google Ads.",
  "Website Oficial": "Official Website",
  "Link do site principal da organização. Usado para gerar links de destino e rastreamento.":
    "Link to the organization's main website. Used to generate destination and tracking links.",
  "Política de Privacidade": "Privacy Policy",
  "URL da política de privacidade. Obrigatório para campanhas de formulário (Leads) no Meta Ads.":
    "Privacy policy URL. Mandatory for lead form campaigns in Meta Ads.",
  CEP: "ZIP Code",
  "Endereço / Rua": "Address / Street",
  "Av. Paulista": "Paulista Ave.",
  Número: "Number",
  Bairro: "Neighborhood",
  "Bela Vista": "Bela Vista",
  Cidade: "City",
  "São Paulo": "São Paulo",
  Estado: "State",
  "Público-alvo (ICP)": "Target Audience (ICP)",
  "define quem é o cliente ideal no Mapa de Empatia":
    "defines who the ideal customer is in the Empathy Map",
  "Ex: Gerentes de produção de indústrias metalúrgicas que buscam reduzir custos com automação":
    "Ex: Production managers in metallurgical industries looking to reduce automation costs",
  "Sincronizando...": "Synchronizing...",
  "Criar Organização": "Create Organization",
  "Master Config": "Master Config",
  "Configurações Base do Sistema": "System Base Configurations",
  "Ecossistema de Integrações": "Integrations Ecosystem",
  "A nova senha deve ter pelo menos 6 caracteres":
    "The new password must have at least 6 characters",
  "As senhas não coincidem": "Passwords do not match",
  "Digite a senha atual para alterar": "Enter current password to change",
  "Perfil atualizado com sucesso!": "Profile updated successfully!",
  "Erro ao atualizar perfil": "Error updating profile",
  "Erro ao atualizar perfil. Tente novamente.":
    "Error updating profile. Try again.",
  "Você tem alterações não salvas. Deseja realmente cancelar?":
    "You have unsaved changes. Do you really want to cancel?",
  "Confirmar cancelamento": "Confirm cancellation",
  "Sim, cancelar": "Yes, cancel",
  "Não, continuar editando": "No, continue editing",
  "Editar Perfil": "Edit Profile",
  "Alterações pendentes": "Pending changes",
  "Lembre-se de salvar suas modificações antes de sair da página.":
    "Remember to save your modifications before leaving the page.",
  "Segurança da Conta": "Account Security",
  "Alterar senha de acesso": "Change access password",
  "Sua conta está protegida por criptografia de ponta a ponta. Mantenha suas credenciais seguras e não as compartilhe com terceiros.":
    "Your account is protected by end-to-end encryption. Keep your credentials secure and do not share them with third parties.",
  "Geração de imagem": "Image generation",
  "Melhoria de imagem": "Image enhancement",
  "Geração de vídeo": "Video generation",
  "Post com IA": "AI Post",
  "Conteúdo com IA": "AI Content",
  "Processamento de conteúdo": "Content processing",
  "Sugestões visuais": "Visual suggestions",
  "Auto-resposta IA": "AI Auto-response",
  "Ebook com IA": "AI Ebook",
  "Criação de post": "Post creation",
  "Agendamento de post": "Post scheduling",
  "Anúncio Pago (Base)": "Paid Ad (Base)",
  "Anúncio Pago (CAPI/Pixel)": "Paid Ad (CAPI/Pixel)",
  "Captura de Lead": "Lead Capture",
  "Uso de IA": "AI Usage",
  "Créditos de IA": "AI Credits",
  "Gestão de recursos para geração de conteúdo":
    "Resource management for content generation",
  "Comprar créditos": "Buy credits",
  "Conta Ativa": "Active Account",
  "Saldo Disponível": "Available Balance",
  créditos: "credits",
  Consumidos: "Consumed",
  "Total Gasto (CR)": "Total Spent (CR)",
  "Dias de Uso": "Days of Use",
  "Na plataforma": "On the platform",
  "Custos por Ação": "Costs per Action",
  cr: "cr",
  "Carregando tabela de custos...": "Loading cost table...",
  "Saldo de Créditos Esgotado": "Credits Balance Exhausted",
  "Atenção ao Saldo de Créditos": "Attention to Credits Balance",
  "Você atingiu o limite de uso. Para continuar gerando imagens, vídeos e conteúdos com inteligência artificial, é necessário adquirir novos créditos.":
    "You have reached the usage limit. To continue generating images, videos, and content with artificial intelligence, you must purchase new credits.",
  "Histórico de Atividade": "Activity History",
  "Atualizando histórico...": "Updating history...",
  "Nenhum registro encontrado": "No record found",
  Data: "Date",
  "Ação / Descrição": "Action / Description",
  Impacto: "Impact",
  CR: "CR",
  Anterior: "Previous",
  Próxima: "Next",
  "As integrações com": "Integrations with",
  "LinkedIn, Facebook, Instagram e Google":
    "LinkedIn, Facebook, Instagram and Google",
  "foram movidas para os níveis de Organização e Empresa Cliente para melhor escalabilidade.":
    "have been moved to the Organization and Client Company levels for better scalability.",
  "Dica Pro": "Pro Tip",
  "Você pode economizar créditos revisando suas sugestões antes de gerar imagens ou vídeos de alta qualidade.":
    "You can save credits by reviewing your suggestions before generating high-quality images or videos.",
  "ECONOMIZE COM INTELIGÊNCIA": "SAVE WITH INTELLIGENCE",
  "Erro desconhecido": "Unknown error",
  "Analytics coletados!": "Analytics collected!",
  "posts analisados": "posts analyzed",
  "Erro:": "Error:",
  "Erro ao coletar analytics:": "Error collecting analytics:",
  "Dashboard de Aprendizado": "Learning Dashboard",
  "Insights baseados em dados reais de engajamento para potencializar sua estratégia de conteúdo social.":
    "Insights based on real engagement data to power your social content strategy.",
  "Tentar novamente": "Try again",
  "Nenhum dado disponível": "No data available",
  "Publique alguns posts para ver as métricas de aprendizado.":
    "Publish some posts to see learning metrics.",
  "Modelos de Performance": "Performance Models",
  "Canais Integrados": "Integrated Channels",
  "X (Twitter)": "X (Twitter)",
  "Selecione a plataforma": "Select platform",
  "Ciclo 7 Dias": "7 Days Cycle",
  "Ciclo 30 Dias": "30 Days Cycle",
  "Ciclo Q1 - 90 Dias": "Q1 Cycle - 90 Days",
  "Selecione o período": "Select period",
  "Sincronizar IA": "Synchronize AI",
  "Status da Coleta": "Collection Status",
  Dispensar: "Dismiss",
  "Centro de Aprendizagem Criativo": "Creative Learning Center",
  "Sincronização profunda de modelos baseados em performance real.":
    "Deep synchronization of models based on real performance.",
  "Métricas Consolidadas": "Consolidated Metrics",
  "Benchmarks de Elite": "Elite Benchmarks",
  "Escopo Temporal": "Time Scope",
  Dias: "Days",
  "Estratégia:": "Strategy:",
  'Use "Sincronizar IA" regularmente para garantir que seus modelos de predição reflitam as mudanças sazonais da audiência.':
    'Use "Synchronize AI" regularly to ensure your prediction models reflect seasonal audience changes.',
  "Plano Estratégico Semanal": "Weekly Strategic Plan",
  'Execute "Sincronizar IA" para gerar seu plano semanal baseado na performance atual.':
    'Run "Synchronize AI" to generate your weekly plan based on current performance.',
  "Gerado por IA com base na performance multicanal atual.":
    "AI generated based on current multichannel performance.",
  "Panorama da Semana": "Week Overview",
  "Itens de Ação Recomendados": "Recommended Action Items",
  "Alocação de Orçamento Sugerida": "Suggested Budget Allocation",
  "Engajamento Médio": "Average Engagement",
  "Total de Views": "Total Views",
  "Performance por Plataforma": "Performance by Platform",
  "Distribuição granular de métricas por canal integrado.":
    "Granular distribution of metrics by integrated channel.",
  "Nenhuma métrica de plataforma disponível para o período selecionado.":
    "No platform metrics available for the selected period.",
  "posts sincronizados": "posts synchronized",
  Views: "Views",
  Likes: "Likes",
  Shares: "Shares",
  Comments: "Comments",
  "Taxa de Engajamento": "Engagement Rate",
  "Horários Ótimos": "Optimal Times",
  "Análise preditiva de janelas de oportunidade baseada no histórico de retenção e engajamento da sua audiência.":
    "Predictive analysis of opportunity windows based on your audience's retention and engagement history.",
  "Pico de Engajamento": "Engagement Peak",
  "Nenhuma janela de alta performance detectada neste ciclo.":
    "No high-performance window detected in this cycle.",
  Interações: "Interactions",
  Performance: "Performance",
  "CTAs de Conversão": "Conversion CTAs",
  "Chamadas para ação filtradas pelo maior índice de clique e resposta.":
    "Calls to action filtered by highest click and response rate.",
  "Sem dados de conversão para as CTAs analisadas.":
    "No conversion data for the analyzed CTAs.",
  "Alta Eficácia": "High Efficacy",
  conversão: "conversion",
  Amostras: "Samples",
  "Cliques Únicos": "Unique Clicks",
  "Benchmarks de Performance": "Performance Benchmarks",
  "Top 5 conteúdos com maior autoridade e tração orgânica.":
    "Top 5 contents with greatest authority and organic traction.",
  "Nenhum post atingiu o benchmark de alta performance neste período.":
    "No post reached the high-performance benchmark in this period.",
  Engajamento: "Engagement",
  "Guia de Negócios": "Business Guide",
  "Guia completo para implementação de soluções empresariais":
    "Complete guide for implementing business solutions",
  "Tutorial Técnico": "Technical Tutorial",
  "Passo a passo para implementação técnica":
    "Step by step for technical implementation",
  "Estudo de Caso": "Case Study",
  "Análise detalhada de casos de sucesso": "Detailed analysis of success cases",
  Whitepaper: "Whitepaper",
  "Documento técnico e acadêmico": "Technical and academic document",
  Checklist: "Checklist",
  "Lista prática de verificação": "Practical verification list",
  "Erro ao carregar ebooks": "Error loading ebooks",
  "Tópico é obrigatório": "Topic is mandatory",
  "Ebook criado com sucesso!": "Ebook created successfully!",
  "Erro ao criar ebook": "Error creating ebook",
  "Ebook excluído com sucesso!": "Ebook deleted successfully!",
  "Erro ao excluir ebook.": "Error deleting ebook.",
  "Ebook salvo com sucesso!": "Ebook saved successfully!",
  "Erro ao salvar ebook": "Error saving ebook",
  "Download iniciado!": "Download started!",
  "Erro ao baixar ebook": "Error downloading ebook",
  "Gerenciar Ebooks": "Manage Ebooks",
  "Crie, edite e gerencie seus ebooks para captura de leads e materiais ricos.":
    "Create, edit and manage your ebooks to capture leads and rich materials.",
  "Buscar ebooks...": "Search ebooks...",
  "Status:": "Status:",
  "Template:": "Template:",
  Gerando: "Generating",
  "Guia Negócios": "Business Guide",
  Tutorial: "Tutorial",
  "Estudo Caso": "Case Study",
  Ordenação: "Sort",
  Downloads: "Downloads",
  "Novo Ebook": "New Ebook",
  "resultados encontrados": "results found",
  "Nenhum ebook criado": "No ebook created",
  "Crie seu primeiro ebook para começar a capturar leads através de downloads.":
    "Create your first ebook to start capturing leads through downloads.",
  "Criar Primeiro Ebook": "Create First Ebook",
  "Nenhum ebook encontrado": "No ebook found",
  "Não foram encontrados ebooks com os filtros aplicados.":
    "No ebooks found with applied filters.",
  "Criar Novo Ebook": "Create New Ebook",
  "Defina o tema e o template para iniciar a geração com IA":
    "Define the theme and template to start generating with AI",
  "Criando...": "Creating...",
  "Iniciar Geração": "Start Generation",
  "Tema Central do Ebook": "Core Theme of the Ebook",
  "Ex: Como escalar seu Marketing B2B com Automação":
    "Ex: How to scale your B2B Marketing with Automation",
  "Estrutura de Conteúdo (Template)": "Content Structure (Template)",
  "Caps.": "Chaps.",
  "Público Preferencial": "Preferred Audience",
  "Profissionais de Área": "Area Professionals",
  "Empresários e Founders": "Entrepreneurs and Founders",
  "Estudantes / Graduandos": "Students / Undergraduates",
  "Gerentes e Líderes": "Managers and Leaders",
  "Diretores e C-Level": "Directors and C-Level",
  "Autor / Especialista": "Author / Expert",
  "Nome do autor para o rodapé": "Author's name for the footer",
  "Identidade Corporativa (Opcional)": "Corporate Identity (Optional)",
  "Sua Empresa ou Marca": "Your Company or Brand",
  "Esta ação removerá permanentemente o material":
    "This action will permanently remove the material",
  "Manter Ebook": "Keep Ebook",
  "Tem certeza que deseja excluir o ebook":
    "Are you sure you want to delete the ebook",
  "Painel de Leads": "Leads Dashboard",
  "Monitorando conversões:": "Monitoring conversions:",
  "Fechar Monitor": "Close Monitor",
  "Audiência Capturada": "Captured Audience",
  Contatos: "Contacts",
  "Até o momento": "So far",
  "Atualizar Dados": "Update Data",
  "Aguardando as primeiras conversões...":
    "Waiting for the first conversions...",
  "Os leads aparecerão aqui assim que baixarem o material.":
    "Leads will appear here as soon as they download the material.",
  "Contato Identificado": "Identified Contact",
  "Organização / Setor": "Organization / Sector",
  Qualidade: "Quality",
  "Setor não informado": "Sector not informed",
  "de Match": "Match",
  "Alta Prioridade": "High Priority",
  "Análise de Refinamento — Resultados Reais":
    "Refinement Analysis — Real Results",
  "O que está funcionando": "What is working",
  "O que não está convertendo": "What is not converting",
  "Mudanças Recomendadas": "Recommended Changes",
  "O agente calculou uma nova alocação de verba baseada nos dados reais.":
    "The agent calculated a new budget allocation based on real data.",
  "Aplicar Nova Alocação": "Apply New Allocation",
  "Novo Canal": "New Channel",
  "Verba Semanal Total": "Total Weekly Budget",
  "/semana": "/week",
  "Mensal est.": "Monthly est.",
  "Adicionar canal": "Add channel",
  "Total:": "Total:",
  "(excede 100%)": "(exceeds 100%)",
  "Missão Tática": "Tactical Mission",
  "Missão Cumprida": "Mission Accomplished",
  Missão: "Mission",
  "Adicionar Nova Missão": "Add New Mission",
  "GPT-5.4 Nano (Rápido)": "GPT-5.4 Nano (Fast)",
  "Baixo Custo": "Low Cost",
  "GPT-5.4 Mini (Equilíbrio)": "GPT-5.4 Mini (Balance)",
  "Custo Médio": "Average Cost",
  "Claude 3.5 Sonnet": "Claude 3.5 Sonnet",
  "Alto Custo": "High Cost",
  "GPT-5.4 (Flagship)": "GPT-5.4 (Flagship)",
  Premium: "Premium",
  Canal: "Channel",
  "Agentes concluíram a análise com sucesso!":
    "Agents successfully completed the analysis!",
  "Créditos insuficientes.": "Insufficient credits.",
  "Erro ao comunicar com os Agentes.": "Error communicating with Agents.",
  "Deep Research concluída!": "Deep Research completed!",
  "Erro na Deep Research.": "Error in Deep Research.",
  "Mia gerou sugestões orgânicas e foram salvas!":
    "Mia generated organic suggestions and they were saved!",
  "Erro ao sugerir posts.": "Error suggesting posts.",
  "Estratégia aprovada ✅": "Strategy approved ✅",
  "Feedback enviado.": "Feedback sent.",
  "Erro ao enviar feedback.": "Error sending feedback.",
  "Estratégia avançada salva com sucesso!":
    "Advanced strategy saved successfully!",
  "JSON inválido! Verifique a sintaxe antes de salvar.":
    "Invalid JSON! Check the syntax before saving.",
  "Estratégia salva com sucesso!": "Strategy saved successfully!",
  "Erro ao salvar edições.": "Error saving editions.",
  "Alex analisou os resultados e gerou recomendações!":
    "Alex analyzed the results and generated recommendations!",
  "Erro ao refinar estratégia.": "Error refining strategy.",
  "Nova alocação aplicada. Salve para confirmar.":
    "New allocation applied. Save to confirm.",
  "Despertando Agentes de IA...": "Waking up AI Agents...",
  "Comitê de Agentes de IA": "AI Agents Committee",
  "Um time de agentes virtuais especializados analisando o seu negócio para recomendar a melhor alocação de verba, canais e copies.":
    "A team of specialized virtual agents analyzing your business to recommend the best budget allocation, channels, and copies.",
  "Prontidão da Campanha": "Campaign Readiness",
  "Missões Concluídas": "Missions Completed",
  "Todas as missões concluídas! Estratégia pronta para lançamento.":
    "All missions completed! Strategy ready for launch.",
  "Modelo IA:": "AI Model:",
  Refazer: "Redo",
  "Refinar com Resultados": "Refine with Results",
  "Ideias Orgânicas": "Organic Ideas",
  "Atualizar (Deep Research)": "Update (Deep Research)",
  "Gerar com Deep Research": "Generate with Deep Research",
  "Atualizar Estratégia": "Update Strategy",
  "Gerar Estratégia": "Generate Strategy",
  "Qual o objetivo principal desta campanha?":
    "What is the main goal of this campaign?",
  "A estratégia só mudará quando você atualizar este objetivo e clicar em":
    "The strategy will only change when you update this goal and click on",
  "Ex: Gerar Leads B2B para o nosso serviço de consultoria, focando em diretores de marketing...":
    "Ex: Generate B2B Leads for our consulting service, focusing on marketing directors...",
  "Modo de edição ativo. Altere dados nos cards, ou habilite o Modo Avançado para editar qualquer campo da IA.":
    "Active edit mode. Change data on the cards, or enable Advanced Mode to edit any AI field.",
  "Desativar Modo Avançado": "Disable Advanced Mode",
  "Modo Avançado (JSON)": "Advanced Mode (JSON)",
  "Anotações do Estrategista (Ficam salvas na sessão):":
    "Strategist Notes (Saved in session):",
  "Ex: Cliente pediu para focarmos 80% da verba em tráfego pago...":
    "Ex: Client asked us to focus 80% of the budget on paid traffic...",
  "Editor JSON Bruto (Cuidado ao alterar chaves estruturais):":
    "Raw JSON Editor (Be careful when altering structural keys):",
  "Restaurar Original": "Restore Original",
  "Os Agentes estão aguardando": "Agents are waiting",
  'Defina um objetivo acima e clique em "Gerar Estratégia".':
    'Define a goal above and click on "Generate Strategy".',
  "Sarah (CMO)": "Sarah (CMO)",
  "Estratégia & Visão Geral": "Strategy & Overview",
  "Max (Tráfego)": "Max (Traffic)",
  "Especialista em Canais & Ads": "Channels & Ads Specialist",
  "Alex (Analytics)": "Alex (Analytics)",
  "Analista de Dados & Otimização": "Data & Optimization Analyst",
  "Mia (Social Media)": "Mia (Social Media)",
  "Conteúdo & Engajamento": "Content & Engagement",
  Editado: "Edited",
  "Analisei os canais e criei as seguintes recomendações de campanhas para rodarmos agora:":
    "I analyzed the channels and created the following campaign recommendations to run now:",
  "Com base na nossa verba atual, esta é a alocação de orçamento ideal e os próximos passos:":
    "Based on our current budget, this is the ideal budget allocation and next steps:",
  "Distribuição de Verba": "Budget Distribution",
  "Próximos Passos": "Next Steps",
  "Analisei as métricas orgânicas passadas e preparei estas 3 ideias de postagens infalíveis:":
    "I analyzed past organic metrics and prepared these 3 foolproof post ideas:",
  "Armar Post": "Arm Post",
  'Clique em "Ideias Orgânicas" para me acionar.':
    'Click on "Organic Ideas" to trigger me.',
  "Dar ordens para": "Give orders to",
  "Ordem anotada no diário de bordo!": "Order logged in the logbook!",
  "Como Hackear o Algoritmo do YouTube": "How to Hack the YouTube Algorithm",
  "Engajar seu público nos primeiros 5 segundos é o segredo absoluto de qualquer vídeo de sucesso no YouTube.\n\nA maioria dos criadores comete o erro de começar com vinhetas demoradas ou explicações longas. Em vez disso, comece jogando uma pergunta intrigante ou mostrando o resultado final imediatamente.\n\nDepois de capturar a atenção, entregue o conteúdo prometido de forma clara e ágil, dividindo em 3 tópicos práticos.\n\nPor fim, adicione uma chamada de ação natural e irresistível: peça para curtir e assistir ao próximo vídeo sugerido no card.":
    "Engaging your audience in the first 5 seconds is the absolute secret to any successful YouTube video.\n\nMost creators make the mistake of starting with lengthy intros or long explanations. Instead, start by asking an intriguing question or showing the final result immediately.\n\nAfter capturing attention, deliver the promised content clearly and quickly, dividing it into 3 practical topics.\n\nFinally, add a natural and irresistible call to action: ask them to like and watch the next suggested video on the card.",
  "Não foi possível acessar o microfone. Verifique as permissões de gravação do navegador.":
    "Could not access the microphone. Check browser recording permissions.",
  "Selecione um arquivo de áudio de amostra.": "Select a sample audio file.",
  "Insira um nome para a voz clonada.": "Enter a name for the cloned voice.",
  Voz: "Voice",
  "clonada com sucesso!": "cloned successfully!",
  "Falha ao clonar voz.": "Failed to clone voice.",
  "Ocorreu um erro ao enviar arquivo para o ElevenLabs.":
    "An error occurred while uploading file to ElevenLabs.",
  "Roteiro Otimizado de Alta Performance": "High Performance Optimized Script",
  "O Segredo da Escala com IA": "The Secret of Scaling with AI",
  "A maioria das empresas comete o erro de tentar escalar o atendimento e vendas contratando mais pessoas sem estruturar seus processos.\n\nA inteligência artificial generativa permite mapear o comportamento dos seus melhores clientes e automatizar interações com tom humano de forma previsível.\n\nPara começar a aplicar isso no seu negócio hoje, identifique o maior gargalo de tempo na sua equipe e configure um agente automatizado focado em resolver esse problema.\n\nSe você quer otimizar a operação da sua empresa e aumentar em até 3x a eficiência com base em dados de mercado, clique no link da bio para testar nossa demonstração gratuita.":
    "Most companies make the mistake of trying to scale customer service and sales by hiring more people without structuring their processes.\n\nGenerative artificial intelligence allows mapping the behavior of your best customers and automating interactions with a human tone predictably.\n\nTo start applying this to your business today, identify the biggest time bottleneck in your team and configure an automated agent focused on solving that problem.\n\nIf you want to optimize your company's operation and increase efficiency up to 3x based on market data, click the link in the bio to test our free demo.",
  "Erro ao gerar vídeo.": "Error generating video.",
  "Tempo esgotado aguardando a compilação do vídeo.":
    "Timeout waiting for video compilation.",
  "Resposta inválida ao enfileirar vídeo.":
    "Invalid response when queuing video.",
  "Erro na conexão com o servidor de renderização.":
    "Connection error with rendering server.",
  "Clone sua voz com inteligência artificial e compile roteiros avançados gerados pelo Deep Researcher em vídeos widescreen elegantes com legendas automáticas e drop shadows em segundos!":
    "Clone your voice with artificial intelligence and compile advanced scripts generated by Deep Researcher into elegant widescreen videos with auto-captions and drop shadows in seconds!",
  "Recurso gratuito — não consome créditos da plataforma (diferente da geração de vídeo em Posts).":
    "Free feature — does not consume platform credits (unlike video generation in Posts).",
  "Produzir Vídeos": "Produce Videos",
  "Clonar Voz": "Clone Voice",
  "Compositor de Roteiro": "Script Composer",
  "Importar Roteiro do Deep Researcher": "Import Deep Researcher Script",
  "Título do Roteiro / Tema": "Script Title / Theme",
  "Ex: Como Hackear o Algoritmo": "Ex: How to Hack the Algorithm",
  "Roteiro Completo (Parágrafos = Slides)":
    "Complete Script (Paragraphs = Slides)",
  "Cada parágrafo virará um slide no vídeo":
    "Each paragraph will become a slide in the video",
  "Escreva seu roteiro de vídeo aqui...": "Write your video script here...",
  "Selecione a Voz para Narração": "Select Voice for Narration",
  "Modo de Vídeo": "Video Mode",
  "Fundo Roxo": "Purple Background",
  "Slides estéticos com gradiente premium":
    "Aesthetic slides with premium gradient",
  "Sua Imagem": "Your Image",
  "Fundo personalizado em widescreen": "Custom widescreen background",
  "Avatar Falante": "Talking Avatar",
  "Foto animada de rosto com narração de IA":
    "Animated face photo with AI narration",
  "Anexar ao post (opcional)": "Attach to post (optional)",
  "ID do post para salvar o vídeo gerado":
    "Post ID to save the generated video",
  "Se informado, o vídeo compilado será anexado ao post após a geração.":
    "If informed, the compiled video will be attached to the post after generation.",
  "Sua Foto de Rosto (Avatar)": "Your Face Photo (Avatar)",
  "Imagem de Fundo Personalizada": "Custom Background Image",
  "Clique para selecionar foto": "Click to select photo",
  "Envie uma foto de frente (fundo limpo)":
    "Upload a front-facing photo (clean background)",
  "Envie imagem HD (Widescreen 16:9 ideal)":
    "Upload HD image (Ideal Widescreen 16:9)",
  "Suba uma imagem para compilar o vídeo":
    "Upload an image to compile the video",
  "Sintetizar & Compilar Vídeo MP4": "Synthesize & Compile MP4 Video",
  "Visualização do Estúdio": "Studio Preview",
  "Criando seu Vídeo Viral...": "Creating your Viral Video...",
  "Por favor, aguarde alguns segundos. O FFmpeg está compilando suas fontes e áudio de voz clonada de forma otimizada.":
    "Please wait a few seconds. FFmpeg is compiling your sources and cloned voice audio optimally.",
  "Sintetizando Narração ElevenLabs": "Synthesizing ElevenLabs Narration",
  "Gerando Slides de Alta Definição (Pillow)":
    "Generating High Definition Slides (Pillow)",
  "Mesclando Streams e Renderizando MP4 (FFmpeg)":
    "Merging Streams and Rendering MP4 (FFmpeg)",
  "Falha na Compilação": "Compilation Failed",
  "Seu Vídeo Aparecerá Aqui": "Your Video Will Appear Here",
  "Insira seu roteiro, selecione sua voz de clone ou pré-definida e compile para criar um vídeo incrível.":
    "Insert your script, select your cloned or predefined voice and compile to create an amazing video.",
  "Resumo da Geração": "Generation Summary",
  COMPILADO: "COMPILED",
  "Duração Estimada": "Estimated Duration",
  segundos: "seconds",
  "Formato de Saída": "Output Format",
  "Widescreen MP4 (HD)": "Widescreen MP4 (HD)",
  "Baixar Vídeo (MP4)": "Download Video (MP4)",
  Recomeçar: "Restart",
  "Registrar Nova Amostra de Voz": "Register New Voice Sample",
  "Nome do Clone": "Clone Name",
  "Ex: Minha Voz Principal, Voz Diretor": "Ex: My Main Voice, Director Voice",
  "Descrição (Opcional)": "Description (Optional)",
  "Ex: Voz calma, ideal para tutoriais e vídeos longos.":
    "Ex: Calm voice, ideal for tutorials and long videos.",
  "Enviar Arquivo": "Upload File",
  "Gravar no Microfone": "Record from Microphone",
  "Amostra de Áudio (Arquivo)": "Audio Sample (File)",
  "Clique para selecionar ou arraste o arquivo": "Click to select or drag file",
  "Envie uma gravação de 1-2 minutos de você falando (MP3, WAV ou M4A) para clonagem perfeita.":
    "Upload a 1-2 minute recording of you speaking (MP3, WAV or M4A) for perfect cloning.",
  "Microfone Gravador": "Recording Microphone",
  "Gravando...": "Recording...",
  "Fale de forma clara por 1-2 minutos. Clique no quadrado vermelho para parar.":
    "Speak clearly for 1-2 minutes. Click the red square to stop.",
  "Nova Gravação Capturada!": "New Recording Captured!",
  "Clique para Iniciar Gravação": "Click to Start Recording",
  "Prévia da Amostra Gravada:": "Preview of Recorded Sample:",
  "Recomenda-se gravar ao menos 30 segundos falando em tom normal para obter um clone realista.":
    "It is recommended to record at least 30 seconds speaking in a normal tone to get a realistic clone.",
  "Enviando Voz para Clonagem...": "Sending Voice for Cloning...",
  "Iniciar Clonagem de Voz": "Start Voice Cloning",
  "Diretório de Vozes de Criação": "Creation Voices Directory",
  Padrão: "Default",
  "Sem descrição disponível.": "No description available.",
  "Ouvir Amostra": "Listen to Sample",
  "Nenhuma Voz Carregada": "No Voice Loaded",
  "Não foi possível carregar os posts pendentes.":
    "Could not load pending posts.",
  "Não foi possível carregar os posts aprovados.":
    "Could not load approved posts.",
  "Não foi possível carregar os posts agendados.":
    "Could not load scheduled posts.",
  "Post reagendado com sucesso!": "Post rescheduled successfully!",
  "Não foi possível realizar o agendamento.": "Could not schedule.",
  "Nossa IA está analisando seu cronograma para encontrar as melhores oportunidades de engajamento.":
    "Our AI is analyzing your schedule to find the best engagement opportunities.",
  "Otimizando Cronograma": "Optimizing Schedule",
  "Otimização Concluída": "Optimization Completed",
  "Evite horários de baixo engajamento": "Avoid low engagement times",
  "Diversifique os temas dos posts": "Diversify post topics",
  "Use CTAs claros e objetivos": "Use clear and objective CTAs",
  "Inclua hashtags relevantes": "Include relevant hashtags",
  "Deseja realmente ignorar este erro? O post sairá da lista de agendados, mas você ainda poderá encontrá-lo no histórico de posts rejeitados.":
    "Do you really want to ignore this error? The post will leave the scheduled list, but you can still find it in the rejected posts history.",
  "Descartar Erro": "Discard Error",
  "Sim, descartar": "Yes, discard",
  "Não, manter": "No, keep",
  "Descartando falha...": "Discarding failure...",
  "Erro descartado com sucesso.": "Error discarded successfully.",
  Sucesso: "Success",
  Represado: "Held Back",
  Processando: "Processing",
  Deduplicado: "Deduplicated",
  "Não foi possível descartar a falha do post.":
    "Could not discard post failure.",
  "Data não disponível": "Date not available",
  "Data inválida": "Invalid date",
  hoje: "today",
  amanhã: "tomorrow",
  "⚠️ Tem certeza que deseja remover o agendamento deste post? O conteúdo continuará disponível na lista de posts para ser reagendado futuramente.":
    "⚠️ Are you sure you want to remove this post's schedule? The content will remain available in the posts list to be rescheduled in the future.",
  "Remover agendamento": "Remove schedule",
  "Erro ao remover agendamento": "Error removing schedule",
  "⚠️ Digite o motivo do cancelamento (opcional):":
    "⚠️ Enter the reason for cancellation (optional):",
  "Cancelar post": "Cancel post",
  "Motivo do cancelamento...": "Reason for cancellation...",
  "Cancelado pelo usuário": "Cancelled by user",
  "Erro ao cancelar post": "Error cancelling post",
  "Post não possui data e horário agendados.":
    "Post does not have a scheduled date and time.",
  "Não é possível retornar ao calendário: a data agendada já passou.":
    "Cannot return to the calendar: the scheduled date has passed.",
  "Retornar ao calendário": "Return to calendar",
  Retornar: "Return",
  "Erro ao retornar post ao calendário": "Error returning post to calendar",
  '🔄 Deseja criar uma cópia deste post para o ${platformName}?\n\nO conteúdo e mídia serão copiados para um novo post do ${platformName} no seu "Banco de Aprovados".':
    '🔄 Do you want to create a copy of this post for ${platformName}?\n\nThe content and media will be copied to a new ${platformName} post in your "Approved Bank".',
  '🔄 Deseja criar uma cópia deste post para republicação?\n\nUma cópia idêntica será gerada no seu "Banco de Aprovados", pronta para ser agendada novamente.':
    '🔄 Do you want to create a copy of this post for republication?\n\nAn identical copy will be generated in your "Approved Bank", ready to be scheduled again.',
  "Agendar no ${platformName}": "Schedule on ${platformName}",
  "Republicar Post": "Republish Post",
  "Copiar para ${platformName}": "Copy to ${platformName}",
  "Duplicar e Repostar": "Duplicate and Repost",
  "✅ Post copiado para ${platformName} com sucesso! Verifique seu Banco de Aprovados.":
    "✅ Post copied to ${platformName} successfully! Check your Approved Bank.",
  "✅ Post duplicado com sucesso! Verifique seu Banco de Aprovados.":
    "✅ Post duplicated successfully! Check your Approved Bank.",
  "Erro ao duplicar post": "Error duplicating post",
  Dom: "Sun",
  Seg: "Mon",
  Ter: "Tue",
  Qua: "Wed",
  Qui: "Thu",
  Sex: "Fri",
  Sáb: "Sat",
  "Calendário de Campanhas": "Campaign Calendar",
  "Visualize seu cronograma de publicações, ajuste horários e maximize o engajamento com nossa IA de otimização.":
    "Visualize your publishing schedule, adjust times and maximize engagement with our optimization AI.",
  Lista: "List",
  Mensal: "Monthly",
  "Otimizar com IA": "Optimize with AI",
  Otimizar: "Optimize",
  "Pesquisar posts por tema ou conteúdo...":
    "Search posts by theme or content...",
  Cronograma: "Schedule",
  Sugestões: "Suggestions",
  "Banco de Aprovados": "Approved Bank",
  "Filtrar:": "Filter:",
  Tudo: "All",
  Próximos: "Upcoming",
  Falhas: "Failures",
  Banco: "Bank",
  "Exibindo resultados para:": "Showing results for:",
  "Limpar busca": "Clear search",
  "Cronograma vazio": "Empty schedule",
  "Combine seu planejamento estratégico com nossa IA para criar cronogramas automáticos ou aprove posts pendentes.":
    "Combine your strategic planning with our AI to create automatic schedules or approve pending posts.",
  "Ver Posts Aprovados": "View Approved Posts",
  "Não encontramos posts com o termo": "We couldn't find posts with the term",
  ". Tente pesquisar por outro termo ou limpe a busca.":
    ". Try searching for another term or clear the search.",
  "Limpar Pesquisa": "Clear Search",
  post: "post",
  s: "s",
  "início às": "starts at",
  Falha: "Failure",
  Agendado: "Scheduled",
  "Editar Post": "Edit Post",
  "Post Agendado": "Scheduled Post",
  "Alterar Data/Hora": "Change Date/Time",
  "Remover Agendamento": "Remove Schedule",
  "Agendar em outra rede": "Schedule on another network",
  "Erro ao publicar": "Error publishing",
  "Pronto para publicação": "Ready for publication",
  Reagendar: "Reschedule",
  "Publicar Agora": "Publish Now",
  "Ignorar erro": "Ignore error",
  "Sem sugestões pendentes": "No pending suggestions",
  "Excelente! No momento você não tem sugestões aguardando revisão. Que tal gerar novas com IA?":
    "Excellent! At the moment you have no suggestions awaiting review. How about generating new ones with AI?",
  "Ver Todos": "See All",
  "Não encontramos sugestões pendentes com o termo":
    "We couldn't find pending suggestions with the term",
  "Aguardando aprovação": "Awaiting approval",
  "Post Sugerido": "Suggested Post",
  "Repostar (Duplicar)": "Repost (Duplicate)",
  "Post removido da fila": "Post removed from queue",
  "Reativar Post": "Reactivate Post",
  "Gerenciar na Fila": "Manage in Queue",
  "Clique em um dia para gerenciar posts": "Click on a day to manage posts",
  Hoje: "Today",
  "mais...": "more...",
  "Posts do dia ${formatDate(dayModal.date)}":
    "Posts of day ${formatDate(dayModal.date)}",
  "Nenhum post nesta data.": "No post on this date.",
  "Ver preview": "See preview",
  Repostar: "Repost",
  "Nenhum post aprovado pronto para agendamento.":
    "No approved post ready for scheduling.",
  Social: "Social",
  Agendar: "Schedule",

  // Auth / Login / Register
  Login: "Login",
  Cadastro: "Sign up",
  "Recuperar Senha": "Recover Password",
  "Redefinir Senha": "Reset Password",
  "Confirmar Email": "Confirm Email",
  "Social Media Manager": "Social Media Manager",
  "Virtus Logo": "Virtus Logo",
  "Bem-vindo de volta!": "Welcome back!",
  "Entre na sua conta para continuar": "Sign in to your account to continue",
  "E-mail é obrigatório": "Email is required",
  "E-mail inválido": "Invalid email",
  "Senha é obrigatória": "Password is required",
  "Erro ao fazer login": "Login failed",
  "Não foi possível conectar ao servidor da API. Confirme se o backend está rodando e se a URL em VITE_APP_API_URL aponta para o host e porta corretos.":
    "Could not connect to the API server. Confirm the backend is running and VITE_APP_API_URL points to the correct host and port.",
  "E-mail ou senha incorretos": "Incorrect email or password",
  "Conta desativada. Entre em contato com o suporte":
    "Account deactivated. Contact support",
  "Tempo limite excedido. Tente novamente": "Timeout exceeded. Try again",
  "Erro interno do servidor. Tente novamente mais tarde":
    "Internal server error. Try again later",
  "Dica: no navegador, teste http://127.0.0.1:8000/api/health (ajuste host/porta se o seu backend for outro).":
    "Tip: in the browser, try http://127.0.0.1:8000/api/health (adjust host/port if your backend differs).",
  "Verifique suas credenciais e tente novamente.":
    "Check your credentials and try again.",
  Email: "Email",
  Senha: "Password",
  "voce@exemplo.com": "you@example.com",
  "Sua senha": "Your password",
  "Ocultar senha": "Hide password",
  "Mostrar senha": "Show password",
  "Lembrar de mim": "Remember me",
  "Esqueceu a senha?": "Forgot password?",
  "Autenticando...": "Authenticating...",
  "Entrando...": "Signing in...",
  Entrar: "Sign in",
  "Não tem uma conta?": "Don't have an account?",
  "Cadastre-se grátis": "Sign up for free",
  "Por favor, preencha todos os campos obrigatórios":
    "Please fill in all required fields",
  "Por favor, insira um e-mail válido": "Please enter a valid email",
  "Nome deve ter pelo menos 2 caracteres": "Name must be at least 2 characters",
  "Por favor, insira um telefone válido com DDD":
    "Please enter a valid phone number with area code",
  "Senha deve ter pelo menos 8 caracteres":
    "Password must be at least 8 characters",
  "Senha deve conter letras e números":
    "Password must contain letters and numbers",
  "As senhas não conferem": "Passwords do not match",
  "Erro no cadastro": "Registration error",
  "Este e-mail já está cadastrado. Tente fazer login ou use outro e-mail.":
    "This email is already registered. Try signing in or use another email.",
  "Por favor, insira um e-mail válido.": "Please enter a valid email.",
  "Por favor, insira um telefone válido com DDD.":
    "Please enter a valid phone number with area code.",
  "Por favor, preencha todos os campos obrigatórios.":
    "Please fill in all required fields.",
  "Nome deve ter pelo menos 2 caracteres.":
    "Name must be at least 2 characters.",
  "Cadastro solicitado!": "Registration submitted!",
  "Seu cadastro na": "Your registration at",
  "Virtus Social Manager": "Virtus Social Manager",
  "foi realizado com sucesso e agora está aguardando a aprovação de um administrador.":
    "was successful and is now awaiting administrator approval.",
  "🔒 Próximos passos:": "🔒 Next steps:",
  "O administrador analisará sua solicitação":
    "The administrator will review your request",
  "Você receberá um e-mail quando for aprovado":
    "You will receive an email when approved",
  "Após a aprovação, você poderá acessar o sistema":
    "After approval, you will be able to access the system",
  "Voltar ao Login": "Back to Login",
  "Criar conta": "Create account",
  "Preencha os dados para começar": "Fill in the details to get started",
  "Ir para página de login": "Go to login page",
  "seu@email.com": "your@email.com",
  "Nome completo": "Full name",
  "Mínimo 8 caracteres, letras e números":
    "Minimum 8 characters, letters and numbers",
  "Confirmar senha": "Confirm password",
  "Ocultar confirmação de senha": "Hide password confirmation",
  "Mostrar confirmação de senha": "Show password confirmation",
  "Repita a senha": "Repeat password",
  "Empresa (opcional)": "Company (optional)",
  "Nome da empresa": "Company name",
  "(00) 00000-0000": "(00) 00000-0000",
  "Criando conta...": "Creating account...",
  "Já tem uma conta?": "Already have an account?",
  "Faça login": "Sign in",
  "Por favor, insira seu e-mail": "Please enter your email",
  "Erro ao solicitar reset de senha": "Error requesting password reset",
  "Erro ao solicitar reset de senha. Tente novamente.":
    "Error requesting password reset. Try again.",
  "Solicitação enviada!": "Request sent!",
  "Verificamos sua solicitação": "We verified your request",
  "Se o e-mail": "If the email",
  "estiver cadastrado, você receberá um": "is registered, you will receive a",
  token: "token",
  "por e-mail. Copie o token e acesse a tela de redefinição para colar e criar sua nova senha.":
    "by email. Copy the token and go to the reset screen to paste it and create your new password.",
  "Não recebeu o e-mail?": "Didn't receive the email?",
  "• Verifique sua pasta de spam": "• Check your spam folder",
  "• Aguarde alguns minutos": "• Wait a few minutes",
  "• Confirme se o e-mail está correto": "• Confirm the email is correct",
  "• Tente novamente": "• Try again",
  "Ir para redefinir senha": "Go to reset password",
  "Tentar com outro e-mail": "Try with another email",
  "Informe seu e-mail para receber o link de redefinição":
    "Enter your email to receive the reset link",
  "Enviando...": "Sending...",
  "Enviar link de redefinição": "Send reset link",
  "Voltar ao login": "Back to login",
  "Informe o token de redefinição": "Enter the reset token",
  "Senha deve ter pelo menos 6 caracteres":
    "Password must be at least 6 characters",
  "Senhas não coincidem": "Passwords do not match",
  "Token de redefinição é obrigatório": "Reset token is required",
  "Erro ao redefinir senha": "Error resetting password",
  "Erro ao redefinir senha. Tente novamente.":
    "Error resetting password. Try again.",
  "Token inválido ou expirado. Solicite um novo e-mail de redefinição.":
    "Invalid or expired token. Request a new reset email.",
  "Token expirado. Solicite um novo e-mail de redefinição.":
    "Token expired. Request a new reset email.",
  "Senha Redefinida com Sucesso!": "Password Reset Successfully!",
  "Sua senha foi redefinida com sucesso. Agora você pode fazer login com sua nova senha.":
    "Your password was reset successfully. You can now sign in with your new password.",
  "Senha redefinida com sucesso! Faça login com sua nova senha.":
    "Password reset successfully! Sign in with your new password.",
  "Fazer Login Agora": "Sign In Now",
  "Cole o token que você recebeu por e-mail no campo abaixo":
    "Paste the token you received by email in the field below",
  "Token de redefinição": "Reset token",
  "Cole aqui o token do e-mail": "Paste the token from the email here",
  Continuar: "Continue",
  "← Voltar ao login": "← Back to login",
  "Digite e confirme sua nova senha": "Enter and confirm your new password",
  "Digite sua nova senha (mín. 6 caracteres)":
    "Enter your new password (min. 6 characters)",
  "Confirme sua nova senha": "Confirm your new password",
  "Usar outro token": "Use another token",
  "Redefinindo senha...": "Resetting password...",
  "← Voltar ao token": "← Back to token",
  "Token de confirmação não encontrado na URL":
    "Confirmation token not found in URL",
  "Por favor, digite sua senha": "Please enter your password",
  "A senha deve ter pelo menos 6 caracteres":
    "Password must be at least 6 characters",
  "Token de confirmação inválido": "Invalid confirmation token",
  "Erro ao confirmar email": "Error confirming email",
  "Erro ao confirmar email. Tente novamente.":
    "Error confirming email. Try again.",
  "Token de confirmação inválido ou expirado.":
    "Invalid or expired confirmation token.",
  "Token de confirmação expirado. Solicite um novo cadastro.":
    "Confirmation token expired. Request a new registration.",
  "A senha deve ter pelo menos 6 caracteres.":
    "Password must be at least 6 characters.",
  "Conta confirmada!": "Account confirmed!",
  "Seu email foi confirmado e sua senha foi definida com sucesso!":
    "Your email was confirmed and your password was set successfully!",
  "✅ Tudo pronto!": "✅ All set!",
  "Você será redirecionado para a página de login em instantes...":
    "You will be redirected to the login page shortly...",
  "Ir para Login": "Go to Login",
  "Defina sua senha para ativar sua conta":
    "Set your password to activate your account",
  "Voltar para cadastro": "Back to registration",
  "Digite sua nova senha": "Enter your new password",
  "Confirmar Senha": "Confirm Password",
  "Confirmando...": "Confirming...",
  "Confirmar Email e Definir Senha": "Confirm Email and Set Password",
  "Voltar para página de login": "Back to login page",
  "Virtus AI | Login": "Virtus AI | Login",
  "Falha no login. Verifique suas credenciais.":
    "Login failed. Check your credentials.",

  // OrganizationUsers
  "Gerencie os membros e níveis de acesso da sua organização corporativa.":
    "Manage members and access levels of your corporate organization.",
  "Novo Membro": "New Member",
  "Gestão de Equipe Global": "Global Team Management",
  "Para gerenciar os usuários e permissões da Organização, você precisa estar no contexto da empresa mãe.":
    "To manage organization users and permissions, you must be in the parent company context.",
  "Erro ao carregar usuários": "Error loading users",
  "Email é obrigatório": "Email is required",
  "Usuário criado": "User created",
  "Erro ao criar usuário": "Error creating user",
  "Usuário atualizado": "User updated",
  "Erro ao atualizar": "Error updating",
  "Permissão alterada": "Permission changed",
  "Erro ao alterar permissão": "Error changing permission",
  "Você não pode excluir a si mesmo": "You cannot delete yourself",
  "Não é possível excluir o Super Administrador":
    "Cannot delete the Super Administrator",
  "Usuário removido": "User removed",
  "Erro ao excluir": "Error deleting",
  "Equipe Vazia": "Empty Team",
  "Sua organização ainda não possui outros colaboradores. Comece expandindo seu time.":
    "Your organization has no other collaborators yet. Start by expanding your team.",
  "Adicionar Primeiro Membro": "Add First Member",
  Desde: "Since",
  "Perfil e Acessos": "Profile & Access",
  "Revogar Acesso": "Revoke Access",
  "Sem permissão para listar empresas cliente.":
    "No permission to list client companies.",
  "Erro ao carregar empresas cliente.": "Error loading client companies.",
  "Novo Colaborador": "New Collaborator",
  Validando: "Validating",
  "Cadastro de Acesso": "Access Registration",
  "Defina as credenciais do novo membro": "Set credentials for the new member",
  "Ex: João Silva": "E.g.: John Smith",
  "Endereço de E-mail": "Email Address",
  "Senha de Acesso": "Access Password",
  "Mínimo 8 caracteres": "Minimum 8 characters",
  "Nível de Permissão": "Permission Level",
  "Editor (Acesso Limitado)": "Editor (Limited Access)",
  "Administrador (Gestão Total)": "Administrator (Full Management)",
  "Configurar Perfil": "Configure Profile",
  Sincronizando: "Syncing",
  Atualizar: "Update",
  "Nome de Exibição": "Display Name",
  "E-mail de Acesso": "Access Email",
  "Ajuste de permissões e dados básicos":
    "Permission and basic data adjustments",
  "Scope de Acesso (Unidades)": "Access Scope (Units)",
  "Selecione as unidades que este editor está autorizado a gerenciar.":
    "Select the units this editor is authorized to manage.",
  "Consultando unidades...": "Loading units...",
  "Nenhuma unidade disponível": "No units available",
  "Criar nova unidade": "Create new unit",
  "Excluir Permanentemente": "Delete Permanently",
  "Manter Membro": "Keep Member",
  "Você está prestes a remover": "You are about to remove",
  "da rede corporativa. Esta ação é":
    "from the corporate network. This action is",
  irreversível: "irreversible",
  "e todos os logs de atividade deste usuário serão arquivados.":
    "and all activity logs for this user will be archived.",
  "O usuário perderá acesso imediato aos ambientes.":
    "The user will immediately lose access to all environments.",

  // ClientCompanies
  "Gerencie as credenciais e acessos de cada unidade vinculada à sua agência.":
    "Manage credentials and access for each unit linked to your agency.",
  "Ver Sede": "View HQ",
  "Nova Unidade": "New Unit",
  "Lista de Unidades": "Units List",
  "A listagem de empresas clientes está disponível apenas no contexto da Agência/Organização.":
    "Client companies listing is only available in the Agency/Organization context.",
  "Pesquisar unidades...": "Search units...",
  Todas: "All",
  Recentes: "Recent",
  "Com CNPJ": "With Tax ID",
  Filtrar: "Filter",
  "Crie sua organização primeiro": "Create your organization first",
  "Para cadastrar empresas clientes, você precisa criar sua organização.":
    "To register client companies, you need to create your organization.",
  "Ir para Organização": "Go to Organization",
  "Nenhuma empresa cliente": "No client companies",
  "Nenhum resultado para sua busca. Tente outro termo.":
    "No results for your search. Try another term.",
  "Cadastre empresas clientes para gerenciar os trabalhos da sua organização.":
    "Register client companies to manage your organization's work.",
  "Cadastrar primeira empresa": "Register first company",
  "Erro ao carregar empresas": "Error loading companies",

  // ClientCompanyDetail
  "Unidade de Negócio": "Business Unit",
  "Configurações e chaves de API específicas para esta unidade de negócio.":
    "Settings and API keys specific to this business unit.",
  "Empresa cliente não encontrada.": "Client company not found.",
  "Voltar à lista": "Back to list",
  "Contexto Inválido": "Invalid Context",
  "Você está tentando configurar uma Unidade de Negócio, mas seu contexto atual é de uma empresa específica. Por favor, volte ao contexto da Organização para gerenciar unidades.":
    "You are trying to configure a Business Unit, but your current context is a specific company. Please switch back to the Organization context to manage units.",
  UNIDADE: "UNIT",
  "Empresa atualizada": "Company updated",
  "Editar Unidade": "Edit Unit",
  "Ajuste de Credenciais e Identidade": "Credentials and Identity Adjustments",
  "Nome da Empresa": "Company Name",
  "Número de telefone principal desta unidade (WhatsApp ou Fixo).":
    "Main phone number for this unit (WhatsApp or landline).",
  "Link do site principal desta unidade.": "Link to this unit's main website.",
  "URL da política de privacidade. Obrigatório para formulários de leads.":
    "Privacy policy URL. Required for lead forms.",
  "usado para identificar o cliente ideal":
    "used to identify the ideal customer",
  "Faturamento e Integrações Independentes":
    "Independent Billing and Integrations",
  "Habilite para utilizar os dados de cadastro e faturamento do cliente. Do contrário, usará as configurações da Organização.":
    "Enable to use the client's registration and billing data. Otherwise, Organization settings will be used.",
  "Endereço / Logradouro": "Address / Street",
  Complemento: "Complement",
  "Estado (UF)": "State",
  "Configurações de Fluxo e Orquestração": "Flow and Orchestration Settings",

  // HelpAndSupport
  "Ajuda e Suporte": "Help & Support",
  "Central de ajuda, FAQs e suporte direto.":
    "Help center, FAQs and direct support.",
  "Como podemos te ajudar hoje?": "How can we help you today?",
  "Nossa equipe está pronta para garantir que sua experiência seja impecável. Acesse nosso FAQ rápido ou fale diretamente com a gente.":
    "Our team is ready to ensure your experience is flawless. Access our quick FAQ or talk to us directly.",
  "Suporte VIP via WhatsApp": "VIP Support via WhatsApp",
  "Como cliente parceiro, você tem linha direta com nossos especialistas. Tem algum bug, dúvida ou solicitação urgente? Chama a gente no Whats!":
    "As a partner client, you have a direct line to our specialists. Have a bug, question or urgent request? Message us on WhatsApp!",
  "Falar no WhatsApp": "Chat on WhatsApp",
  "Suporte por Email": "Email Support",
  "Para dúvidas menos urgentes ou envio de anexos maiores (planilhas, exportações de dados), envie-nos um email.":
    "For less urgent questions or sending larger attachments (spreadsheets, data exports), send us an email.",
  "Respostas rápidas": "Quick responses",
  "Em horário comercial": "During business hours",
  "Acesso a especialistas": "Access to specialists",
  "Perguntas Frequentes (FAQ)": "Frequently Asked Questions (FAQ)",
  "Primeiros Passos": "Getting Started",
  "Como conectar minhas redes sociais?": "How do I connect my social networks?",
  "Acesse a aba 'Organização', vá em 'Minha Organização' e siga as instruções para conectar suas contas do Meta (Facebook/Instagram), Google e LinkedIn.":
    "Go to the 'Organization' tab, open 'My Organization' and follow the instructions to connect your Meta (Facebook/Instagram), Google and LinkedIn accounts.",
  "Como funciona a geração de posts via IA?":
    "How does AI post generation work?",
  "Na área 'Cronograma IA', você pode definir temas, palavras-chave e tom de voz. Nossa inteligência artificial irá sugerir uma grade completa de conteúdos que podem ser aprovados, editados ou rejeitados antes da publicação.":
    "In the 'AI Schedule' area, you can define themes, keywords and tone of voice. Our AI will suggest a full content grid that can be approved, edited or rejected before publishing.",
  "Campanhas e Agendamentos": "Campaigns and Scheduling",
  "Posso editar um post depois de agendado?":
    "Can I edit a post after scheduling?",
  "Sim. Enquanto o post estiver com status 'Agendado' (ou pendente) no Menu de Campanhas, você pode editá-lo, alterar a imagem, o texto ou o horário da postagem.":
    "Yes. While the post has 'Scheduled' (or pending) status in the Campaigns menu, you can edit it, change the image, text or posting time.",
  "Por que meu post não foi publicado?": "Why wasn't my post published?",
  "Posts podem falhar por falta de permissões nas redes sociais anexadas (token expirado) ou instabilidade temporária das plataformas. Verifique a aba 'Erros' no Dashboard para detalhes do log.":
    "Posts may fail due to missing permissions on connected social networks (expired token) or temporary platform instability. Check the 'Errors' tab on the Dashboard for log details.",
  "Integrações (Leadflow)": "Integrations (Leadflow)",
  "Como os leads captados chegam no meu CRM?":
    "How do captured leads reach my CRM?",
  "Utilize o módulo 'Leadflow'. Ao mapear os campos da sua campanha para os campos do sistema destino (ex: CV CRM), as integrações operam em segundo plano roteando os leads recebidos quase em tempo real.":
    "Use the 'Leadflow' module. By mapping your campaign fields to the destination system fields (e.g. CV CRM), integrations run in the background routing received leads almost in real time.",

  // EmpathyMapping
  "Mapeamento de Empatia & ICP": "Empathy Mapping & ICP",
  "Identifique seu Cliente Ideal (ICP), crie mapas de influência e descubra oportunidades baseadas em IA e análise de dados sociais.":
    "Identify your Ideal Customer (ICP), create influence maps and discover opportunities based on AI and social data analysis.",
  "IA Discovery": "AI Discovery",
  Exportar: "Export",
  Mapeamento: "Mapping",
  "Resultados do Mapeamento": "Mapping Results",
  "Visualize os mapas de empatia e influência do seu ICP":
    "View empathy and influence maps for your ICP",
  "Analisando...": "Analyzing...",
  "Analisar Redes Sociais": "Analyze Social Networks",
  "Complete a descoberta de ICP para ver os resultados":
    "Complete ICP discovery to see results",
  "Estratégia e Recomendações": "Strategy and Recommendations",
  "Análise IA": "AI Analysis",
  "Insights baseados no perfil do seu cliente ideal":
    "Insights based on your ideal customer profile",
  "Refazer Perfil": "Redo Profile",
  "Gerar Post com ICP": "Generate Post with ICP",
  "Copy direcionada ao perfil": "Copy targeted to profile",
  "Tema do post (ex: lançamento de produto, dica sobre gestão...)":
    "Post topic (e.g.: product launch, management tip...)",
  "Gerar Post": "Generate Post",
  Genérico: "Generic",
  "Copiado!": "Copied!",
  Copiar: "Copy",
  Copy: "Copy",
  Hashtags: "Hashtags",
  "Complete a descoberta de ICP para ver os insights":
    "Complete ICP discovery to see insights",
  "Busca Inteligente de Leads": "Smart Lead Search",
  "Localizando perfis compatíveis com o ICP":
    "Finding profiles matching the ICP",
  "Complete a descoberta de ICP para buscar leads":
    "Complete ICP discovery to search leads",
  "Erro ao exportar mapas": "Error exporting maps",
  "Erro ao gerar post": "Error generating post",

  // AdsCampaigns (main UI)
  "Campanhas Pagas Meta": "Meta Paid Campaigns",
  "Crie campanhas pagas no Facebook e Instagram usando as credenciais configuradas.":
    "Create paid campaigns on Facebook and Instagram using configured credentials.",
  "Objetivo da campanha": "Campaign objective",
  "Reconhecimento de marca": "Brand awareness",
  "Visitas ao site": "Website visits",
  "Geração de leads": "Lead generation",
  "Conversões no site": "Website conversions",
  "Consideração / Tráfego": "Consideration / Traffic",
  Vendas: "Sales",
  "Nome da campanha": "Campaign name",
  "Orçamento diário (R$)": "Daily budget (R$)",
  "URL da imagem (obrigatório)": "Image URL (required)",
  "A Meta exige uma imagem para veicular o anúncio. Use uma URL pública (JPG/PNG).":
    "Meta requires an image to run the ad. Use a public URL (JPG/PNG).",
  "Destino da campanha": "Campaign destination",
  "Website (URL de destino)": "Website (destination URL)",
  "WhatsApp (Clique para conversar)": "WhatsApp (Click to chat)",
  "Número do WhatsApp (com DDI/DDD)":
    "WhatsApp number (with country/area code)",
  "Idade mínima": "Minimum age",
  "Idade máxima": "Maximum age",
  Gênero: "Gender",
  Masculino: "Male",
  Feminino: "Female",
  "País (código ISO)": "Country (ISO code)",
  "Link do WhatsApp (gerado automaticamente)": "WhatsApp link (auto-generated)",
  "Mensagem padrão do WhatsApp (opcional)":
    "Default WhatsApp message (optional)",
  "URL de destino": "Destination URL",
  "Data de início": "Start date",
  "Data de término": "End date",
  "Status inicial da campanha": "Initial campaign status",
  "Criar campanha": "Create campaign",
  pausada: "paused",
  "(recomendado - revisar antes de ativar)":
    "(recommended - review before activating)",
  ativa: "active",
  "(a campanha começará a veicular e consumir orçamento imediatamente)":
    "(the campaign will start running and consuming budget immediately)",
  "Mensagem do anúncio": "Ad message",
  "Segmentação sugerida (ICP)": "Suggested targeting (ICP)",
  "(baseado no mapa de empatia)": "(based on empathy map)",
  "Aplicar ao formulário": "Apply to form",
  "Geolocalização e Interesses Configurados":
    "Configured Geolocation and Interests",
  "Cidades selecionadas:": "Selected cities:",
  "Estados selecionados:": "Selected states:",
  "Interesses selecionados:": "Selected interests:",
  "Formulário de captação de leads": "Lead capture form",
  "Escolha quais campos o anúncio/campanha deve solicitar do lead. Essas informações podem ser usadas depois para qualificar melhor os leads.":
    "Choose which fields the ad/campaign should request from the lead. This information can later be used to better qualify leads.",
  "E-mail": "Email",
  CPF: "Tax ID (CPF)",
  Cargo: "Job title",
  "Orçamento estimado": "Estimated budget",
  "Setor/Indústria": "Sector/Industry",
  Website: "Website",
  "Campos personalizados": "Custom fields",
  "Adicione quantos campos quiser ao formulário de captação":
    "Add as many fields as you want to the capture form",
  "Adicionar Campo": "Add Field",
  'Nenhum campo personalizado adicionado. Clique em "Adicionar Campo" para começar.':
    'No custom fields added. Click "Add Field" to get started.',
  "Rótulo do campo": "Field label",
  "Tipo de campo": "Field type",
  Texto: "Text",
  "Texto longo": "Long text",
  "Seleção (opções)": "Selection (options)",
  "Opções de seleção": "Selection options",
  "Campo obrigatório": "Required field",
  "Validações automáticas:": "Automatic validations:",
  "Sugerir segmentação ICP": "Suggest ICP targeting",
  "Criar campanha paga": "Create paid campaign",
  "Informe um orçamento diário válido.": "Enter a valid daily budget.",
  "Campanha paga criada com sucesso no Meta!":
    "Paid campaign created successfully on Meta!",
  "Campanha paga criada com sucesso no LinkedIn!":
    "Paid campaign created successfully on LinkedIn!",
  "Campanha paga criada com sucesso no Google!":
    "Paid campaign created successfully on Google!",
  "Erro ao criar campanha": "Error creating campaign",
  "Token de autenticação não encontrado. Faça login novamente.":
    "Authentication token not found. Please sign in again.",
  "Erro ao sugerir segmentação": "Error suggesting targeting",
  "Segmentação sugerida. Revise, edite e aplique se desejar.":
    "Suggested targeting. Review, edit and apply if desired.",
  "Segmentação aplicada aos campos do formulário!":
    "Targeting applied to form fields!",

  // ContentAnalytics
  "Analytics de Biblioteca": "Library Analytics",
  "Explore métricas e insights inteligentes da sua base de conhecimento para potencializar sua criação.":
    "Explore metrics and smart insights from your knowledge base to boost your creation.",
  "Adicione conteúdos à sua biblioteca para ver métricas e análises aqui.":
    "Add content to your library to see metrics and analysis here.",
  "Dimensões da Base": "Base Dimensions",
  "Métricas de Volume": "Volume Metrics",
  "Monitoramento Ativo": "Active Monitoring",
  "Total de Materiais": "Total Materials",
  "Consolidados na biblioteca de conhecimento":
    "Consolidated in the knowledge library",
  "Novos Materiais": "New Materials",
  "Adicionados nos últimos 7 dias": "Added in the last 7 days",
  "Arquitetura de Ativos": "Asset Architecture",
  "Distribuição por Categoria": "Distribution by Category",
  "Links Úteis": "Useful Links",
  "IA Prompts": "AI Prompts",
  "Filtro Estratégico": "Strategic Filter",
  "Orientação de Mercado": "Market Orientation",
  "Foco em Vendas Corporativas": "Focus on Corporate Sales",
  "Foco em Consumidor Final": "Focus on End Consumer",
  "Foco em Atuação Geográfica": "Focus on Geographic Presence",
  "Insights da Base de Conhecimento": "Knowledge Base Insights",
  "Nossa IA analisa sua biblioteca para identificar padrões de sucesso e lacunas informativas na sua estratégia de conteúdo.":
    "Our AI analyzes your library to identify success patterns and information gaps in your content strategy.",
  "Os insights analíticos serão gerados automaticamente conforme novos materiais são incorporados à sua biblioteca.":
    "Analytical insights will be generated automatically as new materials are added to your library.",
  "Identificamos que": "We identified that",
  materiais: "materials",
  "possuem foco B2B, sugerindo uma base sólida para expansão Enterprise.":
    "have a B2B focus, suggesting a solid base for Enterprise expansion.",

  // PostsAnalytics
  "Performance de Canais": "Channel Performance",
  "Métricas de distribuição, eficácia de IA e saúde da grade de conteúdo em tempo real.":
    "Distribution metrics, AI effectiveness and content grid health in real time.",
  "Fluxo de Conteúdo": "Content Flow",
  "Métricas de Pipeline": "Pipeline Metrics",
  "Eficiência de Grade": "Grid Efficiency",
  "Calendário de Distribuição": "Distribution Calendar",
  "Total Agendados": "Total Scheduled",
  "Posts na fila futura": "Posts in future queue",
  "Canais Ativos": "Active Channels",
  "Redes sociais ativas": "Active social networks",
  "Ritmo Médio": "Average Pace",
  "Posts por semana": "Posts per week",
  "Período Ativo": "Active Period",
  Até: "Until",
  "Planejamento aberto": "Open planning",
  "Eficácia da Inteligência": "Intelligence Effectiveness",
  "Produção IA": "AI Production",
  "Materiais criados autonomamente pelos agentes de IA na rede.":
    "Materials created autonomously by AI agents on the network.",
  "Penetração IA": "AI Penetration",
  "Taxa de Aprovação": "Approval Rate",
  "Decisão Humana vs Sugestão IA": "Human Decision vs AI Suggestion",
  validados: "validated",
  "Taxa de Publicação": "Publication Rate",
  "Conversão em Presença Digital": "Conversion to Digital Presence",
  "materiais em live": "materials live",
  Eficácia: "Effectiveness",
  "Melhores Resultados": "Best Results",
  "Ranking de Posts": "Post Ranking",
  "7 dias": "7 days",
  "30 dias": "30 days",
  "90 dias": "90 days",
  "1 ano": "1 year",
  "Todo período": "All time",

  // CampaignLeadDetailsModal
  "Dossiê Detalhado do Lead": "Detailed Lead Dossier",
  "Análise completa de origem, qualificação e integridade dos dados capturados.":
    "Complete analysis of origin, qualification and integrity of captured data.",
  "Sincronizar CAPI": "Sync CAPI",
  "Sincronizar CRM Hub": "Sync CRM Hub",
  "Sem nome": "No name",
  "Lead Score": "Lead Score",
  "Origem de Captura": "Capture Origin",
  "Campanha / Anúncio": "Campaign / Ad",
  "ID do Post:": "Post ID:",
  "Conversão direta / CRM Orgânico": "Direct conversion / Organic CRM",
  "Data Captura": "Capture Date",
  "Dados Profissionais": "Professional Data",
  "Cargo / Função": "Role / Function",
  "Status de Qualificação": "Qualification Status",
  Qualificado: "Qualified",
  "Em Avaliação": "Under Review",
  "Integridade e Sincronização": "Integrity and Synchronization",
  "Suspeita de Fraude": "Fraud Suspect",
  Confirmado: "Confirmed",
  Limpo: "Clean",
  Duplicação: "Duplication",
  Identificado: "Identified",
  Único: "Unique",
  "ID do Lead": "Lead ID",
  "Sync CRM Hub": "CRM Hub Sync",

  // ScheduleSuggestions
  "Cronograma de Postagens com IA": "AI Posting Schedule",
  "Gere cronogramas de alta performance otimizados para suas redes sociais. Nossa IA analisa seu nicho e sugere os melhores horários e temas.":
    "Generate high-performance schedules optimized for your social networks. Our AI analyzes your niche and suggests the best times and themes.",
  "Base do Cronograma": "Schedule Foundation",
  "Defina o alcance da sua estratégia": "Define the scope of your strategy",
  "Redes Sociais Ativas": "Active Social Networks",
  "Início do Ciclo": "Cycle Start",
  "O ponto de partida do seu calendário. Todas as postagens serão agendadas a partir desta data.":
    "The starting point of your calendar. All posts will be scheduled from this date.",
  "Nicho de Atuação": "Business Niche",
  "B2B - Business to Business": "B2B - Business to Business",
  "B2C - Consumidor Final": "B2C - End Consumer",
  "Negócio Local / Regional": "Local / Regional Business",
  "O ecossistema onde você atua. Permite que a IA use gatilhos específicos do seu setor.":
    "The ecosystem you operate in. Allows AI to use sector-specific triggers.",
  "Duração Total": "Total Duration",
  "1 Semana (Sprint)": "1 Week (Sprint)",
  "2 Semanas (Planejamento)": "2 Weeks (Planning)",
  "4 Semanas (Ciclo Mensal)": "4 Weeks (Monthly Cycle)",
  "8 Semanas (Estratégico)": "8 Weeks (Strategic)",
  "O horizonte temporal (alcance) do seu planejamento estratégico atual.":
    "The time horizon (scope) of your current strategic planning.",
  "Frequência de Postagem": "Posting Frequency",
  "1 Post por Semana": "1 Post per Week",
  "3 Posts por Semana": "3 Posts per Week",
  "5 Posts por Semana": "5 Posts per Week",
  "Diário (7 Posts/Sem)": "Daily (7 Posts/Week)",
  "Intenso (2/dia)": "Intensive (2/day)",
  "A intensidade da sua presença digital. Volume de conteúdos produzidos por rede social.":
    "The intensity of your digital presence. Volume of content produced per social network.",
  "Raio-X Estratégico": "Strategic X-Ray",
  Canais: "Channels",
  Janela: "Window",
  "Ciclo de Atividade": "Activity Cycle",
  "Inicia em:": "Starts on:",
  "Finaliza em:": "Ends on:",
  "Fonte de Inteligência": "Intelligence Source",
  "Como a IA deve construir seu conteúdo?": "How should AI build your content?",
  "IA Guiada por Ativos": "Asset-Guided AI",
  "A IA utiliza seus materiais salvos (URLs, Textos, Arquivos) como base prioritária para o planejamento.":
    "AI uses your saved materials (URLs, Text, Files) as the priority base for planning.",
  Selecionado: "Selected",
  "Escolher este modo": "Choose this mode",
  "IA Criativa Pura": "Pure Creative AI",
  "A IA explora tendências globais do seu nicho e gera conteúdos do zero para máxima tração orgânica.":
    "AI explores global trends in your niche and generates content from scratch for maximum organic traction.",
  "Selecione seus Ativos": "Select your Assets",
  "Escolha os materiais que servirão de base para a IA":
    "Choose materials that will serve as the AI base",
  "Buscar Conteúdo": "Search Content",
  "Nome ou prévia...": "Name or preview...",
  "Filtre seus ativos por nome ou palavras-chave na descrição.":
    "Filter your assets by name or keywords in the description.",
  Nicho: "Niche",
  "Todos os nichos": "All niches",
  B2B: "B2B",
  B2C: "B2C",
  Local: "Local",
  "Filtre os ativos com base no segmento de negócio a que pertencem.":
    "Filter assets based on the business segment they belong to.",
  Formato: "Format",
  "Todos os formatos": "All formats",
  "Link Web": "Web Link",
  "Texto/Nota": "Text/Note",
  Documento: "Document",
  "Selecione entre links de sites, notas de texto ou arquivos para basear a IA.":
    "Select between website links, text notes, or files to base the AI on.",
  "Sincronizando biblioteca...": "Syncing library...",
  "Biblioteca Vazia": "Empty Library",
  "Adicione materiais na sua biblioteca de conteúdos para usar este modo.":
    "Add materials to your content library to use this mode.",
  Recarregar: "Reload",
  Balanceada: "Balanced",
  "Distribui os conteúdos de forma equilibrada": "Distributes content evenly",
  Sequencial: "Sequential",
  "Usa os conteúdos em ordem sequencial": "Uses content in sequential order",
  Temática: "Thematic",
  "Agrupa por temas similares": "Groups by similar themes",
  "Estratégia de Distribuição": "Distribution Strategy",
  "Define como os materiais selecionados serão priorizados e integrados no cronograma.":
    "Defines how selected materials will be prioritized and integrated into the schedule.",
  "Personalização do Motor IA": "AI Engine Customization",
  Opcional: "Optional",
  "Refinamento de objetivos e persona (Recomendado)":
    "Objective and persona refinement (Recommended)",
  "Persona / ICP": "Persona / ICP",
  "O perfil detalhado do seu cliente ideal. Ajuda a IA a calibrar o vocabulário e o apelo emocional.":
    "The detailed profile of your ideal customer. Helps AI calibrate vocabulary and emotional appeal.",
  "Ex: Empreendedores digitais que buscam automação...":
    "E.g.: Digital entrepreneurs seeking automation...",
  "Objetivos da Estratégia": "Strategy Objectives",
  "A meta principal do planejamento. Guia a abordagem estratégica e o call-to-action de cada post.":
    "The main goal of the plan. Guides the strategic approach and call-to-action of each post.",
  "Ex: Aumentar o engajamento e gerar autoridade no nicho...":
    "E.g.: Increase engagement and build authority in the niche...",
  "Tipo de Campanha": "Campaign Type",
  "Define se o conteúdo será otimizado para alcance de comunidade ou conversão em anúncios.":
    "Defines whether content will be optimized for community reach or ad conversion.",
  "Modelo de IA (Cérebro)": "AI Model (Brain)",
  "OpenAI - GPT-5.4 Nano (Padrão)": "OpenAI - GPT-5.4 Nano (Default)",
  "OpenAI - GPT-5.1 Flagship": "OpenAI - GPT-5.1 Flagship",
  "OpenAI - GPT-5.4 Mini": "OpenAI - GPT-5.4 Mini",
  "OpenAI - GPT-4o Optimized": "OpenAI - GPT-4o Optimized",
  "Anthropic - Claude 3.5 Sonnet": "Anthropic - Claude 3.5 Sonnet",
  "Anthropic - Claude 3.5 Haiku": "Anthropic - Claude 3.5 Haiku",
  "DeepSeek - DeepSeek V4 Flash": "DeepSeek - DeepSeek V4 Flash",
  "DeepSeek - DeepSeek V4 Pro": "DeepSeek - DeepSeek V4 Pro",
  "Google - Gemini 1.5 Pro": "Google - Gemini 1.5 Pro",
  "Google - Gemini 1.5 Flash": "Google - Gemini 1.5 Flash",
  "Kimi AI - Kimi Chat (Moonshot)": "Kimi AI - Kimi Chat (Moonshot)",
  "Modelos de IA disponíveis para escolha.":
    "AI models available for selection.",
  "Estilo de Criador (Benchmark de Escrita)":
    "Creator Style (Writing Benchmark)",
  "Sem Benchmark - Padrão do Nicho": "No Benchmark - Niche Default",
  Reels: "Reels",
  "Escolha um criador de referência para a IA modelar o estilo de escrita e ganchos.":
    "Choose a reference creator for AI to model writing style and hooks.",
  "Fontes de Pesquisa": "Research Sources",
  "Links de sites ou artigos que a IA deve 'estudar' para enriquecer o contexto do seu negócio.":
    "Website or article links the AI should 'study' to enrich your business context.",
  "Incluir UTM padrão": "Include default UTM",
  "Horários de Publicação": "Publication Times",
  "Os momentos de maior impacto. A Virtus alternará as plataformas nestes horários para maximizar o alcance.":
    "Peak impact moments. Virtus will alternate platforms at these times to maximize reach.",
  "Balanceamento automático": "Automatic balancing",
  "Decolagem Estratégica": "Strategic Launch",
  "Sua presença digital será orquestrada pela Virtus AI":
    "Your digital presence will be orchestrated by Virtus AI",
  "Logística de Publicação": "Publishing Logistics",
  "postagens que entrarão automaticamente como drafts para sua revisão.":
    "posts that will automatically enter as drafts for your review.",
  "Processando Estratégia": "Processing Strategy",
  "Garantir Meu Cronograma": "Secure My Schedule",
  "Selecione pelo menos um conteúdo da biblioteca para prosseguir":
    "Select at least one library content to proceed",
  "Estratégia Mapeada": "Strategy Mapped",
  "Cronograma gerado pela inteligência virtus":
    "Schedule generated by Virtus intelligence",
  "Semana {number}": "Week {number}",
  "Engajamento {prediction}": "Engagement {prediction}",
  Alto: "High",
  Médio: "Medium",
  Moderado: "Moderate",
  "Call to Action:": "Call to Action:",
  "Dica de Ouro:": "Golden Tip:",
  "Token não encontrado": "Token not found",
  'Post enviado para "Posts Pendentes" com sucesso!':
    'Post sent to "Pending Posts" successfully!',
  "Materializar este Post": "Materialize this Post",
  "Estratégias de Refinamento Aplicadas": "Applied Refinement Strategies",
  "Pipeline Estratégico Atualizado": "Strategic Pipeline Updated",
  "Seu ecossistema de conteúdo foi estruturado. As postagens já estão na lista de espera, prontas para serem transformadas em resultados reais.":
    "Your content ecosystem has been structured. Posts are already in the waiting list, ready to be turned into real results.",
  "Ver Posts Pendentes": "View Pending Posts",
  "Concluído!": "Complete!",
  "\n✅ {count} posts criados como 'Pendentes'.":
    "\n✅ {count} posts created as 'Pending'.",
  "🎉 Cronograma gerado com sucesso! {count} posts sugeridos.{postsLine}\n\n⚡ **Créditos Consumidos:** {credits} créditos de IA.":
    "🎉 Schedule generated successfully! {count} suggested posts.{postsLine}\n\n⚡ **Credits Consumed:** {credits} AI credits.",
  "Geração Concluída": "Generation Complete",
  "Falha no processador de tarefas em segundo plano.":
    "Background task processor failed.",
  "Erro de Geração": "Generation Error",
  "Configurando parâmetros...": "Configuring parameters...",
  "IA analisando seu nicho...": "AI analyzing your niche...",
  "Gerando sugestões criativas...": "Generating creative suggestions...",
  "Quase pronto! Organizando calendário...":
    "Almost ready! Organizing calendar...",
  "Selecione pelo menos um conteúdo": "Select at least one content item",
  "Gerando Estratégia": "Generating Strategy",
  "Analisando conteúdos selecionados...": "Analyzing selected content...",
  "Não foi possível enfileirar a tarefa de geração.":
    "Could not enqueue generation task.",
  "Erro desconhecido ao gerar com biblioteca":
    "Unknown error generating with library",
  "Falha na Geração": "Generation Failed",
  "Selecione pelo menos uma plataforma": "Select at least one platform",
  "A data de início não pode ser no passado":
    "Start date cannot be in the past",
  "O período deve ser entre 1 e 52 semanas":
    "Period must be between 1 and 52 weeks",
  "Posts por semana deve ser entre 1 e 20":
    "Posts per week must be between 1 and 20",
  "Criando Cronograma": "Creating Schedule",
  "Iniciando motores de inteligência...": "Starting intelligence engines...",
  "Erro desconhecido ao gerar sugestões":
    "Unknown error generating suggestions",

  // Leadflow module
  "Atualizando...": "Refreshing...",
  "Erro ao carregar Jornada.": "Error loading Journey.",
  "Falha de Conexão com Leadflow": "Leadflow Connection Failure",
  "Rastreie todas as travessias de leads — da origem ao CRM destino.":
    "Track every lead journey — from origin to destination CRM.",
  "Recarregar dados do servidor": "Reload data from server",
  "Erro ao carregar configurações.": "Error loading settings.",
  "Erro de Integração": "Integration Error",
  "Não foi possível conectar ao serviço Leadflow.":
    "Could not connect to the Leadflow service.",
  "Mapeamentos Leadflow": "Leadflow Mappings",
  "Central Técnica - Mapeamentos de Sincronização, Aliases e Lógica RD Station.":
    "Technical Center - Sync Mappings, Aliases and RD Station Logic.",
  "Módulo Leadflow · Mapeamentos": "Leadflow Module · Mappings",
  "Configurações de Sincronia": "Sync Settings",
  "O módulo de": "The",
  "permite definir as regras de identificação e destino dos leads para seu CRM.":
    "module lets you define lead identification and destination rules for your CRM.",
  "Esta funcionalidade faz parte do conjunto Leadflow Advanced. Entre em contato para ativar e configurar seus fluxos de sincronização automática.":
    "This feature is part of the Leadflow Advanced suite. Contact us to activate and configure your automatic sync flows.",
  "Solicitar Ativação": "Request Activation",
  "Falar com Consultor": "Talk to a Consultant",
  Regras: "Rules",
  "Regras de Sincronização e Mapeamento": "Sync and Mapping Rules",
  Dicionário: "Dictionary",
  "Alias e Normalização de Campos": "Alias and Field Normalization",
  "Configurações de Destino CVCRM": "CVCRM Destination Settings",
  "Lógica de Tags e Sincronia RD Station": "RD Station Tags and Sync Logic",
  "Excluir Mapeamento": "Delete Mapping",
  "O mapeamento será removido permanentemente de todos os canais de integração ativos.":
    "The mapping will be permanently removed from all active integration channels.",
  "Investimento (R$)": "Spend ($)",
  Cliques: "Clicks",
  "Explore os relatórios de desempenho consolidado de Meta Ads integrados ao Leadflow.":
    "Explore consolidated Meta Ads performance reports integrated with Leadflow.",
  "Atenção Técnica": "Technical Attention",
  "Módulo Leadflow · Meta Insights": "Leadflow Module · Meta Insights",
  "Integração não ativada": "Integration not activated",
  "O módulo": "The",
  "exibe o desempenho consolidado das suas campanhas de anúncios.":
    "module displays the consolidated performance of your ad campaigns.",
  "O horizonte de dados está vazio": "The data horizon is empty",
  "Reiniciar Análise": "Restart Analysis",
  "Status dos Insights": "Insights Status",
  Registros: "Records",
  "Janela Ativa": "Active Window",
  "Budget Alocado": "Allocated Budget",
  pessoas: "people",
  "Exposição Total": "Total Exposure",
  "Engajamento Clique": "Click Engagement",
  "Conversão Leads": "Lead Conversion",
  "Eficiência CPC": "CPC Efficiency",
  "Investimento Médio por Clique": "Average Spend per Click",
  "Impacto CTR": "CTR Impact",
  "Eficácia do Criativo vs Anúncio": "Creative vs Ad Effectiveness",
  "Escala CPM": "CPM Scale",
  "Custo para cada Mil Impressões": "Cost per Thousand Impressions",
  "Eficiência CPL": "CPL Efficiency",
  "Custo Médio por Lead": "Average Cost per Lead",
  "Social Intelligence": "Social Intelligence",
  Investimento: "Spend",
  "Desempenho por Conta": "Performance by Account",
  "Campanhas de Alta Conversão": "High-Conversion Campaigns",
  "Conjuntos de Anúncios": "Ad Sets",
  "Melhores Anúncios (Ads)": "Top Ads",
  "Detalhamento Granular": "Granular Breakdown",
  "Histórico de Performance": "Performance History",
  Alcance: "Reach",
  "Salvando...": "Saving...",
  "Salvo!": "Saved!",
  "Aviso: Isto é um índice. Clique para substituir pelo ID Real da Conta (act_...).":
    "Warning: This is an index. Click to replace it with the Real Account ID (act_...).",
  "Clique para alterar a chave": "Click to change the key",
  "Chave...": "Key...",
  "Remover mapeamento": "Remove mapping",
  "Nenhum mapeamento ativo": "No active mapping",
  "Adicionar mapeamento": "Add mapping",
  "Novo alias...": "New alias...",
  "Remover alias": "Remove alias",
  Pressione: "Press",
  ou: "or",
  Vírgula: "Comma",
  "para adicionar": "to add",
  "E-mail não informado": "Email not provided",
  "Este lead não possui e-mail válido no CRM. Foi gerado um identificador temporário para processamento interno.":
    "This lead does not have a valid email in the CRM. A temporary identifier was generated for internal processing.",
  "Exibindo {start}–{end} de {total} leads":
    "Showing {start}–{end} of {total} leads",
  "Todas as Origens": "All Origins",
  "Landing Page": "Landing Page",
  Limpar: "Clear",
  "Busca & Filtros Avançados": "Search & Advanced Filters",
  "Busca por Lead": "Lead Search",
  "Nome, email ou ID...": "Name, email or ID...",
  "Período de Análise": "Analysis Period",
  "{days} Dias": "{days} Days",
  "Origem do Lead": "Lead Origin",
  "Aplicar Filtros": "Apply Filters",
  "Volume Geral": "Overall Volume",
  "Total de Leads": "Total Leads",
  "Volume bruto de todos os leads processados pelo sistema.":
    "Raw volume of all leads processed by the system.",
  Qualificados: "Qualified",
  "{percent}% do total": "{percent}% of total",
  "Leads válidos e prontos para o funil de vendas.":
    "Valid leads ready for the sales funnel.",
  Desqualificados: "Disqualified",
  "Leads descartados por dados inválidos ou fora do perfil.":
    "Leads discarded due to invalid data or off-profile.",
  "Plano de Mídia (Origens)": "Media Plan (Origins)",
  "Total de leads vindos de campanhas de Facebook / Instagram Ads.":
    "Total leads from Facebook / Instagram Ads campaigns.",
  "Leads originados de campanhas do Google Ads.":
    "Leads originated from Google Ads campaigns.",
  "RD Station (Entrada)": "RD Station (Inbound)",
  "Leads que entraram via RD Station (webhook de entrada).":
    "Leads that came in via RD Station (inbound webhook).",
  "CRM Direto": "Direct CRM",
  "Leads originados diretamente do CRM ou outras fontes não rastreadas.":
    "Leads originated directly from the CRM or other untracked sources.",
  "Entrega & Sincronização": "Delivery & Sync",
  "Sincronizados CV": "Synced CV",
  "Leads enviados e confirmados no CVCRM.":
    "Leads sent and confirmed in CVCRM.",
  "Não Sincronizados CV": "Not Synced CV",
  "Elegíveis pendentes": "Eligible pending",
  "Leads elegíveis que ainda não foram confirmados no CVCRM.":
    "Eligible leads that have not yet been confirmed in CVCRM.",
  "Inelegíveis CV": "Ineligible CV",
  "Sem telefone": "No phone",
  "Leads que não atendem os critérios mínimos (telefone) para envio ao CVCRM.":
    "Leads that don't meet the minimum criteria (phone) to be sent to CVCRM.",
  "Sincronizados RD": "Synced RD",
  "Leads enviados e confirmados no RD Station.":
    "Leads sent and confirmed in RD Station.",
  "Não Sincronizados RD": "Not Synced RD",
  "Leads elegíveis que ainda não foram confirmados no RD Station.":
    "Eligible leads that have not yet been confirmed in RD Station.",
  "Inelegíveis RD": "Ineligible RD",
  "Sem e-mail válido": "No valid email",
  "Leads sem e-mail válido, não enviados ao RD Station para evitar poluição da base.":
    "Leads without a valid email, not sent to RD Station to avoid polluting the base.",
  "Qualidade & Saúde do Funil": "Quality & Funnel Health",
  Repetidos: "Repeated",
  "Leads identificados como duplicados e não re-enviados ao CRM.":
    "Leads identified as duplicates and not re-sent to the CRM.",
  Represados: "Held Back",
  "Aguardando enriquecimento": "Awaiting enrichment",
  "Leads em processamento ou aguardando dados antes de seguir o fluxo.":
    "Leads being processed or awaiting data before continuing the flow.",
  "Risco Crítico": "Critical Risk",
  "Erros de processamento": "Processing errors",
  "Leads com falhas técnicas durante o processamento ou envio às integrações.":
    "Leads with technical failures during processing or sending to integrations.",
  "Módulo Leadflow · Jornada do Lead": "Leadflow Module · Lead Journey",
  "rastreia cada etapa do lead, desde a captura nos anúncios até a integração final com seu CRM.":
    "tracks every step of the lead, from ad capture to the final integration with your CRM.",
  "Para habilitar esta funcionalidade para sua organização, entre em contato com nossa equipe. A ativação é rápida e o time de suporte irá guiá-lo em cada etapa.":
    "To enable this feature for your organization, contact our team. Activation is quick and our support team will guide you through every step.",
  Origem: "Origin",
  "Destino CRM": "CRM Destination",
  "Status Processamento": "Processing Status",
  "Data e Horário": "Date and Time",
  "Nenhum evento registrado": "No events recorded",
  "Assim que os leads começarem a ser processados, eles aparecerão aqui.":
    "As soon as leads start being processed, they will appear here.",
  "Verificar agora": "Check now",
  "Nenhum resultado para": "No results for",
  "Detalhes da Jornada do Lead": "Lead Journey Details",
  "O envio foi processado com {status}":
    "The submission was processed with {status}",
  "Ações de Qualificação": "Qualification Actions",
  "Mude o status deste lead para sincronizar com públicos de anúncio":
    "Change this lead's status to sync with ad audiences",
  "Lead desqualificado com sucesso!": "Lead successfully disqualified!",
  "Erro ao desqualificar lead": "Error disqualifying lead",
  Desqualificar: "Disqualify",
  "Lead qualificado com sucesso!": "Lead successfully qualified!",
  "Erro ao qualificar lead": "Error qualifying lead",
  "Qualificar Lead": "Qualify Lead",
  "Intenção do Lead": "Lead Intent",
  "Interesses manifestados": "Expressed interests",
  "Produto/ID": "Product/ID",
  Contato: "Contact",
  "Interesse / Metragem": "Interest / Square Footage",
  "Lead sem Nome": "Unnamed Lead",
  "Contatos do Lead": "Lead Contacts",
  "E-mail Principal": "Primary Email",
  Inválido: "Invalid",
  "E-mail não informado no CRM": "Email not provided in CRM",
  "Este lead foi filtrado e": "This lead was filtered and",
  "não enviado ao RD Station": "not sent to RD Station",
  "para evitar poluição da base com e-mails inexistentes.":
    "to avoid polluting the base with nonexistent emails.",
  "Nível de Interesse (Score)": "Interest Level (Score)",
  "Calculado com base no preenchimento de campos e histórico de navegação.":
    "Calculated based on field completion and browsing history.",
  "Atendimento CRM": "CRM Service",
  "Responsável vinculado": "Assigned agent",
  "Ver no CVCRM": "View in CVCRM",
  "Aguardando atribuição de corretor...": "Awaiting agent assignment...",
  "IDs de Rastreamento": "Tracking IDs",
  "Linha do Tempo de Captura": "Capture Timeline",
  "Status OK": "Status OK",
  "Não capturado": "Not captured",
  "Rastreamento de Publicidade": "Advertising Tracking",
  "Origem Granular": "Granular Origin",
  Campanha: "Campaign",
  Conjunto: "Ad Set",
  "Resposta Técnica do CRM (Mensagem)": "CRM Technical Response (Message)",
  "Logs de Transação": "Transaction Logs",
  "Tem certeza que deseja excluir esta regra de roteamento?":
    "Are you sure you want to delete this routing rule?",
  "Nenhum Roteamento Configurado": "No Routing Configured",
  "Adicione inteligências para direcionar leads de LPs específicas.":
    "Add rules to route leads from specific landing pages.",
  "Fila CV": "CV Queue",
  "sem tags": "no tags",
  Prio: "Prio",
  "Editar Regra": "Edit Rule",
  "Excluir Regra": "Delete Rule",
  "Adicionar Regra de Roteamento": "Add Routing Rule",
  "Adicionar Inteligência de Roteamento": "Add Routing Rule",
  "Editar Inteligência de Roteamento": "Edit Routing Rule",
  "Nova Inteligência de Roteamento": "New Routing Rule",
  "Atualizar Regra": "Update Rule",
  "Salvar Configurações": "Save Settings",
  "Gatilho e Prioridade": "Trigger and Priority",
  "Identificador de Conversão (Padrão)": "Conversion Identifier (Default)",
  "Nome da Landing Page ou Formulário no RD Station. Use * para match parcial (ex: *RESIDENCE*).":
    "Name of the Landing Page or Form in RD Station. Use * for partial match (e.g. *RESIDENCE*).",
  Prioridade: "Priority",
  "Menor número = maior prioridade. Útil quando um lead dá match em múltiplas regras.":
    "Lower number = higher priority. Useful when a lead matches multiple rules.",
  "Destino (CVCRM)": "Destination (CVCRM)",
  "ID da Fila (idfila)": "Queue ID (idfila)",
  "O ID numérico da fila no seu CVCRM para onde o lead deve ser enviado.":
    "The numeric ID of the queue in your CVCRM where the lead should be sent.",
  "ID de Repressão (Opcional)": "Suppression ID (Optional)",
  "Conteúdo e Tags": "Content and Tags",
  "Origem/Mídia Personalizada": "Custom Source/Medium",
  "Tags para Adicionar": "Tags to Add",
  "Estas tags serão atribuídas ao lead no CVCRM quando ele der match nesta regra.":
    "These tags will be assigned to the lead in CVCRM when it matches this rule.",
  "Digite a tag...": "Type the tag...",
  "Tipo de Mapeamento": "Mapping Type",
  "Defina se este item é um produto individual ou um empreendimento completo.":
    "Define whether this item is an individual product or a full development.",
  Produto: "Product",
  Empreendimento: "Development",
  "Nome do Produto": "Product Name",
  "Nome do Empreendimento": "Development Name",
  "Nome identificador usado apenas para controle interno no Leadflow.":
    "Identifier name used only for internal control in Leadflow.",
  "Ex: Nome da Unidade": "E.g.: Unit Name",
  "ID CRM (Prod)": "CRM ID (Prod)",
  "ID CRM (Ent)": "CRM ID (Dev)",
  "O ID numérico exato deste item dentro do seu CRM de destino.":
    "The exact numeric ID of this item within your destination CRM.",
  "Trigger Site/Ads": "Site/Ads Trigger",
  "Qual campo técnico do Meta/Google/Site usaremos como gatilho para esta regra?":
    "Which technical field from Meta/Google/Site will we use as the trigger for this rule?",
  "Padrão de Busca (Regex/*)": "Search Pattern (Regex/*)",
  "O valor que deve ser encontrado no Trigger. Aceita '.*' (qualquer coisa) ou valor exato.":
    "The value that must be found in the Trigger. Accepts '.*' (anything) or an exact value.",
  "Regras com números maiores são avaliadas primeiro (ex: 100 vence 10).":
    "Rules with higher numbers are evaluated first (e.g. 100 beats 10).",
  "Destino Hub": "Destination Hub",
  "Para qual sistema de destino os leads deste mapeamento devem ser enviados.":
    "Which destination system the leads from this mapping should be sent to.",
  "Define se esta regra está ativa para novos leads.":
    "Defines whether this rule is active for new leads.",
  "Ativar Regra de Sincronização": "Enable Sync Rule",
  "Remover esta regra": "Remove this rule",
  "Remover regra": "Remove rule",
  "Padrão (Pattern)": "Pattern",
  "Prioridade:": "Priority:",
  "Adicionar nova entrada": "Add new entry",
  "Adicionar Regra": "Add Rule",
  "Configuração ausente": "Missing configuration",
  "Nenhuma configuração do CVCRM encontrada.": "No CVCRM configuration found.",
  "Nenhuma configuração avançada encontrada.":
    "No advanced configuration found.",
  "Ajuste como os leads são formatados e enviados para o seu CRM.":
    "Adjust how leads are formatted and sent to your CRM.",
  "Destino: CVCRM": "Destination: CVCRM",
  "Configurações de Entrega": "Delivery Settings",
  "Parâmetros & Origens": "Parameters & Origins",
  "Configurações de origem enviadas para o CVCRM.":
    "Origin settings sent to CVCRM.",
  "Defina os IDs das origens que serão enviados ao CRM para cada canal de entrada.":
    "Set the origin IDs that will be sent to the CRM for each inbound channel.",
  "Origem Global": "Global Origin",
  "Origem enviada se nenhum mapeamento específico for encontrado.":
    "Origin sent when no specific mapping is found.",
  "ID da Origem no CRM para leads vindos especificamente do Facebook Ads.":
    "CRM Origin ID for leads coming specifically from Facebook Ads.",
  "ID da Origem no CRM para leads vindos especificamente do Instagram Ads.":
    "CRM Origin ID for leads coming specifically from Instagram Ads.",
  "ID da Origem no CRM para leads do Google Ads/Search.":
    "CRM Origin ID for leads from Google Ads/Search.",
  "ID da Origem no CRM para leads sincronizados via RD Station (ex: Webhook RD).":
    "CRM Origin ID for leads synced via RD Station (e.g. RD Webhook).",
  "Meta (Fallback)": "Meta (Fallback)",
  "Valor de fallback para leads da Meta se as origens específicas de FB/IG estiverem vazias.":
    "Fallback value for Meta leads if the specific FB/IG origins are empty.",
  "Dados técnicos de integração com a API do CVCRM.":
    "Technical data for the CVCRM API integration.",
  "CVCRM: Parâmetros de Conexão": "CVCRM: Connection Parameters",
  "Estes parâmetros são gerenciados globalmente e não podem ser alterados por este painel.":
    "These parameters are managed globally and cannot be changed from this panel.",
  Subdomínio: "Subdomain",
  "O subdomínio da sua instância do CVCRM (ex: amana.cvcrm.com.br).":
    "The subdomain of your CVCRM instance (e.g. amana.cvcrm.com.br).",
  "Token do CRM": "CRM Token",
  "Token de API usado para autenticar o Leadflow no seu CRM.":
    "API token used to authenticate Leadflow with your CRM.",
  "Regras para evitar envio de leads incompletos ou repetidos.":
    "Rules to prevent sending incomplete or repeated leads.",
  "Segurança & Duplicidade": "Security & Duplication",
  "Regras para evitar envio de leads incompletos ou repetidos ao CRM.":
    "Rules to prevent sending incomplete or repeated leads to the CRM.",
  "Obrigar Telefone": "Require Phone",
  "Descarta o lead se não houver telefone válido.":
    "Discards the lead if there is no valid phone number.",
  "Se ativado, leads sem telefone válido serão descartados antes de chegar ao CRM.":
    "If enabled, leads without a valid phone number will be discarded before reaching the CRM.",
  Desativado: "Disabled",
  Ativado: "Enabled",
  "Intervalo de tempo para considerar um lead como duplicado.":
    "Time window to consider a lead as duplicate.",
  "CVCRM: Janela de Duplicidade": "CVCRM: Duplication Window",
  "Intervalo de tempo para considerar um lead como duplicado se vier da mesma origem.":
    "Time window to consider a lead as duplicate if it comes from the same origin.",
  "Janela de Retenção": "Retention Window",
  "Evite leads repetidos no mesmo período.":
    "Avoid repeated leads within the same period.",
  Horas: "Hours",
  "Ajuste como os nomes das plataformas e descrições aparecem no CRM.":
    "Adjust how platform names and descriptions appear in the CRM.",
  "Customização de Labels & Interações": "Labels & Interactions Customization",
  "Ajuste como os nomes das plataformas e descrições das interações aparecem no CRM.":
    "Adjust how platform names and interaction descriptions appear in the CRM.",
  "Label Facebook": "Facebook Label",
  "Nome que aparecerá na descrição da interação para identificar o FB.":
    "Name that will appear in the interaction description to identify FB.",
  "Label Instagram": "Instagram Label",
  "Nome que aparecerá na descrição da interação para identificar o IG.":
    "Name that will appear in the interaction description to identify IG.",
  "Label Google": "Google Label",
  "Nome que aparecerá na descrição da interação para identificar o Google.":
    "Name that will appear in the interaction description to identify Google.",
  "Label Meta (Fallback)": "Meta Label (Fallback)",
  "Nome usado se a plataforma Meta não for identificada como FB ou IG.":
    "Name used if the Meta platform is not identified as FB or IG.",
  "Template de Mídia": "Media Template",
  "Formato do campo 'Mídia' no CRM. Suporta tags como {platform}, {campaign_name}, {adset}, {ad}.":
    "Format of the 'Media' field in the CRM. Supports tags like {platform}, {campaign_name}, {adset}, {ad}.",
  "Template de Descrição": "Description Template",
  "Texto principal da interação enviada ao CRM.":
    "Main text of the interaction sent to the CRM.",
  "Telefone Placeholder": "Placeholder Phone",
  "Telefone padrão usado se o lead não possuir um número válido (ex: 11999999999).":
    "Default phone used if the lead doesn't have a valid number (e.g. 11999999999).",
  "RD Sync Automático": "Automatic RD Sync",
  "Define se os leads devem ser sincronizados automaticamente com o RD Station após a entrada no CRM.":
    "Defines whether leads should be automatically synced with RD Station after entering the CRM.",
  Não: "No",
  Sim: "Yes",
  "Identificação técnica de produtos e empreendimentos no CRM.":
    "Technical identification of products and developments in the CRM.",
  "Mapeamento de Produto (No CRM)": "Product Mapping (In CRM)",
  "Configure os campos slug do CRM que identificarão automaticamente o produto e empreendimento.":
    "Configure the CRM slug fields that will automatically identify the product and development.",
  "Slug Campo Código Prod.": "Product Code Field Slug",
  "Nome técnico do campo no CRM que armazena o ID do produto (ex: cf_id_produto).":
    "Technical name of the CRM field that stores the product ID (e.g. cf_id_produto).",
  "Slug Campo Nome Prod.": "Product Name Field Slug",
  "Nome técnico do campo no CRM que armazena o nome por extenso do produto.":
    "Technical name of the CRM field that stores the full product name.",
  "Controle o fluxo de leads que já existem no CRM para permitir ou bloquear novos processamentos.":
    "Control the flow of leads that already exist in the CRM to allow or block further processing.",
  "Regras de Reentrada": "Re-entry Rules",
  Habilitado: "Enabled",
  "Status Permitidos": "Allowed Statuses",
  "IDs dos status no CVCRM onde o lead PODE entrar novamente (ex: Perdido, Cancelado).":
    "IDs of the CVCRM statuses where the lead CAN re-enter (e.g. Lost, Cancelled).",
  "Status Bloqueados": "Blocked Statuses",
  "IDs dos status onde a entrada é terminantemente PROIBIDA (ex: Vendido, Em Negociação).":
    "IDs of the statuses where entry is strictly FORBIDDEN (e.g. Sold, In Negotiation).",
  "Status de Reinício": "Restart Statuses",
  "IDs dos status que forçam o lead a entrar na roleta de distribuição novamente.":
    "IDs of the statuses that force the lead to re-enter the distribution roulette.",
  "Etapas Bloqueadas": "Blocked Stages",
  "Filtra a reentrada do lead se ele estiver em uma destas etapas do funil de vendas.":
    "Filters the lead's re-entry if it is in one of these sales funnel stages.",
  "Nova Etapa Reentrada": "New Re-entry Stage",
  "ID da etapa no CRM para onde o lead será movido ao reentrar (ex: ID da Etapa de Roleta).":
    "ID of the CRM stage where the lead will be moved upon re-entry (e.g. Roulette Stage ID).",
  "Nova Situação Reentrada": "New Re-entry Status",
  "ID da situação no CRM para onde o lead será movido ao reentrar (ex: ID de Em Atendimento).":
    "ID of the CRM status where the lead will be moved upon re-entry (e.g. In Service status ID).",
  "De-para de campos customizados das plataformas para o CRM.":
    "Mapping of custom fields from platforms to the CRM.",
  "CustomFields CVCRM": "CVCRM Custom Fields",
  "Mapeie perguntas customizadas das plataformas para campos específicos no seu CRM.":
    "Map custom questions from platforms to specific fields in your CRM.",
  "Adicionar novo campo customizado para mapeamento":
    "Add new custom field for mapping",
  "Novo Mapeamento": "New Mapping",
  "Slug do campo no CVCRM:": "CVCRM field slug:",
  "Criar Campo": "Create Field",
  "Remover este mapeamento": "Remove this mapping",
  "Remover mapeamento?": "Remove mapping?",
  'Deseja excluir as regras para "{key}"?':
    'Do you want to delete the rules for "{key}"?',
  "Sim, remover": "Yes, remove",
  Manter: "Keep",
  "Dados ausentes": "Missing data",
  "Nenhum mapeamento encontrado para este contexto.":
    "No mapping found for this context.",
  "Configure os nomes de campos que o Leadflow deve reconhecer como E-mail, Telefone, Nome, etc.":
    "Configure the field names that Leadflow should recognize as Email, Phone, Name, etc.",
  "Dicionário Global": "Global Dictionary",
  "Aliasing & Normalização": "Aliasing & Normalization",
  "Padronize os nomes de campos vindos da Meta ou Google para chaves universais.":
    "Standardize field names coming from Meta or Google into universal keys.",
  "Mapeie os campos capturados para os dados de contato no seu CRM.":
    "Map the captured fields to the contact data in your CRM.",
  "Dados de Contato": "Contact Data",
  "Mapeie os nomes de campos que o Leadflow deve reconhecer como informações básicas do lead.":
    "Map the field names that Leadflow should recognize as basic lead information.",
  "Diferentes nomes de campos que representam o E-mail (ex: email, mail, contato_email).":
    "Different field names that represent Email (e.g. email, mail, contato_email).",
  "Campos que trazem o telefone do lead (ex: phone, tel, celular, whatsapp).":
    "Fields that carry the lead's phone number (e.g. phone, tel, celular, whatsapp).",
  "Campos para o nome completo ou primeiro nome (ex: name, nome, first_name).":
    "Fields for the full name or first name (e.g. name, nome, first_name).",
  "Configure os campos que identificam a origem da campanha (UTMs).":
    "Configure the fields that identify the campaign origin (UTMs).",
  "Dados de Campanha": "Campaign Data",
  "Configure os campos que identificam a origem técnica e as UTMs da campanha.":
    "Configure the fields that identify the technical origin and campaign UTMs.",
  "Nomes de campos para a campanha (ex: utm_campaign, campaign, campaign_name).":
    "Field names for the campaign (e.g. utm_campaign, campaign, campaign_name).",
  "Identificadores do conjunto de anúncios (ex: adset, adset_name).":
    "Ad set identifiers (e.g. adset, adset_name).",
  "Nome do anúncio específico (ex: ad, ad_name, ad_layout).":
    "Specific ad name (e.g. ad, ad_name, ad_layout).",
  "O ID numérico do anúncio vindo da plataforma (ex: ad_id, id_anuncio).":
    "The numeric ad ID coming from the platform (e.g. ad_id, id_anuncio).",
  "Configurações avançadas para envio de leads e tags para o RD Station.":
    "Advanced settings for sending leads and tags to RD Station.",
  "Roteamento Inteligente": "Smart Routing",
  "Lógica RD & Tags de Conta": "RD Logic & Account Tags",
  "Distribuição avançada baseada em eventos e origens.":
    "Advanced distribution based on events and origins.",
  "Dados técnicos de integração com a API do RD Station.":
    "Technical data for the RD Station API integration.",
  "RD Station: Credenciais de API": "RD Station: API Credentials",
  "As credenciais do RD Station são configuradas via ambiente e estão protegidas.":
    "RD Station credentials are configured via environment and are protected.",
  "Identificador único da sua aplicação no App Studio do RD Station.":
    "Unique identifier of your application in RD Station's App Studio.",
  "Token de segurança gerado via OAuth para permitir a comunicação entre Leadflow e RD Station.":
    "Security token generated via OAuth to allow communication between Leadflow and RD Station.",
  "Sincronizado (Oculto)": "Synced (Hidden)",
  "Não Configurado": "Not Configured",
  "Configuração unificada de Tags, Mídia e Fila baseada no Identificador de Conversão do RD Station.":
    "Unified configuration of Tags, Media and Queue based on RD Station's Conversion Identifier.",
  "RD Station: Roteamento Integrado": "RD Station: Integrated Routing",
  "Regras unificadas de Fila, Tags e Mídia baseadas no Identificador de Conversão do RD Station.":
    "Unified rules for Queue, Tags and Media based on RD Station's Conversion Identifier.",
  "Lista de nomes de formulários ou Landing Pages que pertencem a esta empresa no RD Station.":
    "List of form or Landing Page names that belong to this company in RD Station.",
  "Identificadores de Conversão": "Conversion Identifiers",
  "Associe os nomes das Landing Pages ou Formulários do RD Station que roteiam leads para esta empresa.":
    "Associate the RD Station Landing Page or Form names that route leads to this company.",
  "Lista de Identificadores RD": "List of RD Identifiers",
  "Títulos exatos das páginas ou identificadores de conversão na RD Station (ex: Landing Page X, Form Contato).":
    "Exact titles of the pages or conversion identifiers in RD Station (e.g. Landing Page X, Contact Form).",
  "Novo identificador...": "New identifier...",
  "Configura quais situações do CVCRM disparam a atualização automática do lead no RD Station.":
    "Configures which CVCRM statuses trigger the automatic lead update in RD Station.",
  "Sincronização Reversa": "Reverse Sync",
  "Configura quais situações do CRM disparam a atualização automática do lead no RD Station.":
    "Configures which CRM statuses trigger the automatic lead update in RD Station.",
  "IDs de Situação Permitidos": "Allowed Status IDs",
  "IDs das situações do CVCRM que devem ser enviadas ao RD Station via webhook. Se vazio, todas as situações serão sincronizadas.":
    "IDs of the CVCRM statuses that should be sent to RD Station via webhook. If empty, all statuses will be synced.",
  "Aplica uma tag específica no lead do RD conforme a Conta de Anúncios Meta/Google de origem.":
    "Applies a specific tag to the RD lead based on the originating Meta/Google Ads Account.",
  "Tags por Conta de Anúncios": "Tags by Ad Account",
  "ID da Conta (act_...)": "Account ID (act_...)",
  "Tag Associada": "Associated Tag",
  "Define o canal de aquisição (Mídia) baseado no ID da Conta de Anúncios enviada pela plataforma.":
    "Defines the acquisition channel (Media) based on the Ad Account ID sent by the platform.",
  "Mídia por Conta de Anúncios": "Media by Ad Account",
  "Mídia Associada": "Associated Media",
  "Mapeie o nome técnico dos campos customizados do CVCRM para os respectivos identificadores da API do RD Station.":
    "Map the technical name of CVCRM custom fields to their corresponding RD Station API identifiers.",
  "RD Station: Dicionário": "RD Station: Dictionary",
  "Mapeie o nome técnico dos campos customizados do CVCRM para o RD Station.":
    "Map the technical name of CVCRM custom fields to RD Station.",
  "Campo no CVCRM": "Field in CVCRM",
  "API Identifier no RD": "API Identifier in RD",
  "Define a situação do lead no CVCRM que dispara o descadastro automático (Opt-out) nas bases legais do RD Station.":
    "Defines the lead status in CVCRM that triggers automatic unsubscribe (Opt-out) in RD Station's legal bases.",
  "RD Station: NÃO PERTURBE": "RD Station: DO NOT DISTURB",
  "Define a situação que dispara o descadastro automático (Opt-out) no RD Station.":
    "Defines the status that triggers automatic unsubscribe (Opt-out) in RD Station.",
  "ID Situação Descadastro": "Unsubscribe Status ID",
  "ID da situação no CRM que, ao ser atingida pelo lead, dispara o descadastro legal no RD Station (padrão CV: 14).":
    "ID of the CRM status that, once reached by the lead, triggers the legal unsubscribe in RD Station (CV default: 14).",
  "Categorias Base Legal": "Legal Basis Categories",
  "Identificadores de Conversão (RD Leads)":
    "Conversion Identifiers (RD Leads)",
  "RD Station: Mapeamento de Conversões": "RD Station: Conversion Mapping",
  "Associe situações do CVCRM a nomes específicos de identificadores no RD Station.":
    "Associate CVCRM statuses with specific identifier names in RD Station.",
  "ID Situação": "Status ID",
  "Identificador no RD": "Identifier in RD",
  "Leads vindos destas Landing Pages (Identificadores) serão salvos no Leadflow, mas NÃO serão enviados ao CVCRM.":
    "Leads coming from these Landing Pages (Identifiers) will be saved in Leadflow, but will NOT be sent to CVCRM.",
  "RD Station: Blacklist de LPs": "RD Station: LP Blacklist",
  "Leads vindos destas Landing Pages serão salvos no Leadflow, mas NÃO serão enviados ao CVCRM.":
    "Leads coming from these Landing Pages will be saved in Leadflow, but will NOT be sent to CVCRM.",
  "Adicionar novo mapeamento de sincronização (Produto ou Empreendimento)":
    "Add new sync mapping (Product or Development)",
  "{count} regra(s) monitorando capturas":
    "{count} rule(s) monitoring captures",
  "Sincronização Ativa": "Active Sync",
  'Excluir "{name}"?': 'Delete "{name}"?',
  "Excluindo...": "Deleting...",
  "Mapeamento de Produto Individual": "Individual Product Mapping",
  "Mapeamento de Empreendimento Completo": "Full Development Mapping",
  "Identificador único deste item no CRM":
    "Unique identifier of this item in the CRM",
  "Campo e valor que disparam esta regra":
    "Field and value that trigger this rule",
  "Prioridade de processamento": "Processing priority",
  "Editar configurações desta regra": "Edit this rule's settings",
  "Editar Mapeamento": "Edit Mapping",
  "Nenhuma regra cadastrada": "No rules registered",
  'Clique em "Adicionar Regra" para começar.':
    'Click "Add Rule" to get started.',
  "Novo {type}": "New {type}",
  "Defina o padrão de identificação automática":
    "Define the automatic identification pattern",
  "Ajuste as regras de sincronismo": "Adjust the sync rules",
  Descartar: "Discard",
  "Criar Mapeamento": "Create Mapping",
  Ranking: "Ranking",
  "{count} itens": "{count} items",
  "Filtrar por este item": "Filter by this item",
  "Ocultar Ranking": "Hide Ranking",
  "Expandir +{count} Registros": "Expand +{count} Records",
  "Refinar análise de performance": "Refine performance analysis",
  "Hierarquia de Anúncios": "Ad Hierarchy",
  "Conta de Anúncio": "Ad Account",
  "Todas as Contas": "All Accounts",
  "Todas as Campanhas": "All Campaigns",
  "Conjunto de Anúncios": "Ad Set",
  "Todos os Conjuntos": "All Ad Sets",
  "Anúncio Individual": "Individual Ad",
  "Todos os Anúncios": "All Ads",
  "Aplicar e Ver Resultados": "Apply and View Results",
  "Média: {value}": "Average: {value}",

  // --- i18n additions (CampaignLeads, LeadKanban, AdsAnalytics, Analytics, AdminCompanies, PostCard, SocialConfigPanel, SuggestionCard, ContentCard, EbookCard) ---
  "* A criação de organizações requer um usuário proprietário. Use o gerenciador de usuários para vincular proprietários a novas organizações.":
    "* Creating organizations requires an owner user. Use the user manager to link owners to new organizations.",
  "...e mais {count} erros": "...and {count} more errors",
  "A IA precisa de pelo menos 10 posts publicados para calibrar as predições de acordo com seu perfil. Atualmente exibindo resultados baseados em benchmarks globais.":
    "The AI needs at least 10 published posts to calibrate predictions according to your profile. Currently showing results based on global benchmarks.",
  "Adicionar Imagem": "Add Image",
  "Adicionar Vídeo": "Add Video",
  "Agendado para": "Scheduled for",
  "Agendado para {date}": "Scheduled for {date}",
  "Agendado: {date}": "Scheduled: {date}",
  Agendamento: "Scheduling",
  "Agendar Publicação": "Schedule Publication",
  "Aguardando Configuração": "Awaiting Configuration",
  "Aguarde novas conversões ou ajuste seus filtros.":
    "Wait for new conversions or adjust your filters.",
  "Alterações pendentes detectadas": "Pending changes detected",
  "Analytics de Anúncios": "Ads Analytics",
  "Análise profunda e inteligência preditiva para sua estratégia de conteúdo social.":
    "Deep analysis and predictive intelligence for your social content strategy.",
  "Apenas cancelados": "Cancelled only",
  "Apenas duplicados": "Duplicates only",
  "Apenas fraudes": "Fraud only",
  "Aprovar com Horário": "Approve with Schedule",
  Arquivo: "File",
  "Atenção:": "Attention:",
  "Autenticação Automática": "Automatic Authentication",
  "Ações de Risco": "Risk Actions",
  "Baixando...": "Downloading...",
  "Baixar PDF": "Download PDF",
  Beta: "Beta",
  "Bloquear após prazo": "Block after deadline",
  "Buscando páginas disponíveis...": "Searching for available pages...",
  "Buscando...": "Searching...",
  "Buscar por organização ou CNPJ...": "Search by organization or Tax ID...",
  "CONECTADO À CONTA:": "CONNECTED TO ACCOUNT:",
  "CPA Médio": "Average CPA",
  "Cadastre uma nova organização (conta mestra) no sistema.":
    "Register a new organization (master account) in the system.",
  "Campanhas com maior retorno real":
    "Campaigns with the highest actual return",
  "Campo Avançado: Usado para conexões BYOA":
    "Advanced Field: Used for BYOA connections",
  Cancelados: "Cancelled",
  Captura: "Capture",
  "Central de Ajuda: {name}": "Help Center: {name}",
  "Clique para abrir": "Click to open",
  "Cliques no Site": "Website Clicks",
  Comentários: "Comments",
  Compartilhamentos: "Shares",
  "Conectado: {page}": "Connected: {page}",
  "Conectar LinkedIn": "Connect LinkedIn",
  "Conectar Meta": "Connect Meta",
  "Conectar {target}": "Connect {target}",
  "Conecte sua conta Meta sem precisar gerenciar tokens.":
    "Connect your Meta account without having to manage tokens.",
  "Conecte sua conta do Google sem precisar inserir tokens manualmente.":
    "Connect your Google account without having to manually enter tokens.",
  "Conecte sua conta do LinkedIn através de um clique.":
    "Connect your LinkedIn account with one click.",
  "Conexão Ativa": "Active Connection",
  "Configurações do Free Tier salvas com sucesso!":
    "Free Tier settings saved successfully!",
  "Confirmar Fraude": "Confirm Fraud",
  "Conta Google conectada com sucesso!":
    "Google account connected successfully!",
  "Conta LinkedIn conectada com sucesso!":
    "LinkedIn account connected successfully!",
  "Conta Meta desconectada.": "Meta account disconnected.",
  Contatado: "Contacted",
  "Conteúdo Salvo": "Content Saved",
  "Conteúdo copiado!": "Content copied!",
  "Conversões Google": "Google Conversions",
  "Copiar link UTM": "Copy UTM link",
  "Criar Público Personalizado na Meta Ads":
    "Create Custom Audience in Meta Ads",
  "Criativo com texto": "Creative with text",
  "Crie tags para segmentar audiências": "Create tags to segment audiences",
  "Créditos Iniciais": "Initial Credits",
  "Créditos e Faturamento": "Credits and Billing",
  Curtidas: "Likes",
  "Custo Lead": "Cost per Lead",
  "Custo por Aquisição": "Cost per Acquisition",
  "Custom Audience Criada": "Custom Audience Created",
  "Data Cadastro": "Registration Date",
  "Data de publicação": "Publication date",
  "De um total de": "Out of a total of",
  Desconectar: "Disconnect",
  "Desconectar LinkedIn": "Disconnect LinkedIn",
  "Desconectar Meta": "Disconnect Meta",
  "Desconectar conta": "Disconnect account",
  "Descrição da fraude (opcional):": "Fraud description (optional):",
  "Deseja marcar este lead como fraudulento? Isso ajudará na inteligência futura.":
    "Do you want to mark this lead as fraudulent? This will help future intelligence.",
  "Desmarcar Duplicado": "Unmark Duplicate",
  Desqualificado: "Disqualified",
  "Destino CAPI": "CAPI Destination",
  Diagnóstico: "Diagnosis",
  "Dias de Teste Grátis": "Free Trial Days",
  Direções: "Directions",
  "Documentação Técnica": "Technical Documentation",
  Download: "Download",
  Duplicado: "Duplicate",
  Duplicados: "Duplicates",
  "Editando: {name}": "Editing: {name}",
  "Editar Conteúdo": "Edit Content",
  "Editar Ebook": "Edit Ebook",
  "Editar Vídeo": "Edit Video",
  Edição: "Editing",
  "Erro ao atualizar organização": "Error updating organization",
  "Erro ao atualizar tags": "Error updating tags",
  "Erro ao baixar modelo": "Error downloading template",
  "Erro ao carregar Kanban": "Error loading Kanban",
  "Erro ao carregar analytics de anúncios": "Error loading ads analytics",
  "Erro ao carregar leads": "Error loading leads",
  "Erro ao carregar organizações": "Error loading organizations",
  "Erro ao conectar página Meta": "Error connecting Meta page",
  "Erro ao criar etapa": "Error creating stage",
  "Erro ao criar organização": "Error creating organization",
  "Erro ao desconectar": "Error disconnecting",
  "Erro ao desconectar Meta": "Error disconnecting Meta",
  "Erro ao excluir lead": "Error deleting lead",
  "Erro ao exportar leads": "Error exporting leads",
  "Erro ao gerar link de conexão": "Error generating connection link",
  "Erro ao importar planilha": "Error importing spreadsheet",
  "Erro ao listar páginas Meta": "Error listing Meta pages",
  "Erro ao mover lead": "Error moving lead",
  "Erro ao processar duplicado": "Error processing duplicate",
  "Erro ao processar fraude": "Error processing fraud",
  "Erro ao remover etapa": "Error removing stage",
  "Erro ao salvar configurações do Free Tier":
    "Error saving Free Tier settings",
  "Erro ao salvar etapa": "Error saving stage",
  "Erro ao sincronizar CAPI": "Error syncing CAPI",
  "Erro ao sincronizar com CRM": "Error syncing with CRM",
  "Erro ao tentar desconectar": "Error trying to disconnect",
  "Erro de conexão com o servidor de CAPI":
    "Connection error with the CAPI server",
  "Erro de rede ao buscar páginas": "Network error fetching pages",
  "Erro de rede ao tentar conectar": "Network error trying to connect",
  "Erro na Criação do Público": "Error Creating Audience",
  "Erro na Publicação": "Publishing Error",
  "Erro na exclusão em massa": "Error in bulk deletion",
  "Esta ação não pode ser desfeita!": "This action cannot be undone!",
  "Esta ação é definitiva e removerá o lead permanentemente dos registros.":
    "This action is final and will permanently remove the lead from the records.",
  "Esta organização ainda não cadastrou nenhuma empresa cliente (unidade).":
    "This organization has not yet registered any client company (unit).",
  "Este post não possui imagem ou vídeo": "This post has no image or video",
  "Etapa atualizada!": "Stage updated!",
  "Etapa criada!": "Stage created!",
  "Etapa padrão — não pode ser excluída": "Default stage — cannot be deleted",
  "Etapa removida.": "Stage removed.",
  'Etapa removida. {count} lead(s) movidos para "Novo".':
    'Stage removed. {count} lead(s) moved to "New".',
  "Etapas do Kanban": "Kanban Stages",
  "Etapas padrão não podem ser excluídas": "Default stages cannot be deleted",
  'Etapas padrão podem ser renomeadas mas não excluídas · Leads em etapas excluídas vão para "Novo"':
    'Default stages can be renamed but not deleted · Leads in deleted stages go to "New"',
  'Evento "{message}" enviado para as plataformas CAPI selecionadas.':
    'Event "{message}" sent to the selected CAPI platforms.',
  "Evento Sincronizado": "Event Synced",
  "Ex: Bot, número falso...": "E.g.: Bot, fake number...",
  "Ex: Custom Audience - Clientes Virtus":
    "E.g.: Custom Audience - Virtus Clients",
  "Ex: Grupo Virtus": "E.g.: Grupo Virtus",
  "Excluir Lead": "Delete Lead",
  "Excluir Lead?": "Delete Lead?",
  "Excluir Post": "Delete Post",
  "Excluir cancelados": "Exclude cancelled",
  "Excluir duplicados": "Exclude duplicates",
  "Excluir em Massa": "Bulk Delete",
  "Excluir fraudes": "Exclude fraud",
  "Excluir {count} Leads?": "Delete {count} Leads?",
  "Exportando...": "Exporting...",
  "Exportar Leads (XLSX)": "Export Leads (XLSX)",
  "Falha Crítica": "Critical Failure",
  "Falha ao conectar página": "Failed to connect page",
  "Falha ao salvar": "Failed to save",
  "Falha na autenticação do Google.": "Google authentication failed.",
  "Falha na autenticação do LinkedIn.": "LinkedIn authentication failed.",
  "Falha na publicação": "Publishing failure",
  Faturamento: "Billing",
  "Faturamento Total": "Total Billing",
  Fechado: "Closed",
  "Filtros de Inteligência": "Intelligence Filters",
  "Flag de fraude removida!": "Fraud flag removed!",
  "Fonte de Dados (.xlsx)": "Data Source (.xlsx)",
  "Fora do perfil desejado": "Outside the desired profile",
  Fraude: "Fraud",
  Fraudes: "Fraud",
  "Gasto Total em Ads": "Total Ad Spend",
  "Gerenciar Etapas": "Manage Stages",
  "Gerenciar Tags": "Manage Tags",
  "Gerenciar tags": "Manage tags",
  "Gerencie as limitações e a validade padrão do plano de experimentação para todas as novas organizações do ecossistema.":
    "Manage the limitations and default validity of the trial plan for all new organizations in the ecosystem.",
  Gestão: "Management",
  Global: "Global",
  "Google Meu Negócio": "Google My Business",
  "Guia de NegÃ³cios": "Business Guide",
  "IA Ativa": "AI Active",
  "IA redimensionou para todos formatos sociais (Clique para ver)":
    "AI resized for all social formats (Click to view)",
  "ID Unidade: {id}": "Unit ID: {id}",
  "ID do Post: #{id}": "Post ID: #{id}",
  "ID: {id}": "ID: {id}",
  "Imagem do post {id}": "Image of post {id}",
  "Imagem não disponível": "Image not available",
  "Imagem {current} de {total}": "Image {current} of {total}",
  "Imagem {index} do post {id}": "Image {index} of post {id}",
  Imagens: "Images",
  Importados: "Imported",
  "Importar Planilha (CAPI)": "Import Spreadsheet (CAPI)",
  "Importe sua base de dados externa para nutrir o ecossistema CAPI (Conversion API) e otimizar públicos.":
    "Import your external database to feed the CAPI (Conversion API) ecosystem and optimize audiences.",
  "Impressões Search": "Search Impressions",
  "Inbound Comum": "Common Inbound",
  "Inconsistências Detectadas ({count})": "Inconsistencies Detected ({count})",
  Independente: "Independent",
  "Iniciar Importação": "Start Import",
  "Integrados com sucesso": "Successfully integrated",
  "Integração LinkedIn removida.": "LinkedIn integration removed.",
  "Integração Oficial": "Official Integration",
  "Integração removida.": "Integration removed.",
  Investido: "Invested",
  "Investido Google": "Google Invested",
  "Isenta a organização de cobranças e bloqueios de créditos.":
    "Exempts the organization from charges and credit blocks.",
  "Lead & Perfil": "Lead & Profile",
  "Lead Qualificado (Lookalike)": "Qualified Lead (Lookalike)",
  "Lead excluído permanentemente!": "Lead permanently deleted!",
  "Lead marcado como duplicado!": "Lead marked as duplicate!",
  "Lead movido para {label}": "Lead moved to {label}",
  "Lead reportado como fraude!": "Lead reported as fraud!",
  "Lead sincronizado com CRM com sucesso!":
    "Lead successfully synced with CRM!",
  "Leads Cancelados": "Cancelled Leads",
  "Leads Duplicados": "Duplicate Leads",
  "Leads de Campanhas Pagas": "Paid Campaign Leads",
  "Leads Únicos": "Unique Leads",
  Ligações: "Calls",
  "Linha {row}: {message}": "Row {row}: {message}",
  "Lista de todas as empresas cliente cadastradas sob esta organização.":
    "List of all client companies registered under this organization.",
  "Mais opÃ§Ãµes": "More options",
  "Mais opções": "More options",
  "Manual / Orgânico": "Manual / Organic",
  "Marcar como Duplicado": "Mark as Duplicate",
  "Marcação de duplicado removida!": "Duplicate mark removed!",
  Membros: "Members",
  "Menu de opções": "Options menu",
  "Modelo baixado com sucesso!": "Template downloaded successfully!",
  "Modo de Aprendizado": "Learning Mode",
  "Motivo da Rejeição/Falha:": "Rejection/Failure Reason:",
  "Motivo não especificado. (Rejeitado antes da atualização do sistema)":
    "Reason not specified. (Rejected before the system update)",
  "Multi Plataforma": "Multi Platform",
  "Métrica de Validação": "Validation Metric",
  "Métricas avançadas de investimento, performance de conversão e ROI por plataforma.":
    "Advanced metrics on investment, conversion performance, and ROI by platform.",
  "Métricas de Conteúdo": "Content Metrics",
  "Métricas de Performance": "Performance Metrics",
  Mídia: "Media",
  Negociando: "Negotiating",
  "Nenhum resultado": "No results",
  "Nenhuma Unidade": "No Units",
  "Nenhuma descriÃ§Ã£o disponÃ­vel para este ebook.":
    "No description available for this ebook.",
  "Nenhuma etapa configurada": "No stage configured",
  "Nenhuma organização global cadastrada ainda.":
    "No global organization registered yet.",
  "Nenhuma página encontrada no Business Suite da Virtus.":
    "No page found in Virtus' Business Suite.",
  "Nenhuma tag criada": "No tag created",
  "Nenhuma tag criada ainda": "No tag created yet",
  "Nome da Organização": "Organization Name",
  "Nome da nova etapa...": "New stage name...",
  "Nome da tag...": "Tag name...",
  "Nome do Público": "Audience Name",
  Nova: "New",
  "Nova Etapa": "New Stage",
  "Nova Organização": "New Organization",
  "Novo Upload": "New Upload",
  "Não definido": "Not defined",
  "Não encontramos nenhuma organização que corresponda aos seus termos de busca.":
    "We couldn't find any organization matching your search terms.",
  "Não foi possível carregar os dados de analytics. Tente novamente.":
    "Could not load analytics data. Please try again.",
  "O horizonte de leads está vazio": "The lead horizon is empty",
  "O nome da organização é obrigatório": "Organization name is required",
  Obrigatório: "Required",
  "Ocultar Post": "Hide Post",
  "Organização atualizada com sucesso!": "Organization updated successfully!",
  "Organização criada com sucesso!": "Organization created successfully!",
  "Origem / Campanha": "Source / Campaign",
  "Para configurar a integração, você precisará das credenciais do portal de desenvolvedor oficial.":
    "To configure the integration, you will need the credentials from the official developer portal.",
  "Parâmetros de Teste (Free Trial)": "Trial Parameters (Free Trial)",
  "Patrocinar no LinkedIn": "Sponsor on LinkedIn",
  Pausados: "Paused",
  "Pendente de Verificação": "Pending Verification",
  Perdido: "Lost",
  "Performance por Canal": "Performance by Channel",
  "Personalize as colunas do funil de vendas":
    "Customize the sales funnel columns",
  "Peça para a equipe Virtus adicionar sua página ao Business Suite.":
    "Ask the Virtus team to add your page to Business Suite.",
  "Planilha de Exemplo": "Sample Spreadsheet",
  "Plano Enterprise": "Enterprise Plan",
  "Plataforma: {name}": "Platform: {name}",
  "Possíveis Fraudes": "Possible Fraud",
  "Post #{id}": "Post #{id}",
  "Post contém imagem": "Post contains image",
  "Post contém vídeo": "Post contains video",
  "Post publicado": "Post published",
  "Postagem Orgânica": "Organic Post",
  Predições: "Predictions",
  "Processando...": "Processing...",
  "Prompt IA": "AI Prompt",
  "Pronto para envio": "Ready to send",
  "Pronto!": "Done!",
  "Prontos para conversão": "Ready for conversion",
  "Publicado: {date}": "Published: {date}",
  "Publicados / Total": "Published / Total",
  "Página '{name}' conectada!": "Page '{name}' connected!",
  "Página conectada via Virtus": "Page connected via Virtus",
  Qualificação: "Qualification",
  "ROI Geral": "Overall ROI",
  "ROI Pesquisa/Display": "Search/Display ROI",
  "ROI {value}x": "ROI {value}x",
  "Ranking de Performance": "Performance Ranking",
  "Re-Sincronizar CRM": "Re-Sync CRM",
  Real: "Actual",
  Rejeitado: "Rejected",
  "Rejeitado pelo cliente": "Rejected by client",
  "Relatório de Leads exportado!": "Lead Report exported!",
  "Remover Flag": "Remove Flag",
  "Remover Vídeo": "Remove Video",
  "Reportar Fraude": "Report Fraud",
  Reprocessar: "Reprocess",
  Republicar: "Republish",
  Restantes: "Remaining",
  "Resultado do Processamento": "Processing Result",
  "Resultados (Leads)": "Results (Leads)",
  "Retorno Médio": "Average Return",
  "Reverter para Pendente": "Revert to Pending",
  "Salvar Regras": "Save Rules",
  Search: "Search",
  "Selecionar →": "Select →",
  "Selecione a página para conectar:": "Select the page to connect:",
  "Selecione ou Arraste o Arquivo": "Select or Drag the File",
  "Selecione um arquivo .xlsx": "Select a .xlsx file",
  "Selecione...": "Select...",
  "Sem call to action": "No call to action",
  "Sem hashtags": "No hashtags",
  "Sem mídia": "No media",
  "Sem prÃ©via disponÃ­vel": "No preview available",
  "Seu navegador não suporta a tag de vídeo.":
    "Your browser does not support the video tag.",
  "Sim, Excluir!": "Yes, Delete!",
  "Sincronizado CRM": "CRM Synced",
  "Sincronizados CRM": "CRM Synced",
  "Sincronizar Dados": "Sync Data",
  "Sincronizar Métricas": "Sync Metrics",
  "Sincronizar com CRM": "Sync with CRM",
  "Sincronização de Audience (CAPI)": "Audience Sync (CAPI)",
  "Smart Resized": "Smart Resized",
  "Status & Qualificação": "Status & Qualification",
  "Super Admin: Organizações Globais": "Super Admin: Global Organizations",
  "Suporta apenas planilhas Excel estruturadas":
    "Only supports structured Excel spreadsheets",
  Sync: "Sync",
  "Sync CRM": "Sync CRM",
  "São listadas apenas páginas do Business Suite da Virtus. Selecione a página correta deste cliente. Se a página não aparecer, use os campos":
    "Only pages from Virtus' Business Suite are listed. Select the correct page for this client. If the page does not appear, use the fields",
  "Tag criada!": "Tag created!",
  Tags: "Tags",
  "Tags com Meta Audience sincronizam automaticamente ao marcar o lead":
    "Tags with Meta Audience automatically sync when the lead is marked",
  "Tem certeza que deseja desconectar o LinkedIn?":
    "Are you sure you want to disconnect LinkedIn?",
  "Tem certeza que deseja desconectar o Meta (Facebook/Instagram)?":
    "Are you sure you want to disconnect Meta (Facebook/Instagram)?",
  "Tem certeza que deseja desconectar o {platform}?":
    "Are you sure you want to disconnect {platform}?",
  Tendências: "Trends",
  "Termos Gerais": "General Terms",
  "Termos de Arquivo": "File Terms",
  Testar: "Test",
  "Token não encontrado. Faça login novamente.":
    "Token not found. Please log in again.",
  "Total de Conversões": "Total Conversions",
  "Total de Organizações": "Total Organizations",
  "Tráfego Pago (Anúncio)": "Paid Traffic (Ad)",
  "Turbinar com Meta Ads": "Boost with Meta Ads",
  "Tutorial TÃ©cnico": "Technical Tutorial",
  "URL/Site": "URL/Site",
  "Unidades Cadastradas": "Registered Units",
  "Unidades: {name}": "Units: {name}",
  "Use este modelo para garantir a importação sem erros.":
    "Use this template to ensure an error-free import.",
  "Validando...": "Validating...",
  Vazio: "Empty",
  "Ver Detalhes": "View Details",
  "Ver Dossiê": "View Dossier",
  "Ver Dossiê Completo": "View Full Dossier",
  "Ver Leads ({count})": "View Leads ({count})",
  "Ver Todas": "View All",
  "Ver Variações Sociais": "View Social Variations",
  "Ver imagem em tela cheia": "View image in full screen",
  "Ver mais": "See more",
  "Ver menos": "See less",
  "Ver no LinkedIn": "View on LinkedIn",
  Visualizar: "View",
  "Visualizar e Editar": "View and Edit",
  "Visualização da Imagem": "Image Preview",
  "Visualize, gerencie e sincronize leads gerados pelas suas campanhas pagas no Facebook, Instagram e Google Ads.":
    "View, manage, and sync leads generated by your paid campaigns on Facebook, Instagram, and Google Ads.",
  "Visão Geral": "Overview",
  "Visão mestre de todas as organizações (contas principais) cadastradas no sistema.":
    "Master view of all organizations (main accounts) registered in the system.",
  "Vídeo na galeria": "Video in gallery",
  "Vídeo não disponível": "Video not available",
  Vídeos: "Videos",
  "acima para configurar manualmente.": "above to configure manually.",
  capturas: "captures",
  e: "and",
  padrão: "default",
  "{count} Campanhas": "{count} Campaigns",
  "{count} Campanhas Ativas": "{count} Active Campaigns",
  "{count} Consumidos": "{count} Consumed",
  "{count} Créditos": "{count} Credits",
  "{count} Downloads": "{count} Downloads",
  "{count} Leads": "{count} Leads",
  "{count} campanha(s)": "{count} campaign(s)",
  "{count} dias": "{count} days",
  "{count} dias de uso": "{count} days of use",
  "{count} lead(s) importado(s). Use para CAPI, Lookalike ou Customer Match.":
    "{count} lead(s) imported. Use for CAPI, Lookalike, or Customer Match.",
  "{count} leads excluídos com sucesso!": "{count} leads successfully deleted!",
  "{count} leads · {stages} etapas": "{count} leads · {stages} stages",
  "{count} local(is) · 90 dias": "{count} location(s) · 90 days",
  "{count} selecionados": "{count} selected",
  "{day} às {time}": "{day} at {time}",
  "{hint} \n\n(Campo de Sistema: Deixe em branco para usar as credenciais automáticas Globais da Plataforma).":
    "{hint} \n\n(System Field: Leave blank to use the Platform's automatic Global credentials).",
  "Última Sincronização:": "Last Sync:",

  // AdsManager
  "Configure sua estratégia de anúncios para Meta, Google e LinkedIn em um único fluxo assistido por IA.":
    "Configure your ad strategy for Meta, Google, and LinkedIn in a single AI-assisted flow.",
  "Isso resetará todos os dados preenchidos até agora neste fluxo.":
    "This will reset all data filled in so far in this flow.",
  "Sua segmentação está validada. Defina o investimento final para ativar as campanhas.":
    "Your targeting is validated. Set the final investment to activate campaigns.",
  "Ready for Launch": "Ready for Launch",
  "Ready for Injection": "Ready for Injection",
  Budget: "Budget",
  Live: "Live",
  "Customer Match": "Customer Match",
  "Google Trends": "Google Trends",
  "YouTube Video": "YouTube Video",
  "FB & Instagram": "FB & Instagram",
  "Link wa.me": "wa.me link",
  BRASIL: "BRAZIL",
  "{count} ESTADOS": "{count} STATES",
  "Onde seu anúncio vai aparecer": "Where your ad will appear",
  "O que o usuário vai ver": "What the user will see",
  Público: "Audience",
  "Quem deve ser impactado": "Who should be reached",
  "Configuração do formulário": "Form configuration",
  Revisão: "Review",
  "Confirmação final": "Final confirmation",
  "Público Personalizado (CRM)": "Custom Audience (CRM)",
  "Olá! Tenho interesse.": "Hello! I am interested.",
  "Rascunho Sem Nome": "Untitled Draft",
  Cadastrar: "Sign Up",
  "Candidatar-se": "Apply",
  Baixar: "Download",
  "Solicitar Orçamento": "Get Quote",
  "Campanha lançada na plataforma, mas o status local não foi atualizado. Tente novamente em instantes.":
    "Campaign launched on the platform, but local status was not updated. Try again shortly.",
  "▶ Cole o URN do formulário criado no Campaign Manager":
    "▶ Paste the form URN created in Campaign Manager",
  "Ou cole o URN de um formulário existente (LinkedIn Campaign Manager)":
    "Or paste the URN of an existing form (LinkedIn Campaign Manager)",
  "* A Meta exige um link válido de política de privacidade para exibir formulários de leads.":
    "* Meta requires a valid privacy policy link to display lead forms.",
  "Não encontrou a lista? Você pode gerar uma nova agora mesmo com seus leads qualificados:":
    "Could not find the list? You can generate a new one now with your qualified leads:",
  "Nenhum Público Personalizado encontrado. Crie um no menu Leads ou através do botão abaixo.":
    "No Custom Audience found. Create one in the Leads menu or via the button below.",
  "Encontre pessoas semelhantes aos seus clientes atuais integrados via CRM.":
    "Find people similar to your current customers integrated via CRM.",
  "Sincronize a sua lista atual de clientes diretamente com a Meta para direcionar anúncios a eles.":
    "Sync your current customer list directly with Meta to target ads to them.",
  "Adicione links extras para aumentar a taxa de clique (CTR)":
    "Add extra links to increase click-through rate (CTR)",
  "Use um post já aprovado para automatizar o preenchimento.":
    "Use an already approved post to auto-fill the form.",
  "O Google recomenda usar múltiplos vídeos para teste A/B e melhor performance.":
    "Google recommends using multiple videos for A/B testing and better performance.",
  "Descrição 1 (ex: Atendimento rápido via WhatsApp)":
    "Description 1 (e.g.: Fast support via WhatsApp)",
  "Descrição 2 (ex: Tire suas dúvidas agora mesmo)":
    "Description 2 (e.g.: Get your questions answered now)",
  "Número completo (DDI + DDD + Número) que receberá o lead.":
    "Full number (country + area + number) that will receive the lead.",
  "Texto que virá escrito quando o usuário abrir o chat com você.":
    "Text pre-filled when the user opens a chat with you.",
  "O anúncio utilizará o botão nativo do WhatsApp e objetivo de Mensagens.":
    "The ad will use the native WhatsApp button and Messages objective.",
  "O link wa.me será gerado automaticamente com base nos valores informados acima.":
    "The wa.me link will be generated automatically based on the values above.",
  "Sincronize sua estratégia com dados históricos para otimizar o alcance.":
    "Sync your strategy with historical data to optimize reach.",
  "Os resultados exibidos refletem o investimento e os principais resultados relativos a **{duration} dias**. Os resultados previstos são uma estimativa de tendência e não garantem o desempenho.":
    "The results shown reflect the investment and key results over **{duration} days**. Predicted results are a trend estimate and do not guarantee performance.",
  "Adicione pelo menos 1 palavra-chave para analisar no Google Trends.":
    "Add at least 1 keyword to analyze in Google Trends.",
  "Nenhum trigger de captura personalizada configurado para o Leadflow.":
    "No custom capture triggers configured for Leadflow.",
  "Link da Política de Privacidade (Obrigatório pela Meta)":
    "Privacy Policy Link (Required by Meta)",
  "Ao confirmar, os dados serão sincronizados com o Gerenciador de Anúncios. Certifique-se de que sua conta de anúncios possui um método de pagamento válido cadastrado no":
    "By confirming, data will be synced with Ads Manager. Make sure your ad account has a valid payment method registered in",
  "Sua estratégia foi validada pela IA e todos os criativos estão em conformidade com as diretrizes de mídia paga.":
    "Your strategy was validated by AI and all creatives comply with paid media guidelines.",
  "Não foi possível carregar a imagem. Verifique o post original.":
    "Could not load the image. Check the original post.",
  "Selecione um local para visualizar a área de cobertura no mapa interativo.":
    "Select a location to view the coverage area on the interactive map.",
  "Sua campanha será veiculada entre {startTime} de {startDate} e {endTime} de {endDate}.":
    "Your campaign will run between {startTime} on {startDate} and {endTime} on {endDate}.",
  "Adicionados à URL de destino do anúncio. Deixe vazio para usar UTMs padrão.":
    "Added to the ad destination URL. Leave empty to use default UTMs.",
  "Nenhuma conta encontrada. Verifique sua conexão LinkedIn.":
    "No account found. Check your LinkedIn connection.",
  "TikTok Ads Manager": "TikTok Ads Manager",
  "Anúncio otimizado pela IA com base no post!":
    "Ad optimized by AI based on the post!",
  "Erro ao buscar sugestão de IA": "Error fetching AI suggestion",
  "Sugestão aplicada!": "Suggestion applied!",
  "Keywords otimizadas!": "Keywords optimized!",
  "Não foi possível otimizar as keywords.": "Could not optimize keywords.",
  "Lookalike criada com sucesso!": "Lookalike created successfully!",
  "Erro ao criar Lookalike": "Error creating Lookalike",
  "Público Personalizado criado com sucesso!":
    "Custom Audience created successfully!",
  "Erro ao criar Público": "Error creating audience",
  "Customer Match sincronizada!": "Customer Match synced!",
  "Erro ao criar Customer Match": "Error creating Customer Match",
  "Conhecimento de Marca": "Brand Awareness",
  "Geração de Leads": "Lead Generation",
  Tráfego: "Traffic",
  "Lead via WhatsApp": "Lead via WhatsApp",
  "Lead via Formulário": "Lead via Form",
  "Tráfego Site": "Website Traffic",
  "Verba Diária": "Daily Budget",
  "Invest. Total ({days}d)": "Total Invest. ({days}d)",
  "Revisar e Lançar": "Review and Launch",
  "Lançar Agora": "Launch Now",
  "Orçamento diário mínimo para Meta: R$ {min}.":
    "Minimum daily budget for Meta: R$ {min}.",
  "Selecione um post aprovado.": "Select an approved post.",
  "Erro ao criar formulário LinkedIn": "Error creating LinkedIn form",
  "Falha ao atualizar status local da campanha.":
    "Failed to update local campaign status.",
  "Campanha lançada com aviso: {warning}":
    "Campaign launched with warning: {warning}",
  "Campanha lançada com sucesso!": "Campaign launched successfully!",
  "Erro no lançamento": "Launch error",
  "Erro ao lançar campanha": "Error launching campaign",
  "Rascunho salvo com sucesso!": "Draft saved successfully!",
  "Erro ao salvar rascunho": "Error saving draft",
  "Rascunho carregado com sucesso!": "Draft loaded successfully!",
  "Não foi possível carregar o rascunho.": "Could not load the draft.",
  "Lançamento de Campanha Unificado": "Unified Campaign Launch",
  "Rascunho ID: #{draftId}": "Draft ID: #{draftId}",
  "Limpar campos?": "Clear fields?",
  "Sim, limpar tudo": "Yes, clear all",
  "Manter dados": "Keep data",
  "Limpar Tudo": "Clear All",
  "Salvar Rascunho": "Save Draft",
  "Próximo Passo": "Next Step",
  "Lançando...": "Launching...",
  "Canal de veiculação da campanha": "Campaign delivery channel",
  "Busca & YouTube": "Search & YouTube",
  "Vídeos Curtos": "Short Videos",
  "Profissional • LinkedIn Ads Integration":
    "Professional • LinkedIn Ads Integration",
  "Configurações Gerais": "General Settings",
  "Nome da Campanha": "Campaign Name",
  "Ex: Campanha de Leads - Março 2024": "E.g.: Lead Campaign - March 2024",
  "Nome da Empresa (Google Ads)": "Business Name (Google Ads)",
  "Ex: Minha Empresa Ltda": "E.g.: My Company Ltd",
  "Este nome aparecerá nos seus anúncios do Google.":
    "This name will appear in your Google ads.",
  "Objetivo no LinkedIn": "LinkedIn Objective",
  "Conhecimento da marca": "Brand awareness",
  "Destino / Ação Principal": "Destination / Primary Action",
  "Tráfego para Site (URL)": "Traffic to Website (URL)",
  "Conversão (WhatsApp)": "Conversion (WhatsApp)",
  "Geração de Leads (Formulário)": "Lead Generation (Form)",
  "Plataforma de Entrega": "Delivery Platform",
  "Ambos (FB + IG) (Recomendado)": "Both (FB + IG) (Recommended)",
  "Somente Facebook": "Facebook Only",
  "Somente Instagram": "Instagram Only",
  "Tipo de Campanha Google": "Google Campaign Type",
  "Rede de Pesquisa": "Search Network",
  "Rede de Display": "Display Network",
  "Performance Max (IA)": "Performance Max (AI)",
  "Link Direto (wa.me)": "Direct Link (wa.me)",
  "Mais compatível (pessoal ou business)":
    "Most compatible (personal or business)",
  "WhatsApp Oficial": "Official WhatsApp",
  "Exige Meta Business Manager": "Requires Meta Business Manager",
  "Número do WhatsApp": "WhatsApp Number",
  "Mensagem Pré-definida (Opcional)": "Pre-defined Message (Optional)",
  "Olá! Quero mais informações...": "Hello! I'd like more information...",
  "Criação do Anúncio": "Ad Creation",
  "Configure o criativo e as mensagens principais":
    "Configure the creative and main messages",
  "Atualizar Hub": "Refresh Hub",
  "Importar Conteúdo Validado": "Import Validated Content",
  "Escrita Manual (Do Zero)": "Manual Writing (From Scratch)",
  "[AO VIVO]": "[LIVE]",
  "[VALIDADO]": "[VALIDATED]",
  "Legenda:": "Legend:",
  "Somente Texto": "Text Only",
  "Erro / Expirado": "Error / Expired",
  "Restaurar do Post Selecionado": "Restore from Selected Post",
  "Copy (Legenda Principal)": "Copy (Main Caption)",
  "Descrição do Snippet Google": "Google Snippet Description",
  "Copy da campanha.": "Campaign copy.",
  "Digite a oferta irresistível aqui...": "Type the irresistible offer here...",
  "URL / Link de Destino": "URL / Destination Link",
  "Título de Destaque (Headline)": "Highlight Title (Headline)",
  "Máximo 30 caracteres para Google Ads":
    "Maximum 30 characters for Google Ads",
  "Título impactante ao lado do botão.": "Impactful title next to the button.",
  "Ex: Oferta Exclusiva 50% OFF": "E.g.: Exclusive 50% OFF Offer",
  "Subtítulo Descritivo": "Descriptive Subtitle",
  "Máximo 90 caracteres para Google Ads":
    "Maximum 90 characters for Google Ads",
  "Mais contexto para o lead...": "More context for the lead...",
  "Link da Imagem (PMax/Display)": "Image Link (PMax/Display)",
  "Vídeos do YouTube (Opcional)": "YouTube Videos (Optional)",
  "Visual do Anúncio": "Ad Visual",
  "Imagens do Post ({count})": "Post Images ({count})",
  "Sitelinks do Google (Opcional)": "Google Sitelinks (Optional)",
  "Insira uma URL de destino primeiro.": "Enter a destination URL first.",
  "Sitelinks sugeridos com sucesso!": "Sitelinks suggested successfully!",
  "Erro ao obter sugestões.": "Error getting suggestions.",
  "Sugerindo...": "Suggesting...",
  "Sugerir com IA": "Suggest with AI",
  "Sitelink #{index}": "Sitelink #{index}",
  "Título (ex: Fale Conosco)": "Title (e.g.: Contact Us)",
  "URL Final (https://...)": "Final URL (https://...)",
  "Sugestão de Público": "Audience Suggestion",
  "Baseado no objetivo da campanha": "Based on campaign objective",
  "Idade Recomendada": "Recommended Age",
  ANOS: "YEARS",
  "Perfil Alvo": "Target Profile",
  Homens: "Men",
  Mulheres: "Women",
  "Estrutura Sugerida": "Suggested Structure",
  "{count} Segmentos de Interesse": "{count} Interest Segments",
  "{count} Palavras-chave": "{count} Keywords",
  "Análise da IA": "AI Analysis",
  "Aplicar Sugestões": "Apply Suggestions",
  "Gerar Sugestões": "Generate Suggestions",
  Online: "Online",
  "Sync: 100%": "Sync: 100%",
  "Resultados previstos": "Predicted results",
  "Tamanho do público": "Audience size",
  "Calculando...": "Calculating...",
  "Saiba mais": "Learn more",
  "Sua Marca": "Your Brand",
  "Visual Placeholder": "Visual Placeholder",
  Patrocinado: "Sponsored",
  "Seu texto principal aparecerá aqui...": "Your main text will appear here...",
  "SITE DA MARCA": "BRAND WEBSITE",
  "Seu Título Aqui": "Your Title Here",
  "12 coment. • 8 partilh.": "12 comments • 8 shares",
  Curtir: "Like",
  Comentar: "Comment",
  "Sua legenda...": "Your caption...",
  "Anúncio de Pesquisa": "Search Ad",
  "Seu Título Impactante do Google": "Your Impactful Google Title",
  "Sua descrição de anúncio Google...": "Your Google ad description...",
  "e mais": "and more",
  "{count} conexões": "{count} connections",
  "seguem esta Page": "follow this Page",
  "{count} seguidores": "{count} followers",
  Promovida: "Promoted",
  "Seu texto do post aparecerá aqui...": "Your post text will appear here...",
  "Filtre Interessados Reais": "Filter Real Prospects",
  "Formulário de Contato Inteligente": "Smart Contact Form",
  "Soluções de Automação para Negócios": "Business Automation Solutions",
  "Investimento e Datas": "Investment and Dates",
  "Investimento Diário (BRL)": "Daily Investment (BRL)",
  "Estimativa de Alcance": "Reach Estimate",
  "Saúde da Verba: Adequada": "Budget Health: Adequate",
  "Mínimo sugerido: R$ 20,00/dia": "Suggested minimum: R$ 20.00/day",
  "Cronograma da Campanha": "Campaign Schedule",
  "Período de Veiculação": "Delivery Period",
  "Parâmetros de Rastreamento": "Tracking Parameters",
  "URL Tracking (UTM)": "URL Tracking (UTM)",
  "Validação do Orçamento": "Budget Validation",
  "Período total de {days} dias.": "Total period of {days} days.",
  Segmentação: "Targeting",
  "Demografia e Comportamentos": "Demographics and Behaviors",
  "Filtros Base": "Base Filters",
  "Idade Mínima": "Minimum Age",
  "Idade Máxima": "Maximum Age",
  "Idioma do Perfil (LinkedIn)": "Profile Language (LinkedIn)",
  "Ex: Português, Inglês...": "E.g.: Portuguese, English...",
  "Segmentação Detalhada (Interesses)": "Detailed Targeting (Interests)",
  "Pesquisar interesses na Meta...": "Search interests on Meta...",
  Interesse: "Interest",
  "Busque interesses acima para segmentar seu público.":
    "Search interests above to target your audience.",
  "Segmentação Profissional (LinkedIn)": "Professional Targeting (LinkedIn)",
  "Localidade (LinkedIn)": "Location (LinkedIn)",
  "Ex: Brasil, São Paulo...": "E.g.: Brazil, São Paulo...",
  "Cargos (Job Titles)": "Job Titles",
  "Ex: Gerente de Marketing...": "E.g.: Marketing Manager...",
  "Setores (Industries)": "Industries",
  "Ex: Tecnologia, Varejo...": "E.g.: Technology, Retail...",
  "Nível de Experiência (Seniorities)": "Experience Level (Seniorities)",
  "Ex: Sênior, Diretor...": "E.g.: Senior, Director...",
  "Tamanho da Empresa (Company Sizes)": "Company Size (Company Sizes)",
  "Ex: 51-200 empregados...": "E.g.: 51-200 employees...",
  "Habilidades (Skills)": "Skills",
  "Ex: Marketing Digital, Python...": "E.g.: Digital Marketing, Python...",
  "Funções (Job Functions)": "Job Functions",
  "Ex: Marketing, Vendas...": "E.g.: Marketing, Sales...",
  "Audiências Correspondentes (Matched Audiences)": "Matched Audiences",
  "Carregando audiências...": "Loading audiences...",
  "Nenhuma audiência disponível nesta conta.":
    "No audiences available in this account.",
  "Palavras-chave": "Keywords",
  "Capturar novas keywords...": "Capture new keywords...",
  "Nenhuma palavra-chave incluída.": "No keywords included.",
  "Conta de Anúncios LinkedIn": "LinkedIn Ad Account",
  "Selecione a Conta para Veiculação": "Select Account for Delivery",
  "Públicos Personalizados (CRM)": "Custom Audiences (CRM)",
  "Público Lookalike": "Lookalike Audience",
  "Fonte da Semente (Seed)": "Seed Source",
  "Leads Qualificados (Virtus)": "Qualified Leads (Virtus)",
  "Eventos do Pixel (Meta)": "Pixel Events (Meta)",
  "Precisão (Lookalike %)": "Precision (Lookalike %)",
  "Sincronizar e Gerar Lookalike": "Sync and Generate Lookalike",
  "Público Personalizado (Apenas Lista)": "Custom Audience (List Only)",
  "Selecione um Público Personalizado": "Select a Custom Audience",
  "Assinar Termos da Meta": "Accept Meta Terms",
  "Atualizar Listas": "Refresh Lists",
  "Carregando seus públicos da Meta...": "Loading your Meta audiences...",
  "aprox. {count} pessoas": "approx. {count} people",
  "tamanho indisponível": "size unavailable",
  "Nome do Novo Público": "New Audience Name",
  "Gerar Nova Lista": "Generate New List",
  "Integração direta com sua base de dados Virtus habilitada.":
    "Direct integration with your Virtus database enabled.",
  "Origem dos Dados": "Data Source",
  "Todos os Leads (Virtus)": "All Leads (Virtus)",
  "Leads Convertidos (CRM)": "Converted Leads (CRM)",
  "Sincronizar com Google Ads": "Sync with Google Ads",
  "Nome do formulário é obrigatório.": "Form name is required.",
  "URL da política de privacidade é obrigatória (seção acima).":
    "Privacy policy URL is required (section above).",
  "Erro ao criar formulário.": "Error creating form.",
  "Erro de conexão ao criar formulário.": "Connection error creating form.",
  "Captura & Dataset": "Capture & Dataset",
  "Protocolo de Sincronização": "Sync Protocol",
  "{count} CAMPOS ATIVOS": "{count} ACTIVE FIELDS",
  "Perguntas Transacionais (Custom)": "Transactional Questions (Custom)",
  "Adicionar Pergunta IA": "Add AI Question",
  "Enunciado da Pergunta de Captação": "Capture Question Text",
  "Ex: Qual o seu maior desafio hoje?":
    "E.g.: What is your biggest challenge today?",
  "Formato de Resposta Técnica": "Technical Response Format",
  "Texto Livre (TOFU)": "Free Text (TOFU)",
  "E-mail Validado (Lead)": "Validated Email (Lead)",
  "Telefone (WhatsApp)": "Phone (WhatsApp)",
  "Quantificador (Numérico)": "Quantifier (Numeric)",
  "Conformidade & Legal": "Compliance & Legal",
  "Formulário do LinkedIn": "LinkedIn Form",
  "Formulário vinculado": "Form linked",
  "Configure e crie o formulário abaixo": "Configure and create the form below",
  "Formulário Vinculado": "Linked Form",
  "Formulário selecionado": "Selected form",
  Trocar: "Change",
  "Ou selecionar outro formulário existente": "Or select another existing form",
  "Selecione um formulário...": "Select a form...",
  "Nome do Formulário *": "Form Name *",
  "Ex: Formulário de Contato": "E.g.: Contact Form",
  "CTA (Chamada para Ação) *": "CTA (Call to Action) *",
  "Título (opcional)": "Title (optional)",
  "Ex: Fale conosco": "E.g.: Contact us",
  "Descrição (opcional)": "Description (optional)",
  "Ex: Preencha seus dados abaixo": "E.g.: Fill in your details below",
  "Campos incluídos": "Included fields",
  "EMAIL, FIRST_NAME, LAST_NAME (padrão)":
    "EMAIL, FIRST_NAME, LAST_NAME (default)",
  "+ {count} pergunta(s)": "+ {count} question(s)",
  "Criar e Vincular via API": "Create and Link via API",
  "Criação automática indisponível": "Automatic creation unavailable",
  "Abrir LinkedIn Campaign Manager →": "Open LinkedIn Campaign Manager →",
  "Configurações Básicas": "Basic Settings",
  "FB + IG": "FB + IG",
  "Somente FB": "FB Only",
  "Somente IG": "IG Only",
  "Botão (CTA)": "Button (CTA)",
  "Saiba Mais": "Learn More",
  Destino: "Destination",
  "API Oficial": "Official API",
  "Formulário Interno": "Internal Form",
  "Público & Alcance": "Audience & Reach",
  "Segmentação por Idade": "Age Targeting",
  "{min}-{max} anos": "{min}-{max} years",
  "Segmentos de Interesse": "Interest Segments",
  "{count} keywords": "{count} keywords",
  Amplo: "Broad",
  "{count} interesses": "{count} interests",
  "Audiência Avançada": "Advanced Audience",
  "Ativada (Lookalike/Match)": "Enabled (Lookalike/Match)",
  Desativada: "Disabled",
  "Orçamento & Período": "Budget & Period",
  Duração: "Duration",
  "{days} dias": "{days} days",
  "Sitelinks (Google)": "Sitelinks (Google)",
  "Sem desc.": "No desc.",
  "Vídeos (YouTube)": "Videos (YouTube)",
  "Vídeo #{index}": "Video #{index}",
  "Resumo Tático": "Tactical Summary",
  "Revisão final dos sistemas antes da decolagem.":
    "Final systems review before launch.",
  "Verba Total (Estimado)": "Total Budget (Estimated)",
  "Preview Criativo": "Creative Preview",
  "Erro de Mídia": "Media Error",
  "Sem Mídia Anexada": "No Media Attached",
  "Veiculação Somente Texto": "Text-Only Delivery",
  "ID: {theme}": "ID: {theme}",
  "Post Selecionado": "Selected Post",
  "Conteúdo original da biblioteca.": "Original content from the library.",
  "Localização & Tags": "Location & Tags",
  "Locais Ativos ({count})": "Active Locations ({count})",
  "Ponto {index}": "Point {index}",
  "Nacional (Brasil)": "National (Brazil)",
  "Campos de Coleta": "Collection Fields",
  "Política de Lançamento Ativa": "Active Launch Policy",
  "Tudo certo para lançar, {name}?": "All set to launch, {name}?",
  parceiro: "partner",
  "LANÇAR CAMPANHA AGORA": "LAUNCH CAMPAIGN NOW",
  Locais: "Locations",
  "Brasil inteiro selecionado": "All of Brazil selected",
  "{count} estado(s) selecionado(s)": "{count} state(s) selected",
  "{count} seleções selecionadas": "{count} selections selected",
  "Endereço / Raio": "Address / Radius",
  "Estado ou País": "State or Country",
  "Buscar endereços ou bairros": "Search addresses or neighborhoods",
  "Localização Atual": "Current Location",
  "Minha Localização": "My Location",
  "Usar minha localização": "Use my location",
  "Ex: Pinheiros, São Paulo - SP...": "E.g.: Pinheiros, São Paulo - SP...",
  "Raio de Alcance Padrão": "Default Reach Radius",
  "Brasil inteiro": "All of Brazil",
  "Todo o território nacional": "Entire national territory",
  "ou selecione estados específicos": "or select specific states",
  "Filtrar estados...": "Filter states...",
  Sintonizado: "Tuned",
  "Alcance Geográfico": "Geographic Reach",
  "Estratégia Sincronizada": "Synchronized Strategy",
  "Cadastrar-se": "Sign Up",
  "Formulário de Contato": "Contact Form",
  Vídeo: "Video",
  "Em breve": "Coming soon",
  // Modals i18n
  "#exemplo #outra #hashtag": "#example #another #hashtag",
  "(Detalhes: {details})": "(Details: {details})",
  "(análise simplificada)": "(simplified analysis)",
  "162 conexões": "162 connections",
  "2º": "2nd",
  "409 seguidores": "409 followers",
  "45 coment. • 12 partilh.": "45 comments • 12 shares",
  "580.000+": "580,000+",
  'A IA analisará o conteúdo do post e sugerirá conceitos visuais, paleta de cores, elementos e prompts por plataforma. Clique em "Gerar sugestões" para começar.':
    'AI will analyze the post content and suggest visual concepts, color palettes, elements and prompts per platform. Click "Generate suggestions" to get started.',
  "A IA está criando sua nova arte...": "AI is creating your new artwork...",
  "A IA está pintando seu prompt...": "AI is painting your prompt...",
  "A IA redimensionou sua imagem para os formatos ideais de cada rede":
    "AI resized your image to the ideal formats for each network",
  "A IA retornou um formato de resposta vazio.":
    "AI returned an empty response format.",
  "A imagem atual do post será usada como referência. A IA irá melhorá-la com base no conteúdo e nas instruções abaixo.":
    "The current post image will be used as reference. AI will enhance it based on the content and instructions below.",
  "Acessar Site": "Visit Site",
  "Add Arquivos": "Add Files",
  "Add Mídia": "Add Media",
  "Adicionar Mais Sitelinks": "Add More Sitelinks",
  "Adicionar Texto": "Add Text",
  "Adicionar palavra-chave...": "Add keyword...",
  "Adicione ao menos uma mídia ou texto": "Add at least one media item or text",
  "Adicione conteúdo à sua biblioteca para gerar posts com IA":
    "Add content to your library to generate posts with AI",
  "Agendando...": "Scheduling...",
  "Agendar nesta rede": "Schedule on this network",
  "Agente de Hooks": "Hooks Agent",
  "Agora mesmo": "Just now",
  "Aguarde 15s": "Wait 15s",
  "Ajustar Segmentação Avançada B2B": "Adjust Advanced B2B Targeting",
  "Ajuste o conteúdo, hashtags e configurações do seu post":
    "Adjust your post content, hashtags and settings",
  "Alcance mais pessoas com sua publicação": "Reach more people with your post",
  "Alcançar perfis similares": "Reach similar profiles",
  "Alterar Categoria": "Change Category",
  "Analisando Bases de Conhecimento e DNA da Empresa...":
    "Analyzing Knowledge Bases and Company DNA...",
  "Ao cadastrar uma nova unidade, você poderá configurar as credenciais de redes sociais e canais de atendimento específicos para ela logo em seguida.":
    "When registering a new unit, you can configure social media credentials and support channels specific to it right away.",
  "Ao turbinar esta publicação, ela será enviada para aprovação na Meta. Sua conta de anúncios será cobrada conforme o orçamento definido. As UTMs de rastreamento profissional serão inseridas automaticamente em todos os links.":
    "When boosting this post, it will be sent for Meta approval. Your ad account will be charged according to the defined budget. Professional tracking UTMs will be automatically added to all links.",
  Arquivado: "Archived",
  "Arquivo Base": "Base File",
  "Arquivo muito grande ({size}MB). Tamanho máximo: 25MB":
    "File too large ({size}MB). Maximum size: 25MB",
  "Arquivo muito grande ({size}MB). Tamanho máximo: {max}MB.":
    "File too large ({size}MB). Maximum size: {max}MB.",
  "As alterações serão aplicadas ao reprocessar o vídeo":
    "Changes will be applied when reprocessing the video",
  "As hashtags do post serão usadas automaticamente nos metadados do vídeo.":
    "Post hashtags will be automatically used in the video metadata.",
  "Ativando...": "Activating...",
  "Ativar Campanha": "Activate Campaign",
  "Ativo na Biblioteca": "Active in Library",
  "Audience Network": "Audience Network",
  "Aumente o engajamento social e o número de seguidores do perfil":
    "Increase social engagement and profile followers",
  "Azul corporativo": "Corporate blue",
  "B2B (Empresas)": "B2B (Businesses)",
  "B2B (Enterprise / Pessoas de Negócios)":
    "B2B (Enterprise / Business People)",
  "B2C (Consumidor / Varejo)": "B2C (Consumer / Retail)",
  "B2C (Consumidor Final)": "B2C (End Consumer)",
  "Background (opcional)": "Background (optional)",
  "Baixar imagem": "Download image",
  "Baixo Centro": "Bottom Center",
  "Baixo Direita": "Bottom Right",
  "Baixo Esquerda": "Bottom Left",
  "Biblioteca Digital": "Digital Library",
  Branco: "White",
  "Buscar música ou artista...": "Search song or artist...",
  "Cadastrado em": "Registered on",
  "Cadastrar Empresa": "Register Company",
  "Call to Action": "Call to Action",
  "Call to Action (CTA)": "Call to Action (CTA)",
  "Canais Habilitados para Geração": "Channels Enabled for Generation",
  "Canais de Destino": "Destination Channels",
  "Canais de Distribuição": "Distribution Channels",
  "Cargos Profissionais": "Professional Roles",
  "Cargos:": "Roles:",
  "Carregando contas...": "Loading accounts...",
  "Carregando formulários...": "Loading forms...",
  "Carrossel • Álbum • Múltiplos Arquivos": "Carousel • Album • Multiple Files",
  Centro: "Center",
  "Chamada para Ação (CTA)": "Call to Action (CTA)",
  Cinza: "Gray",
  Classificação: "Classification",
  "Clique abaixo para agendar este post especificamente nesta rede.":
    "Click below to schedule this post specifically on this network.",
  'Clique em "Gerar Criativo" para ver o resultado':
    'Click "Generate Creative" to see the result',
  "Clique para adicionar imagens de referência":
    "Click to add reference images",
  "Clique para adicionar logo": "Click to add logo",
  "Clique para selecionar": "Click to select",
  "Comandos de IA": "AI Commands",
  Compartilhar: "Share",
  "Compondo textos sobre o fundo…": "Composing text over the background…",
  "Conceito visual": "Visual concept",
  "Confiança: {value}%": "Confidence: {value}%",
  "Configuração Avançada de Meta Ads": "Advanced Meta Ads Configuration",
  "Confirmar Agendamento": "Confirm Scheduling",
  "Confirmar Formatos": "Confirm Formats",
  "Confirmar Publicação": "Confirm Publishing",
  "Confirmar Rejeição": "Confirm Rejection",
  "Confirmar Turbinamento": "Confirm Boost",
  "Confirmação de Agendamento": "Scheduling Confirmation",
  "Conta de Anúncios": "Ad Account",
  "Conteúdo Gerado por IA": "AI Generated Content",
  "Conteúdo Textual": "Text Content",
  "Conteúdo do Post": "Post Content",
  "Conteúdo viral gerado com sucesso!": "Viral content generated successfully!",
  "Conteúdo é obrigatório": "Content is required",
  "Corpo (Body)": "Body",
  "Corpo do Texto": "Text Body",
  'Corrija a pronúncia de palavras específicas. Exemplo: "marketing" → "márqueting"':
    'Fix pronunciation of specific words. Example: "marketing" → "márqueting"',
  "Criador de Arte IA": "AI Art Creator",
  "Criador de Vídeo IA": "AI Video Creator",
  "Criando Unidade...": "Creating Unit...",
  "Criar Novo Post": "Create New Post",
  "Criar Posts": "Create Posts",
  "Criar imagem criativa com texto sobreposto para campanha":
    "Create creative image with overlaid text for campaign",
  "Criar/Editar público": "Create/Edit audience",
  Criativo: "Creative",
  "Criativo gerado": "Creative generated",
  "Crie sua organização antes de cadastrar empresas clientes.":
    "Create your organization before registering client companies.",
  "Crie, agende e direcione publicações para suas redes sociais com IA":
    "Create, schedule and direct posts to your social networks with AI",
  "OpenAI (GPT-5.5): geração de imagens de alta qualidade":
    "OpenAI (GPT-5.5): high-quality image generation",
  "DNA Aplicado": "DNA Applied",
  "Dados da Unidade": "Unit Data",
  "Dados do Registro": "Record Data",
  "Dark Mode": "Dark Mode",
  "Data de Entrada": "Entry Date",
  "Data de Envio": "Send Date",
  "Deep Research Viral": "Viral Deep Research",
  "Defina o momento perfeito para seu post ir ao ar":
    "Set the perfect time for your post to go live",
  "Definir Data e Hora": "Set Date and Time",
  "Descreva a imagem desejada...": "Describe the desired image...",
  "Descrição 1 (Máx 35)": "Description 1 (Max 35)",
  "Descrição 2 (Máx 35)": "Description 2 (Max 35)",
  "Descrição do fundo (opcional)": "Background description (optional)",
  "Desktop View": "Desktop View",
  "Destaque o principal benefício em uma frase.":
    "Highlight the main benefit in one sentence.",
  "Detalhes do Conteúdo": "Content Details",
  "Dica de Performance": "Performance Tip",
  "Dica:": "Tip:",
  "Dicas:": "Tips:",
  "Digite a legenda ou descrição do vídeo...":
    "Enter the video caption or description...",
  "Digite e pressione Enter...": "Type and press Enter...",
  "Digite o conteúdo do post antes de gerar a imagem":
    "Enter post content before generating the image",
  "Digite ou edite o texto que será narrado no vídeo...":
    "Type or edit the text that will be narrated in the video...",
  "Digite para buscar músicas": "Type to search songs",
  "Digite um prompt para gerar a imagem":
    "Enter a prompt to generate the image",
  Disponibilidade: "Availability",
  "Distribuição Sugerida": "Suggested Distribution",
  "Documento (PDF, DOCX, TXT)": "Document (PDF, DOCX, TXT)",
  "Editar Post #{postId}": "Edit Post #{postId}",
  'Edite o texto acima e clique em "Regenerar Áudio" para criar um novo áudio com as correções.':
    'Edit the text above and click "Regenerate Audio" to create new audio with the corrections.',
  "Edite o texto para corrigir o conteúdo da narração. Use a seção abaixo para corrigir pronúncias específicas.":
    "Edit the text to fix the narration content. Use the section below to fix specific pronunciations.",
  "Editor de Criativo de Campanha": "Campaign Creative Editor",
  "Editor de Vídeo - Post #{postId}": "Video Editor - Post #{postId}",
  "Elementos visuais": "Visual elements",
  "Empresa cliente cadastrada": "Client company registered",
  "Enfoque do Conteúdo": "Content Focus",
  Entendido: "Got it",
  "Enviar Imagem": "Upload Image",
  "Enviar Vídeo": "Upload Video",
  "Erro ao carregar sugestões.": "Error loading suggestions.",
  "Erro ao conectar com a IA.": "Error connecting to AI.",
  "Erro ao criar conteúdo": "Error creating content",
  "Erro ao criar post temporário: ID do post não encontrado na resposta. Resposta recebida: {response}":
    "Error creating temporary post: Post ID not found in response. Response received: {response}",
  "Erro ao criar post. Tente novamente.": "Error creating post. Try again.",
  "Erro ao extrair textos: {message}": "Error extracting texts: {message}",
  "Erro ao gerar conteúdo.": "Error generating content.",
  "Erro ao gerar criativo": "Error generating creative",
  "Erro ao gerar imagem": "Error generating image",
  "Erro ao gerar imagem com IA": "Error generating image with AI",
  "Erro ao melhorar conteúdo com IA. Tente novamente mais tarde.":
    "Error improving content with AI. Try again later.",
  "Erro ao regenerar áudio: {message}": "Error regenerating audio: {message}",
  "Erro ao republicar": "Error republishing",
  "Erro ao republicar: {message}": "Error republishing: {message}",
  "Erro ao sugerir hooks": "Error suggesting hooks",
  "Erro ao transcrever áudio: {message}": "Error transcribing audio: {message}",
  "Erro de conexão ao buscar músicas.":
    "Connection error while fetching songs.",
  "Erro de validação: {errors}": "Validation error: {errors}",
  "Erro desconhecido ao gerar criativo.": "Unknown error generating creative.",
  "Erro desconhecido ao gerar imagem": "Unknown error generating image",
  "Escolha como o post será publicado na plataforma":
    "Choose how the post will be published on the platform",
  "Escolha o formulário que os usuários preencherão no anúncio.":
    "Choose the form users will fill out in the ad.",
  "Escreva algum conteúdo antes de melhorar com IA":
    "Write some content before improving with AI",
  "Escreva o conteúdo do seu post aqui...": "Write your post content here...",
  Espanhol: "Spanish",
  "Especificações técnicas": "Technical specifications",
  "Especifique restrições ou instruções específicas para a geração da imagem. Estas serão incluídas no contexto de geração. Deixe vazio para usar o contexto do post.":
    "Specify restrictions or specific instructions for image generation. These will be included in the generation context. Leave empty to use the post context.",
  "Esta ação não pode ser desfeita": "This action cannot be undone",
  "Este feedback ajudará a melhorar futuros posts.":
    "This feedback will help improve future posts.",
  "Este post tem imagem e vídeo. O LinkedIn publicará apenas o vídeo (ignora imagem).":
    "This post has image and video. LinkedIn will only publish the video (ignores image).",
  'Estes textos foram detectados automaticamente no vídeo. Você pode editá-los na seção "Textos e Legendas" acima.':
    'These texts were automatically detected in the video. You can edit them in the "Texts and Captions" section above.',
  "Estilo de composição": "Composition style",
  "Estilo visual": "Visual style",
  "Ex: Automatize postagens, analise resultados e cresça mais rápido.":
    "E.g.: Automate posts, analyze results and grow faster.",
  "Ex: Comece Grátis": "E.g.: Start Free",
  "Ex: Como a IA está revolucionando o atendimento ao cliente...":
    "E.g.: How AI is revolutionizing customer service...",
  "Ex: Conteúdo não está alinhado com nossa marca, precisa de mais informações técnicas, formato inadequado...":
    "E.g.: Content is not aligned with our brand, needs more technical information, inappropriate format...",
  "Ex: Crie post sobre [tema] para o público [persona]...":
    "E.g.: Create post about [topic] for [persona] audience...",
  "Ex: Diretor, Gestor...": "E.g.: Director, Manager...",
  "Ex: Landing Page, Post LinkedIn Semanal...":
    "E.g.: Landing Page, Weekly LinkedIn Post...",
  "Ex: Saiba Mais, Link na Bio...": "E.g.: Learn More, Link in Bio...",
  "Ex: Saiba mais, Comente, Compartilhe...":
    "E.g.: Learn more, Comment, Share...",
  "Ex: Sênior, VP...": "E.g.: Senior, VP...",
  "Ex: TI, Finanças...": "E.g.: IT, Finance...",
  "Ex: Transforme seu negócio com IA": "E.g.: Transform your business with AI",
  "Ex: Um pôr do sol futurista sobre uma metrópole neon...":
    "E.g.: A futuristic sunset over a neon metropolis...",
  "Ex: Virtus Soluções": "E.g.: Virtus Solutions",
  "Ex: escritório moderno, pessoas trabalhando, tons azuis e brancos":
    "E.g.: modern office, people working, blue and white tones",
  "Ex: não incluir texto na imagem, manter as cores da marca, adicionar mais contraste...":
    "E.g.: do not include text in the image, keep brand colors, add more contrast...",
  "Ex: não incluir texto, não incluir logo, etc.":
    "E.g.: do not include text, do not include logo, etc.",
  "Exemplo do que você verá após gerar as sugestões":
    "Example of what you will see after generating suggestions",
  "Exibir em apps parceiros": "Show in partner apps",
  "Expansão de Público": "Audience Expansion",
  "Extraindo...": "Extracting...",
  "Extrair Textos": "Extract Texts",
  "Facebook (Página - Feed)": "Facebook (Page - Feed)",
  "Faixa Etária": "Age Range",
  "Falha ao processar otimização do post com IA":
    "Failed to process post optimization with AI",
  "Falha na Publicação": "Publishing Failed",
  "Fazendo Upload...": "Uploading...",
  "Fazendo ajustes em: {name}": "Making adjustments to: {name}",
  "Feed em dispositivos móveis": "Feed on mobile devices",
  "Ferramentas de IA": "AI Tools",
  "Fim (s)": "End (s)",
  "Focado em curtidas, comentários e interações no seu post.":
    "Focused on likes, comments and interactions on your post.",
  "Fonte Externa": "External Source",
  "Formato de Origem": "Source Format",
  "Formato e estilo": "Format and style",
  "Formato não disponível": "Format not available",
  "Formato não suportado ({name}). Use: JPG, PNG, GIF ou WebP. Tamanho máx.: {max}MB.":
    "Unsupported format ({name}). Use: JPG, PNG, GIF or WebP. Max size: {max}MB.",
  "Formato não suportado ({name}). Use: MP4, MOV, AVI, MKV ou WebM. Tamanho máx.: {max}MB.":
    "Unsupported format ({name}). Use: MP4, MOV, AVI, MKV or WebM. Max size: {max}MB.",
  "Formulário de Leads LinkedIn": "LinkedIn Lead Form",
  "GLM-Image: modelo de geração da Zhipu AI":
    "GLM-Image: generation model from Zhipu AI",
  "GPT-5.4 Nano — padrão e performático":
    "GPT-5.4 Nano — default and performant",
  "Galeria de Mídias": "Media Gallery",
  "Galeria do Post": "Post Gallery",
  "Gemini Imagen: geração de imagens com IA do Google":
    "Gemini Imagen: image generation with Google AI",
  "Gerando Imagem...": "Generating Image...",
  "Gerando Vídeo...": "Generating Video...",
  "Gerando fundo com Gemini…": "Generating background with Gemini…",
  "Gerando imagem...": "Generating image...",
  "Gerando…": "Generating…",
  "Gerar Criativo": "Generate Creative",
  "Gerar IA": "Generate AI",
  "Gerar Imagem IA": "Generate AI Image",
  "Gerar Imagem com IA": "Generate Image with AI",
  "Gerar Imagem com IA (Prompt)": "Generate Image with AI (Prompt)",
  "Gerar Imagem com IA - Post #{postId}":
    "Generate Image with AI - Post #{postId}",
  "Gerar Mídia": "Generate Media",
  "Gerar Novamente": "Generate Again",
  "Gerar Vídeo IA": "Generate AI Video",
  "Gerar Vídeo com IA": "Generate Video with AI",
  "Gerar Vídeo com IA - Post #{postId}":
    "Generate Video with AI - Post #{postId}",
  "Gerar e Adicionar à Galeria": "Generate and Add to Gallery",
  "Gerar e Incluir": "Generate and Include",
  "Gerar sugestões": "Generate suggestions",
  "Google Ads Performance": "Google Ads Performance",
  "Google Search": "Google Search",
  "Guia de Pronúncia (Opcional)": "Pronunciation Guide (Optional)",
  "Hashtags Estratégicas": "Strategic Hashtags",
  "HeyGen: especializado em criativos de marketing":
    "HeyGen: specialized in marketing creatives",
  Horário: "Time",
  "HÁ 2 HORAS": "2 HOURS AGO",
  IA: "AI",
  "IA Otimizada": "Optimized AI",
  "Ideal para levar pessoas para o seu site ou perfil externo.":
    "Ideal for driving people to your website or external profile.",
  "Idioma do Perfil": "Profile Language",
  "Idioma do perfil:": "Profile language:",
  Imagem: "Image",
  "Imagem Original Preservada": "Original Image Preserved",
  "Imagem atual (referência)": "Current image (reference)",
  "Imagem corporativa com gráficos e tom profissional.":
    "Corporate image with charts and professional tone.",
  "Imagem do post": "Post image",
  "Imagens de Referência": "Reference Images",
  "Incentive a ação do público": "Encourage audience action",
  "Informe o motivo da rejeição do post":
    "Provide the reason for rejecting the post",
  "Informe os dados básicos para o cadastro.":
    "Enter the basic registration data.",
  Inglês: "English",
  "Iniciar Deep Research": "Start Deep Research",
  "Insira o texto base aqui...": "Enter the base text here...",
  "Inspiração Visual": "Visual Inspiration",
  "Instruções específicas para a melhoria. O backend usa o conteúdo do post como contexto; deixe vazio para usar apenas o contexto automático.":
    "Specific instructions for enhancement. The backend uses post content as context; leave empty to use only automatic context.",
  "Investimento & Cronograma": "Investment & Schedule",
  "Investimento Total Estimado": "Estimated Total Investment",
  "Início (s)": "Start (s)",
  "Início da Veiculação": "Start Date",
  "Item {number}": "Item {number}",
  "JPG, PNG, GIF, WebP. Tamanho máx.: {max}MB":
    "JPG, PNG, GIF, WebP. Max size: {max}MB",
  "LANÇANDO...": "LAUNCHING...",
  "LANÇAR NO GOOGLE": "LAUNCH ON GOOGLE",
  Legenda: "Caption",
  "Legenda do Post": "Post Caption",
  "Light Mode": "Light Mode",
  "Limitações do servidor": "Server limitations",
  "Link de Destino": "Destination Link",
  "Link de redirecionamento do CTA": "CTA redirect link",
  "Link do CTA (opcional)": "CTA Link (optional)",
  "Link do Site": "Website Link",
  "LinkedIn (Feed/Página)": "LinkedIn (Feed/Page)",
  "LinkedIn (exemplo)": "LinkedIn (example)",
  "Localidade Alvo": "Target Location",
  "Localidades:": "Locations:",
  Localização: "Location",
  "Logo da Empresa": "Company Logo",
  "Logo e imagens de referência estão disponíveis apenas com":
    "Logo and reference images are only available with",
  "Logo preview": "Logo preview",
  "MP4, MOV, AVI, MKV, WebM. Tamanho máx.: {max}MB":
    "MP4, MOV, AVI, MKV, WebM. Max size: {max}MB",
  "Mais engajamento": "More engagement",
  Melhorado: "Enhanced",
  "Melhorando...": "Enhancing...",
  "Melhorar Imagem": "Enhance Image",
  "Melhorar Imagem com IA - Post #{postId}":
    "Enhance Image with AI - Post #{postId}",
  "Melhorar com IA": "Enhance with AI",
  "Melhorar qualidade da imagem e remover fundo":
    "Improve image quality and remove background",
  "Melhorar texto, gerar hashtags e CTA com IA":
    "Improve text, generate hashtags and CTA with AI",
  Meta: "Goal",
  "Mobile View": "Mobile View",
  "Modelo de IA para gerar sugestões": "AI model to generate suggestions",
  "Mostrando 20 primeiras palavras. Adicione mais palavras manualmente se necessário.":
    "Showing first 20 words. Add more words manually if needed.",
  "Motivo da Rejeição": "Rejection Reason",
  MÍDIA: "MEDIA",
  "Máx R$ 1000": "Max R$ 1000",
  "Mín R$ 20": "Min R$ 20",
  "Mínimo de {min} caracteres ({count}/{min})":
    "Minimum {min} characters ({count}/{min})",
  "Múltiplas imagens permitidas": "Multiple images allowed",
  "Músicas da Meta Sound Collection. Disponibilidade pode variar por região.":
    "Songs from Meta Sound Collection. Availability may vary by region.",
  "Na fila de geração...": "In generation queue...",
  "Na fila...": "In queue...",
  "Nano Banana 2: modelo Gemini para imagens (Google)":
    "Nano Banana 2: Gemini model for images (Google)",
  "Nenhum formulário ativo encontrado": "No active form found",
  'Nenhum texto adicionado. Clique em "Adicionar Texto" para criar legendas ou overlays.':
    'No text added. Click "Add Text" to create captions or overlays.',
  "Nenhuma conta encontrada": "No account found",
  "Nenhuma imagem carregada": "No image loaded",
  "Nenhuma música encontrada": "No song found",
  "Nome do Conteúdo": "Content Name",
  "Nome do conteúdo é obrigatório": "Content name is required",
  "Nome do conteúdo...": "Content name...",
  "Nome e dados do conteúdo são obrigatórios":
    "Name and content data are required",
  "Nossa IA vai ler o DNA da sua empresa e a Base de Conhecimento para criar um conteúdo altamente engajador.":
    "Our AI will read your company DNA and Knowledge Base to create highly engaging content.",
  "Nota Estratégica": "Strategic Note",
  "Nota de Veiculação": "Delivery Note",
  "Novo Visual IA": "New AI Visual",
  "Não foi possível carregar músicas. Verifique se a conta Instagram está configurada.":
    "Could not load songs. Check if the Instagram account is configured.",
  "Nível de Senioridade": "Seniority Level",
  "O Virtus adaptou sua imagem automaticamente. Você pode clicar em":
    "Virtus automatically adapted your image. You can click",
  "O conteúdo deste site será analisado pela IA no momento da geração dos posts.":
    "This site's content will be analyzed by AI when generating posts.",
  "O conteúdo do post é obrigatório": "Post content is required",
  "O horário deve ser pelo menos 5 minutos no futuro":
    "Time must be at least 5 minutes in the future",
  "O motivo deve ter pelo menos {min} caracteres":
    "Reason must be at least {min} characters",
  "O post": "The post",
  "O post será publicado imediatamente após a confirmação":
    "The post will be published immediately after confirmation",
  "O que você deseja fazer agora?": "What would you like to do now?",
  "O script será convertido em fala usando text-to-speech":
    "The script will be converted to speech using text-to-speech",
  "O sistema analisará seu conteúdo para extrair tópicos relevantes para cada plataforma.":
    "The system will analyze your content to extract relevant topics for each platform.",
  "O sistema irá agrupar as hashtags para melhor performance SEO.":
    "The system will group hashtags for better SEO performance.",
  "O vídeo será gerado após criar o post (pode levar alguns minutos).":
    "The video will be generated after creating the post (may take a few minutes).",
  ORG: "ORG",
  "Objetivo Principal": "Main Objective",
  "Obter sugestões visuais": "Get visual suggestions",
  "Onde republicar": "Where to republish",
  "Ops! Ocorreu um erro na publicação": "Oops! A publishing error occurred",
  Optimized: "Optimized",
  "Origem do Registro": "Record Origin",
  Original: "Original",
  "Orçamento Diário (R$)": "Daily Budget (R$)",
  "Os resultados exibidos refletem o investimento e os principais resultados relativos a":
    "The results shown reflect the investment and key results over",
  "Os resultados previstos são uma estimativa de tendência e não garantem o desempenho.":
    "Predicted results are a trend estimate and do not guarantee performance.",
  "Os textos aparecerão sobrepostos no vídeo durante a reprodução. Use os controles do vídeo para verificar em diferentes momentos.":
    "Texts will appear overlaid on the video during playback. Use video controls to check at different moments.",
  "Os textos serão sobrepostos ao vídeo nas posições e horários especificados":
    "Texts will be overlaid on the video at the specified positions and times",
  "Otimizado para Carrossel Instagram & Álbum Facebook":
    "Optimized for Instagram Carousel & Facebook Album",
  "Otimizar Texto": "Optimize Text",
  "Otimização Automatizada": "Automated Optimization",
  "Otimização Social Automática": "Automatic Social Optimization",
  Outros: "Others",
  "Ouvir preview": "Listen to preview",
  PAGO: "PAID",
  "PDF, Word, TXT — Máx. 25MB": "PDF, Word, TXT — Max. 25MB",
  "PMax / Discovery": "PMax / Discovery",
  "PNG, JPG até 5MB": "PNG, JPG up to 5MB",
  "Paleta de cores": "Color palette",
  "Para Reels, use formato 1080x1920 (9:16), codec H.264 + AAC para melhor compatibilidade.":
    "For Reels, use 1080x1920 (9:16) format, H.264 + AAC codec for best compatibility.",
  "Parar preview": "Stop preview",
  Pesquisa: "Search",
  "Plataformas disponíveis": "Available platforms",
  "Por favor, edite o texto transcrito antes de regenerar o áudio.":
    "Please edit the transcribed text before regenerating the audio.",
  "Por favor, insira um tema.": "Please enter a topic.",
  Português: "Portuguese",
  "Português (Brasil)": "Portuguese (Brazil)",
  Posição: "Position",
  "Post LinkedIn": "LinkedIn Post",
  "Postagem do Google": "Google Post",
  'Posts com CTAs claros tendem a ter uma taxa de conversão 28% maior. Considere usar verbos de ação como "Descubra", "Garanta" ou "Participe".':
    'Posts with clear CTAs tend to have a 28% higher conversion rate. Consider using action verbs like "Discover", "Get" or "Join".',
  "Preencha pelo menos o Título ou o Corpo do criativo.":
    "Fill in at least the Title or Body of the creative.",
  Preview: "Preview",
  "Preview da Publicação": "Publication Preview",
  "Preview do Vídeo": "Video Preview",
  "Preview indisponível": "Preview unavailable",
  "Processando vídeo com IA...": "Processing video with AI...",
  Promovido: "Promoted",
  "Prompt de IA": "AI Prompt",
  "Prompt para imagem": "Image prompt",
  "Pronto para upload": "Ready to upload",
  'Pronúncia para "{word}"': 'Pronunciation for "{word}"',
  "Provedor de IA": "AI Provider",
  "Provedor de fundo (IA)": "Background provider (AI)",
  "Pré-visualizar anúncio": "Preview ad",
  "Pré-visualização": "Preview",
  "Publicando...": "Publishing...",
  "Publicar Post": "Publish Post",
  "Publicar em Múltiplas Plataformas": "Publish on Multiple Platforms",
  "Publicar em Outras Redes": "Publish on Other Networks",
  "Publicar em {count} plataforma": "Publish on {count} platform",
  "Publicar em {count} plataformas": "Publish on {count} platforms",
  "Público Alvo": "Target Audience",
  "Público do Conteúdo": "Content Audience",
  "Público sugerido": "Suggested audience",
  "Que resultados você gostaria de alcançar com o anúncio?":
    "What results would you like to achieve with the ad?",
  "Quem deve ver seu anúncio?": "Who should see your ad?",
  Recomendação: "Recommendation",
  "Referência {index}": "Reference {index}",
  "Regenerando...": "Regenerating...",
  Regenerar: "Regenerate",
  "Regenerar Áudio": "Regenerate Audio",
  "Rejeitando...": "Rejecting...",
  "Rejeitar Post": "Reject Post",
  "Remover arquivo": "Remove file",
  "Remover imagem {index}": "Remove image {index}",
  "Remover logo": "Remove logo",
  "Remover texto": "Remove text",
  "Remover trilha": "Remove track",
  "Republicar em {count} destino": "Republish to {count} destination",
  "Republicar em {count} destinos": "Republish to {count} destinations",
  "Republicar no LinkedIn": "Republish on LinkedIn",
  "Requer vídeo": "Requires video",
  "Restrições e Instruções (Opcional)":
    "Restrictions and Instructions (Optional)",
  Revisar: "Review",
  "Salve o post antes de gerar uma imagem com IA":
    "Save the post before generating an image with AI",
  "Script de Narração": "Narration Script",
  "Script de Narração e Pronúncia": "Narration and Pronunciation Script",
  "Se vazio, a IA usará o título/conteúdo do post para gerar o fundo.":
    "If empty, AI will use the post title/content to generate the background.",
  "Segmentação Detalhada": "Detailed Targeting",
  "Segmentação por IA que se adapta com base no seu conteúdo e objetivo. Inclui mais do que apenas configurações como idioma e localidade.":
    "AI targeting that adapts based on your content and objective. Includes more than just settings like language and location.",
  Seguindo: "Following",
  Seguir: "Follow",
  "Selecione as plataformas": "Select platforms",
  "Selecione pelo menos uma plataforma antes de gerar a imagem":
    "Select at least one platform before generating the image",
  "Selecione um arquivo para upload": "Select a file to upload",
  "Selecione um vídeo": "Select a video",
  "Selecione uma imagem": "Select an image",
  "Senioridade:": "Seniority:",
  "Separe as hashtags com espaços": "Separate hashtags with spaces",
  "Setores de Atuação": "Industry Sectors",
  "Setores:": "Sectors:",
  "Simulação de feed profissional": "Professional feed simulation",
  "Sincronização Direta com Google Ads": "Direct Google Ads Sync",
  "Sitelinks (Opcional)": "Sitelinks (Optional)",
  "Sitelinks aumentam seu CTR em até 20%. Recomendamos preencher todos os campos para maximizar o Índice de Qualidade do Google Ads.":
    "Sitelinks increase your CTR by up to 20%. We recommend filling all fields to maximize Google Ads Quality Score.",
  "Sobre o que você quer falar?": "What do you want to talk about?",
  "Social Media que responde, engaja e gera leads profissionalmente.":
    "Social Media that responds, engages and generates leads professionally.",
  "Solução Completa para Negócios": "Complete Business Solution",
  "Sponsor LinkedIn Post": "Sponsor LinkedIn Post",
  "Stability AI: Stable Diffusion 3.5 Large":
    "Stability AI: Stable Diffusion 3.5 Large",
  "Status atual: {status}": "Current status: {status}",
  "Sugerir hooks": "Suggest hooks",
  "Sugestões visuais já geradas": "Visual suggestions already generated",
  "Sugestões visuais — Post #{postId}": "Visual suggestions — Post #{postId}",
  "Sugira um cenário para esta imagem...":
    "Suggest a scenario for this image...",
  "Suporta trilha": "Supports track",
  "Tablet View": "Tablet View",
  Tamanho: "Size",
  "Tem certeza que deseja continuar?": "Are you sure you want to continue?",
  Tempo: "Time",
  "Texto Transcrito do Áudio (Edite para corrigir):":
    "Transcribed Audio Text (Edit to fix):",
  "Texto a exibir no vídeo": "Text to display on video",
  "Texto sugerido para overlay": "Suggested overlay text",
  "Texto transcrito do áudio do vídeo...":
    "Transcribed text from video audio...",
  "Textos Detectados no Vídeo": "Texts Detected in Video",
  "Textos do criativo": "Creative texts",
  "Textos e Legendas do Vídeo": "Video Texts and Captions",
  "Tipo de Publicação": "Publication Type",
  "Tipo não suportado: {ext}. Use PDF, Word, Excel ou TXT.":
    "Unsupported type: {ext}. Use PDF, Word, Excel or TXT.",
  "Tom emocional": "Emotional tone",
  "Topo Centro": "Top Center",
  "Topo Direita": "Top Right",
  "Topo Esquerda": "Top Left",
  "Total Estimado: R$ {value}": "Estimated Total: R$ {value}",
  "Total: R$ {total}": "Total: R$ {total}",
  "Transcrevendo...": "Transcribing...",
  "Transcrever Áudio": "Transcribe Audio",
  "Trilha sonora para Reels (Meta Sound Collection)":
    "Soundtrack for Reels (Meta Sound Collection)",
  "Turbinar Google Ads": "Boost Google Ads",
  "Turbinar Publicação": "Boost Post",
  "Término (Previsão)": "End (Forecast)",
  "Título (Header)": "Title (Header)",
  "Título (Máx 25)": "Title (Max 25)",
  "Título do Anúncio": "Ad Title",
  "URL / Site": "URL / Site",
  "Upload de Imagem - Post #{postId}": "Image Upload - Post #{postId}",
  "Upload de Vídeo - Post #{postId}": "Video Upload - Post #{postId}",
  "Upload de imagem": "Image upload",
  "Upload de imagens de referência": "Reference images upload",
  "Upload de logo": "Logo upload",
  "Upload de vídeo": "Video upload",
  "Upload e Criar": "Upload and Create",
  "Usar este Post": "Use this Post",
  "Use essas sugestões ao criar ou melhorar a imagem do post (Gerar imagem / Melhorar imagem).":
    "Use these suggestions when creating or enhancing the post image (Generate image / Enhance image).",
  "Use o guia de pronúncia para corrigir palavras que podem ser mal pronunciadas":
    "Use the pronunciation guide to fix words that may be mispronounced",
  "Use um formulário para coletar dados de contato de potenciais clientes":
    "Use a form to collect contact data from potential customers",
  "Ver público sugerido": "View suggested audience",
  "Verificar e corrigir erros de gramática": "Check and fix grammar errors",
  "Visualizando informações detalhadas de: {name}":
    "Viewing detailed information for: {name}",
  "Visualização do Conteúdo Base": "Base Content View",
  "Você está prestes a excluir o post": "You are about to delete the post",
  "Volume da música": "Music volume",
  "Volume do vídeo original": "Original video volume",
  campo: "field",
  "em qualquer formato acima para criar uma duplicata do post já otimizada para essa plataforma.":
    "on any format above to create a duplicate of the post already optimized for that platform.",
  "frases de impacto geradas por IA com base no conteúdo do post":
    "impact phrases generated by AI based on post content",
  instagram_feed: "instagram_feed",
  inválido: "invalid",
  mais: "more",
  "no menu de contexto (canto superior).": "in the context menu (top corner).",
  "organização selecionada": "selected organization",
  "ou arraste e solte um vídeo aqui": "or drag and drop a video here",
  "ou arraste e solte uma imagem aqui": "or drag and drop an image here",
  "ou arraste para esta área": "or drag to this area",
  "será publicado agora na plataforma selecionada.":
    "will be published now on the selected platform.",
  "será publicado automaticamente na data selecionada abaixo.":
    "will be published automatically on the date selected below.",
  "será publicado nas plataformas escolhidas.":
    "will be published on the chosen platforms.",
  "será republicado nos destinos selecionados. As credenciais do LinkedIn vêm da":
    "will be republished to the selected destinations. LinkedIn credentials come from the",
  "virtusautomation.com.br": "virtusautomation.com.br",
  "{count} / 10 Itens": "{count} / 10 Items",
  "{count} Ativas": "{count} Active",
  "{count} Canais": "{count} Channels",
  "{count} Caracteres": "{count} Characters",
  "{count} Fontes da Base Usadas": "{count} Knowledge Base Sources Used",
  "{count} Selecionados": "{count} Selected",
  "{count} caracteres": "{count} characters",
  "{count} curtidas": "{count} likes",
  "{duration} dias": "{duration} days",
  "Áudio original": "Original audio",
  "Áudio regenerado com sucesso! O vídeo será atualizado.":
    "Audio regenerated successfully! The video will be updated.",
  "Última geração: {date}": "Last generation: {date}",
  Brasil: "Brazil",
  Español: "Spanish",
  "Mídia {index}": "Media {index}",
  "Pré-visualização do criativo do anúncio": "Ad creative preview",

  // Mapa de Empatia (ICP chat/insights/leads)
  "Erro ao buscar leads": "Failed to search leads",
  Score: "Score",
  "{total} Leads Qualificados Identificados": "{total} Qualified Leads Found",
  "Match Contextual: {icp}...": "Contextual Match: {icp}...",
  "Exportar CSV": "Export CSV",
  Identificação: "Identification",
  "Empresa & Cargo": "Company & Title",
  Região: "Region",
  "Match Score": "Match Score",
  "Escaneando fontes de leads...": "Scanning lead sources...",
  "Pronto para buscar?": "Ready to search?",
  "Iniciar Busca": "Start Search",
  "{remaining} de {limit} leads gratuitos restantes este mês":
    "{remaining} of {limit} free leads remaining this month",
  "Limite gratuito de {limit} leads/mês atingido — entre em contato para mais volume":
    "Free limit of {limit} leads/month reached — contact us for more volume",
  "O que pensa e sente": "What they think and feel",
  "O que ouve": "What they hear",
  "O que vê": "What they see",
  "O que diz e faz": "What they say and do",
  "Dores e frustrações": "Pains and frustrations",
  "Ganhos e desejos": "Gains and desires",
  "Tomadores de Decisão": "Decision Makers",
  "Líderes de Opinião": "Opinion Leaders",
  Porteiros: "Gatekeepers",
  "Usuários Finais": "End Users",
  Influenciadores: "Influencers",
  "ICP Preliminar — conteúdo insuficiente para análise precisa":
    "Preliminary ICP — not enough content for a precise analysis",
  "Perfil Estratégico do ICP": "Strategic ICP Profile",
  "Atualizado com dados reais": "Updated with real data",
  "Análise demográfica e comportamental": "Demographic and behavioral analysis",
  Influência: "Influence",
  Empatia: "Empathy",
  "Atributos Demográficos": "Demographic Attributes",
  "Dados não identificados": "No data identified",
  "Canais de Conversão": "Conversion Channels",
  "Canais não mapeados": "No channels mapped",
  "Tags de Identificação": "Identification Tags",
  "Tags não identificadas": "No tags identified",
  "Imersão psicológica no cotidiano do cliente":
    "A psychological deep dive into the customer's daily life",
  "Dados não disponíveis": "No data available",
  "Círculos de Influência": "Circles of Influence",
  "Ecossistema de decisão e compra": "Decision and purchase ecosystem",
  "Nenhum registro detectado": "No records detected",
  "Dinâmica de Fluxo e Argumentação": "Flow and Argumentation Dynamics",
  "Influencia diretamente:": "Directly influences:",
  "Prioridades Estratégicas": "Strategic Priorities",
  "Dores, metas e critérios de decisão do ICP":
    "The ICP's pains, goals, and decision criteria",
  "Dores & Desafios": "Pains & Challenges",
  "Dores não identificadas": "No pains identified",
  "Objetivos & Metas": "Objectives & Goals",
  "Metas não identificadas": "No goals identified",
  "Crivos de Decisão": "Decision Criteria",
  "Critérios não identificados": "No criteria identified",
  "ICP Preliminar — poucos dados disponíveis":
    "Preliminary ICP — limited data available",
  "Eficiência Estratégica": "Strategic Efficiency",
  "Score geral e destaques do ICP": "Overall score and ICP highlights",
  "Alinhamento Global": "Overall Alignment",
  "do seu ICP": "of your ICP",
  "Ponto Forte Principal": "Top Strength",
  "Insight ainda não identificado": "Insight not yet identified",
  "Insight Complementar": "Additional Insight",
  "Tendências & Oportunidades": "Trends & Opportunities",
  "O que está em alta e o que priorizar agora":
    "What's trending and what to prioritize now",
  "Tendências de Mercado": "Market Trends",
  "Nenhuma tendência mapeada": "No trends mapped",
  "Oportunidades IA": "AI Opportunities",
  "Aguardando sinais do mercado": "Waiting for market signals",
  "Diretriz Criativa de Conteúdo": "Content Creative Guidelines",
  "Como se comunicar com o seu ICP": "How to communicate with your ICP",
  "Formatos Recomendados": "Recommended Formats",
  "Formatos não definidos": "No formats defined",
  "Perfil de Linguagem": "Language Profile",
  "Tom de Voz": "Tone of Voice",
  Profissional: "Professional",
  "Ritmo de Publicação": "Posting Cadence",
  "3-5x por semana": "3-5x per week",
  "Clusters de Tópicos": "Topic Clusters",
  "Tópicos não definidos": "No topics defined",
  "Abordagem Estratégica": "Strategic Approach",
  "Distribuição & Performance": "Distribution & Performance",
  "Canais, horários e público-alvo prioritário":
    "Channels, timing, and priority target audience",
  "Canais de Alta Performance": "High-Performance Channels",
  "Canais não definidos": "No channels defined",
  "Janelas de Oportunidade": "Opportunity Windows",
  "Melhores Dias": "Best Days",
  "Horários Pico": "Peak Times",
  "Audiência Alvo": "Target Audience",
  "Buyer Personas": "Buyer Personas",
  "Não identificado": "Not identified",
  "Sub-segmentos do ICP": "ICP Sub-segments",
  "Influenciadores de Apoio": "Supporting Influencers",
  "Protocolo de Engajamento": "Engagement Protocol",
  "Táticas recomendadas para essa audiência":
    "Recommended tactics for this audience",
  "Olá! Sou seu assistente de recomendações de ICP e personas. 👋\n\n        Posso ajudá-lo com:\n        • 🎯 Identificar seu ICP (Cliente Ideal)\n        • ⭐ Sugerir influenciadores relevantes\n        • 👑 Identificar tomadores de decisão\n        • 💳 Entender o perfil de compradores\n        • 👤 Analisar usuários finais\n\n        **Dica:** Posso coletar dados das suas redes sociais automaticamente para fazer análises mais precisas. Deseja que eu colete agora?":
    "Hi! I'm your ICP and persona recommendation assistant. 👋\n\n        I can help you with:\n        • 🎯 Identifying your ICP (Ideal Customer Profile)\n        • ⭐ Suggesting relevant influencers\n        • 👑 Identifying decision makers\n        • 💳 Understanding buyer profiles\n        • 👤 Analyzing end users\n\n        **Tip:** I can automatically collect data from your social networks to run more precise analyses. Want me to collect it now?",
  "Erro ao conectar com o servidor. ": "Failed to connect to the server. ",
  "Verifique se o servidor backend está rodando na porta 8000.":
    "Check that the backend server is running on port 8000.",
  "Chatbot de descoberta não está disponível. Verifique os logs do servidor.":
    "Discovery chatbot is not available. Check the server logs.",
  "Erro desconhecido.": "Unknown error.",
  "❌ {errorMessage}": "❌ {errorMessage}",
  "🔄 Coletando dados das redes sociais e analytics... Isso pode levar alguns segundos.":
    "🔄 Collecting data from social networks and analytics... This may take a few seconds.",
  Nenhuma: "None",
  "• ICP: Não identificado — {reason}": "• ICP: Not identified — {reason}",
  "Dados insuficientes para identificar o perfil do cliente ideal.":
    "Not enough data to identify the ideal customer profile.",
  "• ICP detectado: {profile}": "• ICP detected: {profile}",
  "• ICP: Em análise": "• ICP: Under analysis",
  "• Insights de empatia: Disponíveis": "• Empathy insights: Available",
  "• Insights: Em processamento": "• Insights: Processing",
  "⚠️ {error}": "⚠️ {error}",
  "Erro ao coletar dados. ": "Failed to collect data. ",
  'Quer que eu colete os dados agora? Responda "sim" ou clique no botão abaixo.':
    'Want me to collect the data now? Reply "yes" or click the button below.',
  "❌ {error}": "❌ {error}",
  "Erro ao processar mensagem": "Failed to process message",
  "Erro ao processar mensagem. ": "Failed to process message. ",
  "Verifique se o servidor está rodando na porta 8000.":
    "Check that the server is running on port 8000.",
  "Verifique se o servidor está rodando.": "Check that the server is running.",
  "✅ **Descoberta concluída!**\n\nAgora você pode explorar:\n• **Mapeamento** - Visualize os mapas de empatia e influência\n• **Estratégia** - Veja recomendações e insights\n• **Lead Finder** - Encontre leads que correspondem ao seu ICP\n\nNavegue pelas abas acima para ver todos os detalhes!":
    "✅ **Discovery complete!**\n\nYou can now explore:\n• **Mapping** - View the empathy and influence maps\n• **Strategy** - See recommendations and insights\n• **Lead Finder** - Find leads matching your ICP\n\nBrowse the tabs above to see all the details!",
  "🎯 Meu ICP": "🎯 My ICP",
  "Qual é meu ICP? Quem é meu cliente ideal?":
    "What is my ICP? Who is my ideal customer?",
  "⭐ Influenciadores": "⭐ Influencers",
  "Quem são os influenciadores ideais para meu ICP?":
    "Who are the ideal influencers for my ICP?",
  "👑 Decisores": "👑 Decision Makers",
  "Quem são os tomadores de decisão de compra?":
    "Who are the purchase decision makers?",
  "💳 Compradores": "💳 Buyers",
  "Quem são os compradores? Qual o perfil do comprador?":
    "Who are the buyers? What is the buyer profile?",
  "👤 Usuários": "👤 Users",
  "Quem são os usuários finais? Qual o perfil do usuário final?":
    "Who are the end users? What is the end user profile?",
  "Coletar Dados Agora": "Collect Data Now",
  "Confirmar Seleção": "Confirm Selection",
  Analisando: "Analyzing",
  "Digite sua mensagem...": "Type your message...",
  "Limpar Histórico do Chat": "Clear Chat History",
  "Tem certeza que deseja limpar o histórico do chat?":
    "Are you sure you want to clear the chat history?",

  // Respostas Automáticas - aba Histórico
  "Filtro de Status": "Status Filter",
  Aprovados: "Approved",
  Rejeitados: "Rejected",
  Enviados: "Sent",
  "Todas as Redes": "All Networks",
  Positivo: "Positive",
  Neutro: "Neutral",
  Negativo: "Negative",
  Tipo: "Type",
  Pergunta: "Question",
  Reclamação: "Complaint",
  Elogio: "Compliment",
  Outro: "Other",
  Comentário: "Comment",
  "Confiança IA": "AI Confidence",
  "selecionado(s)": "selected",
  "pendente(s)": "pending",
  "Não existem respostas geradas para os filtros selecionados.":
    "There are no responses generated for the selected filters.",

  // Modal de Detalhes da Interação (Respostas Automáticas)
  "Nossa Equipe": "Our Team",
  "Modo Edição": "Edit Mode",
  "Gerando alternativas...": "Generating alternatives...",
  "Sugerir 3 alternativas": "Suggest 3 alternatives",
  "Nível de Confiança": "Confidence Level",
  "Plataforma Origem": "Source Platform",
  "Ver post de origem": "View source post",
  "Classificação (editável)": "Classification (editable)",
  "— auto —": "— auto —",
  "Salvar Classificação": "Save Classification",
  "Salvar Alteração": "Save Change",
  "Nenhuma alternativa foi gerada. Tente novamente.":
    "No alternatives were generated. Try again.",
  "Erro ao gerar alternativas": "Error generating alternatives",

  // Modal de Template (Base de Conhecimento)
  "Preencha nome e conteúdo do template":
    "Fill in the template's name and content",
  "Template atualizado com sucesso": "Template updated successfully",
  "Template criado com sucesso": "Template created successfully",
  "Erro ao salvar template": "Error saving template",
  "Tem certeza que deseja excluir este template?":
    "Are you sure you want to delete this template?",
  "Template excluído": "Template deleted",
  "Erro ao excluir template": "Error deleting template",
  "Editar Template": "Edit Template",
  "Salvar Template": "Save Template",
  "Ex.: Saudação Padrão": "E.g.: Standard Greeting",
  "Palavras-chave de gatilho": "Trigger keywords",
  "Separadas por vírgula": "Comma-separated",
  Utilização: "Usage",
  "Ou comece com uma sugestão": "Or start with a suggestion",
  "Erro ao carregar templates": "Error loading templates",
  "Não foi possível carregar os templates": "Could not load templates",
  "Não foi possível carregar os dados": "Could not load data",
  "Respostas automáticas com IA": "AI-powered auto-responder",
  "Conectado (RAG)": "Connected (RAG)",
  "Modo Manual": "Manual Mode",
  "Status Global": "Global Status",
  Operacional: "Operational",
  "Piloto Automático (Auto-Aprovação)": "Autopilot (Auto-Approval)",
  "Confiança Mínima": "Minimum Confidence",
  "Preview:": "Preview:",
  "Nenhuma tag criada ainda.": "No tags created yet.",
  "Se ligado, a IA enviará as respostas automaticamente para a rede social sem passar pela sua aprovação manual, desde que a confiança seja maior que o mínimo configurado abaixo.":
    "When on, the AI will send responses automatically to the social network without your manual approval, as long as confidence is above the minimum configured below.",
  "Respostas com confiança abaixo deste valor ({percent}%) sempre cairão na fila de moderação humana (Pendente), mesmo com o piloto automático ligado.":
    "Responses with confidence below this value ({percent}%) will always fall into the human moderation queue (Pending), even with autopilot on.",
  "Fontes Externas (RAG)": "External Sources (RAG)",
  "URLs de sites ou PDFs que o agente vai ler e indexar para responder com base neles. Uma URL por linha.":
    "URLs of websites or PDFs the agent will read and index to answer based on them. One URL per line.",
  "Salvar Fontes": "Save Sources",

  // AgentConfigPanel (Configuração do Agente IA)
  "Entendido! 😊 Vou chamar um dos nossos especialistas para te ajudar com isso. Aguarde um momentinho que logo entraremos em contato!":
    "Understood! 😊 I'll call one of our specialists to help you with this. Please wait a moment, we'll be in touch shortly!",
  "Olá! Vi seu comentário e quero te ajudar 😊 Como posso te atender?":
    "Hi! I saw your comment and I'd like to help 😊 How can I assist you?",
  "Rápido & econômico": "Fast & affordable",
  "Mais inteligente": "Smarter",
  "Configuração do Agente IA": "AI Agent Configuration",
  "Defina o comportamento e o conhecimento da Inteligência Artificial para o {platform}.":
    "Define the behavior and knowledge of the AI for {platform}.",
  "DNA da Empresa": "Company DNA",
  "Defina a personalidade do agente: nome, tom de voz, regras de negócio. Ele se passará por humano usando estas instruções.":
    "Define the agent's personality: name, tone of voice, business rules. It will pass as human using these instructions.",
  "Você é o Carlos, especialista da loja. Seja sempre educado.\nNunca ofereça descontos...\nNão mencione que é uma IA — você é da equipe de atendimento.":
    "You are Carlos, the store's specialist. Always be polite.\nNever offer discounts...\nDon't mention you're an AI — you're part of the support team.",
  "Escalada para Humano (Human-in-the-Loop)":
    "Human Escalation (Human-in-the-Loop)",
  'Quando o cliente disser palavras-chave abaixo, o bot não responderá com IA, enviará a mensagem de transferência e deixará em "Pendente".':
    'When the customer says any of the keywords below, the bot will not reply with AI, will send the handoff message, and will leave it as "Pending".',
  "Nenhum gatilho cadastrado.": "No triggers registered.",
  "Ex: qual o preço...": "E.g.: what's the price...",
  "Mensagem de transferência": "Handoff message",
  "Quando um comentário contiver as palavras-chave abaixo, o bot envia automaticamente uma DM ao comentarista (Facebook e Instagram).":
    "When a comment contains the keywords below, the bot automatically sends a DM to the commenter (Facebook and Instagram).",
  "Nenhuma palavra-chave cadastrada — recurso desativado.":
    "No keywords registered — feature disabled.",
  "Ex: quero saber mais, interesse, preço...":
    "E.g.: want to know more, interested, price...",
  "Mensagem enviada por DM": "Message sent via DM",
  "Botões de Resposta Rápida": "Quick Reply Buttons",
  "Botões enviados junto com a DM automática (Messenger / Instagram Direct). Máximo 13, título até 20 caracteres.":
    "Buttons sent along with the automatic DM (Messenger / Instagram Direct). Max 13, title up to 20 characters.",
  "Nenhum botão cadastrado.": "No buttons registered.",
  "Ex: Sim, tenho interesse! (máx 20 chars)":
    "E.g.: Yes, I'm interested! (max 20 chars)",
  "Modelo de IA": "AI Model",
  "Salvar Configurações do Agente": "Save Agent Settings",
  "Verifique sua conexão e tente novamente em instantes.":
    "Check your connection and try again shortly.",
  "Crie seu primeiro template de resposta para agilizar seu atendimento.":
    "Create your first response template to speed up your support.",
  // Sede corporativa / Empresa Cliente (cabeçalho, identidade, integrações, assinatura)
  "1 dia": "1 day",
  "A IA usa essa descrição para escrever posts e montar o Mapa de Empatia.":
    "The AI uses this description to write posts and build the Empathy Map.",
  "A pessoa recebe um link válido por 7 dias. Se já tiver conta com este e-mail, confirma com a senha dela; se não, cria a conta ao aceitar.":
    "The person gets a link valid for 7 days. If they already have an account with this email, they confirm with their password; otherwise they create the account when accepting.",
  "A pessoa só entra na organização depois de aceitar o convite.":
    "The person only joins the organization after accepting the invitation.",
  "A última verificação da integração falhou.":
    "The last integration check failed.",
  "Abrir integrações": "Open integrations",
  "Ad Account ID (Conta de Anúncios)": "Ad Account ID",
  "Adicione esta URI em 'URIs de redirecionamento autorizados' do mesmo Client ID informado acima:":
    "Add this URI to 'Authorized redirect URIs' of the same Client ID entered above:",
  "Admin (Gestão Total)": "Admin (Full Management)",
  "Aguardando 1º pagamento": "Awaiting first payment",
  "Aguardando aceite": "Awaiting acceptance",
  "Ainda não configurado": "Not configured yet",
  "Alterações não salvas": "Unsaved changes",
  "Ambos (Página + Perfil Pessoal)": "Both (Page + Personal Profile)",
  "Anúncios no ChatGPT": "Ads on ChatGPT",
  "Arquivo muito grande. Tamanho máximo: 5MB.":
    "File too large. Maximum size: 5MB.",
  "Arraste a imagem ou clique para escolher":
    "Drag the image here or click to choose",
  "As alterações desta integração ainda não foram salvas.":
    "The changes to this integration have not been saved yet.",
  "Assento Extra (R$/mês)": "Extra Seat (R$/month)",
  Assentos: "Seats",
  "Assentos Inclusos": "Included Seats",
  "Assentos em uso": "Seats in use",
  "Assim sua marca aparece nos criativos":
    "This is how your brand looks in creatives",
  "Assinatura Virtus Chat": "Virtus Chat Subscription",
  "Assinatura do Virtus Chat cancelada.": "Virtus Chat subscription canceled.",
  "Assinatura do Virtus Chat criada! Aguardando confirmação do pagamento.":
    "Virtus Chat subscription created! Awaiting payment confirmation.",
  "Assinatura reajustada com o preço atual do plano!":
    "Subscription adjusted to the current plan price!",
  Assinaturas: "Subscriptions",
  "Ativar Assinatura": "Activate Subscription",
  "Baixa confiança — extraído da imagem da logo, verifique com atenção.":
    "Low confidence — extracted from the logo image, check carefully.",
  "Brand kit aplicado aos criativos gerados por IA":
    "Brand kit applied to AI-generated creatives",
  "Buscando endereço...": "Looking up address...",
  "Buscar dados": "Look up data",
  "Buscar minhas páginas": "Find my pages",
  "Buscar página...": "Search page...",
  "CEP não encontrado. Preencha o endereço à mão.":
    "Postal code not found. Fill in the address manually.",
  "CNPJ não informado": "CNPJ not provided",
  "Cadastre a primeira empresa cliente": "Add your first client company",
  Cancelada: "Canceled",
  "Cancelar Assinatura Virtus Chat": "Cancel Virtus Chat Subscription",
  "Cancelar convite de": "Cancel invitation for",
  "Cliente ideal (ICP)": "Ideal customer (ICP)",
  "Cobrando só a equipe do Virtus Chat — sincronizado em {date}.":
    "Billing only the Virtus Chat team — synced on {date}.",
  "Cobrando só a equipe do Virtus Chat.": "Billing only the Virtus Chat team.",
  "Cobrança recorrente por assento da equipe de atendimento":
    "Recurring billing per support team seat",
  "Com o CNPJ, buscamos nome, endereço e telefone na Receita Federal (via BrasilAPI).":
    "With the CNPJ, we look up name, address and phone at the Federal Revenue (via BrasilAPI).",
  "Como a marca se comunica — usado para personalizar textos gerados por IA.":
    "How the brand communicates — used to personalize AI-generated text.",
  Completar: "Complete",
  "Completar dados": "Complete details",
  Conectado: "Connected",
  Conectar: "Connect",
  "Conectar Ads": "Connect Ads",
  Conexão: "Connection",
  "Conexão ativa": "Connection active",
  "Configure chaves de API para serviços auxiliares como Google Maps, IA (Gemini/Veo) e YouTube.":
    "Configure API keys for auxiliary services such as Google Maps, AI (Gemini/Veo) and YouTube.",
  "Configure credenciais da Advertiser API da OpenAI para criar e gerenciar anúncios exibidos no ChatGPT.":
    "Configure OpenAI Advertiser API credentials to create and manage ads shown in ChatGPT.",
  "Configure credenciais para a TikTok Business API para gestão de campanhas e métricas.":
    "Configure TikTok Business API credentials for campaign and metrics management.",
  "Configure credenciais para o Google Ads API para gestão de campanhas e conversões.":
    "Configure Google Ads API credentials for campaign and conversion management.",
  "Configure credenciais para o Google Business Profile (ex-GMB) para publicações orgânicas locais.":
    "Configure Google Business Profile (formerly GMB) credentials for local organic posts.",
  "Configure sua integração com LinkedIn para publicar posts profissionais.":
    "Configure your LinkedIn integration to publish professional posts.",
  "Configure sua integração unificada para Facebook e Instagram através do ecossistema Meta for Developers.":
    "Configure your unified Facebook and Instagram integration through Meta for Developers.",
  "Confira e ajuste se necessário antes de salvar.":
    "Review and adjust if needed before saving.",
  "Confirmar Cancelamento": "Confirm Cancellation",
  "Conta: {name}": "Account: {name}",
  "Contato & Endereço": "Contact & Address",
  "Contexto para IA": "AI context",
  "Continuar editando": "Keep editing",
  "Convidar membro": "Invite member",
  "Convite cancelado": "Invitation canceled",
  "Convite enviado": "Invitation sent",
  "Convite reenviado": "Invitation resent",
  "Convites pendentes": "Pending invitations",
  "Copiar URI": "Copy URI",
  "Cor de fundo": "Background color",
  "Cor primária": "Primary color",
  "Cores de marca detectadas": "Brand colors detected",
  Corrigir: "Fix",
  Credenciais: "Credentials",
  "Credenciais e tokens manuais (avançado)":
    "Manual credentials and tokens (advanced)",
  "Credenciais próprias do app (avançado)":
    "Your own app credentials (advanced)",
  "Customer ID (Cliente)": "Customer ID",
  "Dados da conta": "Account details",
  "Dados encontrados: {city}/{state}. Endereço e telefone serão salvos junto.":
    "Data found: {city}/{state}. Address and phone will be saved too.",
  "Dados institucionais": "Company details",
  "Defina cores e fonte para ver a prévia":
    "Set colors and font to see the preview",
  "Define um preço próprio pra esta assinatura (ex.: cliente promocional) — diferente da config global. Depois de definir, não use o botão Reajustar aqui (ele reaplica o preço global e sobrescreveria este).":
    "Sets a custom price for this subscription (e.g., promotional client) — different from the global setting. After setting it, don't use the Adjust button here (it reapplies the global price and would overwrite this one).",
  "Descartar alterações?": "Discard changes?",
  "Descreva o seu cliente ideal": "Describe your ideal customer",
  "Detectamos cores de marca a partir do seu site/logo":
    "We detected brand colors from your website/logo",
  Documentação: "Documentation",
  "Ecossistema Virtus": "Virtus ecosystem",
  "Em dia": "Up to date",
  "Em uso: estas credenciais substituem as credenciais padrão da Virtus.":
    "In use: these credentials replace the default Virtus credentials.",
  "Empresa cliente": "Client company",
  "Empresas cliente": "Client companies",
  "Encontramos cores no seu site/logo. Revise e aplique ao brand kit.":
    "We found colors on your website/logo. Review and apply them to the brand kit.",
  Endereço: "Address",
  "Endereço preenchido pelo CEP. Confira o número.":
    "Address filled from the postal code. Check the number.",
  Enviando: "Sending",
  "Enviar convite": "Send invitation",
  "Erro ao ativar assinatura": "Error activating subscription",
  "Erro ao ativar assinatura do Virtus Chat":
    "Error activating Virtus Chat subscription",
  "Erro ao cancelar assinatura": "Error canceling subscription",
  "Erro ao cancelar assinatura do Virtus Chat":
    "Error canceling Virtus Chat subscription",
  "Erro ao cancelar convite": "Error canceling invitation",
  "Erro ao enviar convite": "Error sending invitation",
  "Erro ao reajustar assinatura": "Error adjusting subscription",
  "Erro ao reajustar assinatura do Virtus Chat":
    "Error adjusting Virtus Chat subscription",
  "Erro ao reenviar convite": "Error resending invitation",
  "Erro ao salvar preço personalizado": "Error saving custom price",
  "Esta organização ainda não tem assinatura recorrente do Virtus Chat.":
    "This organization doesn't have a recurring Virtus Chat subscription yet.",
  "Ex: Automação Industrial, Marketing Digital":
    "E.g.: Industrial Automation, Digital Marketing",
  "Ex: Direto, técnico, sem jargão de vendas":
    "E.g.: Direct, technical, no sales jargon",
  "Ex: Marketing Digital": "E.g.: Digital Marketing",
  "Exigida em campanhas de leads do Meta": "Required for Meta lead campaigns",
  Expirado: "Expired",
  "Falha na conexão": "Connection failed",
  "Falta preencher: {fields}.": "Still missing: {fields}.",
  Fonte: "Font",
  "Fonte da marca": "Brand font",
  "Formato não suportado. Use PNG, JPG ou WEBP.":
    "Unsupported format. Use PNG, JPG or WEBP.",
  Fundo: "Background",
  Gerenciar: "Manage",
  "Gerenciar empresas cliente": "Manage client companies",
  "Gestão de Tráfego": "Traffic Management",
  "Há um card de identidade em edição com alterações não salvas.":
    "An identity card is being edited with unsaved changes.",
  "ID da empresa no Leadflow": "Company ID in Leadflow",
  "ID desta empresa cliente na plataforma Virtus Leadflow (separada). Necessário pra consultar status e auditoria dos leads capturados por lá.":
    "ID of this client company on the Virtus Leadflow platform (separate). Required to check status and audit leads captured there.",
  "ID desta organização na plataforma Virtus Leadflow (separada). Necessário pra consultar status e auditoria de leads capturados por lá diretamente aqui. Só use aqui se a organização NÃO tiver Empresas Cliente configuradas — senão configure por Empresa Cliente.":
    "ID of this organization on the Virtus Leadflow platform (separate). Required to check status and audit leads captured there directly here. Only use it here if the organization has NO Client Companies — otherwise configure it per Client Company.",
  "ID do Gestor (MCC)": "Manager ID (MCC)",
  "Identidade & Marca": "Identity & Brand",
  "Identificação legal e presença online":
    "Legal identification and online presence",
  "Informe um e-mail válido.": "Enter a valid email.",
  "Informe valores válidos": "Enter valid values",
  Integrações: "Integrations",
  "Integrações conectadas": "Connected integrations",
  "Isso cancela a cobrança recorrente no Asaas — a organização perde o acesso pago ao Virtus Chat a partir do próximo ciclo.":
    "This cancels the recurring charge in Asaas — the organization loses paid access to Virtus Chat from the next cycle.",
  Logotipo: "Logo",
  "Maps, YouTube & IA": "Maps, YouTube & AI",
  Marca: "Brand",
  "Marketing de Vídeo": "Video Marketing",
  "Nenhum assento cobrado no momento.": "No seats billed at the moment.",
  "Nenhuma integração com erro": "No integrations with errors",
  "Nenhuma página administrada por esta conta do LinkedIn.":
    "No pages managed by this LinkedIn account.",
  "Nome da fonte usada nos criativos gerados por IA. As sugestões vêm do Google Fonts.":
    "Name of the font used in AI-generated creatives. Suggestions come from Google Fonts.",
  "Nome da organização": "Organization name",
  "Não configurado": "Not configured",
  "Não encontramos esse CNPJ. Preencha os dados abaixo à mão.":
    "We couldn't find this CNPJ. Fill in the details below manually.",
  "Não foi possível buscar as páginas.": "Couldn't fetch the pages.",
  "Não foi possível enviar o e-mail.": "Couldn't send the email.",
  "Não foi possível enviar o logo.": "Couldn't upload the logo.",
  "Não verificado": "Not verified",
  "Número de Telefone (Chamadas)": "Phone Number (Calls)",
  "O Virtus Chat ainda não informou a equipe: por enquanto todos os usuários ativos da organização contam como assento.":
    "Virtus Chat hasn't reported the team yet: for now every active user in the organization counts as a seat.",
  "O link deve começar com http:// ou https://":
    "The link must start with http:// or https://",
  "O pagamento da assinatura está atrasado.":
    "The subscription payment is overdue.",
  "O que precisa de atenção nesta organização":
    "What needs attention in this organization",
  "Ocultar URL": "Hide URL",
  "Opcional. Marca, contexto para IA e endereço podem ser preenchidos depois, em Identidade & Marca.":
    "Optional. Brand, AI context and address can be filled in later, under Identity & Brand.",
  "Os tokens de acesso são criados ao conectar a conta.":
    "Access tokens are created when you connect the account.",
  Other: "Other",
  "PNG, JPG ou WEBP, até 5MB": "PNG, JPG or WEBP, up to 5MB",
  "Pagamento atrasado": "Payment overdue",
  "Pela organização": "By the organization",
  "Pela própria empresa": "By the company itself",
  Pendências: "To-dos",
  "Perfil Pessoal (Exige App Próprio / BYOA)":
    "Personal Profile (Requires Own App / BYOA)",
  "Perfil completo": "Profile complete",
  "Perfil de Empresa": "Business Profile",
  "Perfil incompleto": "Incomplete profile",
  "Perfil para IA": "AI profile",
  "Perfil {percent}% completo": "Profile {percent}% complete",
  "Pessoal & Empresa": "Personal & Company",
  "Política de privacidade": "Privacy policy",
  "Preencha só se for usar um app próprio. Deixe em branco para usar as credenciais da Virtus.":
    "Fill in only if you use your own app. Leave blank to use the Virtus credentials.",
  "Presença online": "Online presence",
  "Preço Base (R$/mês)": "Base Price (R$/month)",
  "Preço Personalizado": "Custom Price",
  "Preço personalizado aplicado!": "Custom price applied!",
  Primária: "Primary",
  "Página da Empresa (Login 1-Click)": "Company Page (1-Click Login)",
  "Quem está sendo cobrado ({count})": "Who is being billed ({count})",
  "Quem é cobrado pelos serviços desta empresa":
    "Who is billed for this company's services",
  Reajustar: "Adjust",
  Reconectar: "Reconnect",
  "Redes sociais": "Social networks",
  Reenviar: "Resend",
  "Renovando...": "Renewing...",
  "Renovar agora": "Renew now",
  "Requer atenção": "Needs attention",
  "Sala, andar, bloco": "Suite, floor, building",
  "Salvar Preço": "Save Price",
  "Salvar e testar": "Save and test",
  "Salvo, mas ainda não verificado": "Saved, but not verified yet",
  Segmento: "Industry",
  "Selecione a página...": "Select the page...",
  "Sem assinatura recorrente": "No recurring subscription",
  "Seções da organização": "Organization sections",
  "Somente leitura": "Read only",
  "Status e auditoria dos leads capturados no Leadflow":
    "Status and audit of leads captured in Leadflow",
  "Também usamos o site para sugerir as cores da sua marca automaticamente.":
    "We also use the website to suggest your brand colors automatically.",
  "Telefone de contato": "Contact phone",
  "Telefone para Chamadas (Google Ads)": "Call Phone (Google Ads)",
  "Tem certeza que quer cancelar a assinatura de {name}? Essa ação não pode ser desfeita por aqui — pra reativar, será preciso ativar de novo.":
    "Are you sure you want to cancel {name}'s subscription? This can't be undone here — to reactivate, you'll need to activate it again.",
  "Testar conexão": "Test connection",
  "Tipo Padrão": "Default Type",
  "Token Meta": "Meta Token",
  "Token Meta renovado automaticamente por mais {days} dias!":
    "Meta token automatically renewed for another {days} days!",
  "Tokens de acesso gerenciados automaticamente pela conexão.":
    "Access tokens managed automatically by the connection.",
  "Tom de voz": "Tone of voice",
  "Tom de voz da marca": "Brand tone of voice",
  "Trocar logo": "Change logo",
  "Tudo em ordem. Nenhuma pendência no momento.":
    "All good. Nothing needs attention right now.",
  "URI copiada com sucesso!": "URI copied!",
  "URI de Redirecionamento Obrigatória (Google Cloud Console)":
    "Required Redirect URI (Google Cloud Console)",
  "Usa os dados de cadastro e faturamento da organização. É o padrão.":
    "Uses the organization's registration and billing details. This is the default.",
  "Usa os dados desta empresa cliente (CNPJ e endereço) no faturamento e nas integrações.":
    "Uses this client company's details (CNPJ and address) for billing and integrations.",
  "Usado em anúncios de chamada e cadastros de pagamento":
    "Used in call ads and payment records",
  "Usado em textos, criativos e no Mapa de Empatia":
    "Used in copy, creatives and the Empathy Map",
  "Usado nos anúncios de chamada do Google Ads": "Used in Google Ads call ads",
  "Usando as credenciais padrão da Virtus.":
    "Using the default Virtus credentials.",
  "Usar sugestão": "Use suggestion",
  "Usar uma URL": "Use a URL",
  "Use uma cor hexadecimal (ex: #7B2CF5)": "Use a hex color (e.g.: #7B2CF5)",
  "Valor mensal": "Monthly amount",
  "Ver assinatura": "View subscription",
  "Ver dados": "View details",
  "Ver perfil": "View profile",
  "Verificado em {date}": "Verified on {date}",
  "Você pode consultar os dados desta organização. Só administradores podem alterá-los.":
    "You can view this organization's details. Only administrators can change them.",
  "Website oficial": "Official website",
  "não definida": "not set",
  "{n} assento(s)": "{n} seat(s)",
  "{n} campo(s) para completar": "{n} field(s) to complete",
  "{n} com erro": "{n} with errors",
  "{n} de {total} conectadas": "{n} of {total} connected",
  "{n} inclusos · extra R$ {price}": "{n} included · extra R$ {price}",
  "Última sincronização: {date}": "Last sync: {date}",
  "Abrir página de pagamento": "Open payment page",
  "Ainda não há assinatura recorrente do Virtus Chat.":
    "There is no recurring Virtus Chat subscription yet.",
  "Empresa Cliente": "Client Company",
  "Isso cancela a cobrança recorrente no Asaas — o acesso pago ao Virtus Chat termina no próximo ciclo.":
    "This cancels the recurring charge in Asaas — paid access to Virtus Chat ends at the next cycle.",
  "Não foi possível carregar a empresa cliente.":
    "Couldn't load the client company.",
  "Não foi possível carregar a organização.": "Couldn't load the organization.",
  "Não foi possível criar a organização.": "Couldn't create the organization.",
  "O navegador bloqueou a janela de conexão. Permita pop-ups para este site.":
    "The browser blocked the connection window. Allow pop-ups for this site.",
  "O pagamento da primeira mensalidade está pendente.":
    "The first monthly payment is pending.",
  "Preencha: {fields}": "Fill in: {fields}",
  "Tentar de novo": "Try again",
  "Ver detalhes": "View details",
  "Ver integração": "View integration",
  "Verifique sua conexão e tente de novo.":
    "Check your connection and try again.",
  "Carregando integrações": "Loading integrations",
  "Carregando visão geral": "Loading overview",
  "Carregando assinatura": "Loading subscription",
  "Carregando assentos": "Loading seats",
  "/mês": "/month",
  "A cobrança recorrente no Asaas é encerrada.":
    "The recurring charge in Asaas is stopped.",
  "A integração para de sincronizar e publicar até você conectar a conta de novo.":
    "The integration stops syncing and publishing until you connect the account again.",
  "A unidade principal é onde as publicações vão por padrão.":
    "The primary location is where posts go by default.",
  "Adição ao carrinho": "Add to cart",
  "Ao cancelar:": "When you cancel:",
  "Aplicar preço": "Apply price",
  "Após clique": "After click",
  "Após visualização": "After view",
  "As publicações automáticas nas unidades param e os tokens de acesso são removidos.":
    "Automatic posts to the locations stop and the access tokens are removed.",
  "Assento extra": "Extra seat",
  "Assentos inclusos": "Included seats",
  "Autorize o acesso ao Google Ads sem copiar tokens manualmente.":
    "Authorize Google Ads access without copying tokens manually.",
  "Buscar página": "Search page",
  "Cancelar a assinatura do Virtus Chat?":
    "Cancel the Virtus Chat subscription?",
  "Cancelar assinatura": "Cancel subscription",
  "Com os {n} assento(s) de hoje": "With today's {n} seat(s)",
  Compra: "Purchase",
  "Conectar com Google": "Connect with Google",
  "Conectar com LinkedIn": "Connect with LinkedIn",
  "Conectar com a conta Google": "Connect your Google account",
  "Conectar página do Facebook/Instagram": "Connect a Facebook/Instagram page",
  "Conectar página selecionada": "Connect selected page",
  "Conecte a conta abaixo para começar.":
    "Connect the account below to get started.",
  "Conecte o perfil ou a página da empresa com um clique.":
    "Connect the profile or company page in one click.",
  "Conta Google Ads conectada": "Google Ads account connected",
  "Conta Google conectada": "Google account connected",
  "Conta Google desconectada.": "Google account disconnected.",
  "Conta LinkedIn": "LinkedIn account",
  "Conta de anúncios {id}": "Ad account {id}",
  "Conversões (Conversions API)": "Conversions (Conversions API)",
  "Criar regra": "Create rule",
  "Definir como unidade principal": "Set as primary location",
  "Depois de aplicar, não use “Reajustar” nesta assinatura: ele volta ao preço do plano e substitui este.":
    "After applying, don't use “Adjust” on this subscription: it goes back to the plan price and replaces this one.",
  "Desativar a regra “{name}”?": "Disable the rule “{name}”?",
  "Desconectar Google Business Profile?": "Disconnect Google Business Profile?",
  "Desconectar LinkedIn?": "Disconnect LinkedIn?",
  "Desconectar Meta?": "Disconnect Meta?",
  "Desconectar {platform}?": "Disconnect {platform}?",
  "Erro na última verificação": "Error in the last check",
  "Escolha uma página do Business Suite da Virtus, sem gerenciar tokens.":
    "Pick a page from the Virtus Business Suite, no tokens to manage.",
  "Escolher página": "Choose page",
  "Ex: Compra efetuada": "E.g.: Purchase completed",
  "Facebook e Instagram param de publicar e sincronizar até você conectar uma página de novo.":
    "Facebook and Instagram stop publishing and syncing until you connect a page again.",
  "Manter assinatura": "Keep subscription",
  "Mensalidade base": "Base monthly fee",
  "Nenhuma página com esse nome.": "No page with that name.",
  "Nenhuma regra de conversão ainda. Crie a primeira acima.":
    "No conversion rules yet. Create the first one above.",
  "Nenhuma unidade encontrada nesta conta. Clique em Sincronizar depois de criar o perfil no Google.":
    "No locations found in this account. Click Sync after creating the profile on Google.",
  "Nova regra": "New rule",
  "Não foi possível carregar as conversões do LinkedIn.":
    "Couldn't load LinkedIn conversions.",
  "Não foi possível criar a regra.": "Couldn't create the rule.",
  "Não foi possível definir a unidade principal.":
    "Couldn't set the primary location.",
  "Não foi possível desativar a regra.": "Couldn't disable the rule.",
  "Não foi possível sincronizar as unidades.": "Couldn't sync the locations.",
  "O LinkedIn para de registrar conversões por esta regra. Ela continua na lista como desativada.":
    "LinkedIn stops recording conversions for this rule. It stays in the list as disabled.",
  "O acesso pago ao Virtus Chat termina no fim do ciclo atual.":
    "Paid access to Virtus Chat ends at the end of the current cycle.",
  "Para voltar, será preciso ativar uma nova assinatura.":
    "To come back, you'll need to activate a new subscription.",
  "Preencha as credenciais abaixo e salve.":
    "Fill in the credentials below and save.",
  "Preço personalizado": "Custom price",
  "Publique no Google Maps e na Pesquisa Google.":
    "Publish on Google Maps and Google Search.",
  "Página conectada": "Page connected",
  "Páginas disponíveis": "Available pages",
  "Recursos adicionais": "Additional features",
  "Regra de conversão criada.": "Conversion rule created.",
  "Regra desativada.": "Rule disabled.",
  "Salve o Ad Account ID para gerenciar as regras de conversão (Conversions API).":
    "Save the Ad Account ID to manage conversion rules (Conversions API).",
  "Sem endereço cadastrado": "No address on file",
  Sincronizar: "Sync",
  "Só aparecem páginas do Business Suite da Virtus. Se a página deste cliente não estiver na lista, preencha Page ID e Page Access Token em “Credenciais e tokens manuais”.":
    "Only pages from the Virtus Business Suite are listed. If this client's page isn't here, fill in Page ID and Page Access Token under “Manual credentials and tokens”.",
  "Trocar conta": "Switch account",
  "Trocar página": "Change page",
  "Unidade principal": "Primary location",
  "Unidades ({n})": "Locations ({n})",
  "Unidades sincronizadas.": "Locations synced.",
  "Use um número inteiro (0 ou mais)": "Use a whole number (0 or more)",
  "Use um valor como 197,00": "Use a value like 197.00",
  "Use um valor como 49,90": "Use a value like 49.90",
  "Vale só para esta assinatura": "Applies to this subscription only",
  "hoje: R$ {value}": "today: R$ {value}",
  "{n} dia(s)": "{n} day(s)",
  "{n} unidade(s) encontrada(s)": "{n} location(s) found",
  "Última verificação: {date}": "Last check: {date}",
  "“{name}” agora é a unidade principal.":
    "“{name}” is now the primary location.",
  "Alterações salvas.": "Changes saved.",
  "Alterações salvas. Testando a conexão...":
    "Changes saved. Testing the connection...",
  Salvo: "Saved",
  "Conta e unidades do Google": "Google account and locations",
  "Testando...": "Testing...",
  "verificado em {date}": "verified on {date}",
  "Credenciais salvas. Falta testar a conexão.":
    "Credentials saved. The connection still needs a test.",
  "Ativar assinatura": "Activate subscription",
  "Anunciante {id}": "Advertiser {id}",
  "Conta {id}": "Account {id}",
  "Página {id}": "Page {id}",
  "Unidade {id}": "Location {id}",
  Ver: "View",
  "{n} precisa(m) de atenção": "{n} need(s) attention",
  // Upload de mídia do post (validação no modal de edição)
  "“{name}”: formato não suportado. Use JPG, PNG, GIF ou WEBP.":
    "“{name}”: unsupported format. Use JPG, PNG, GIF or WEBP.",
  "“{name}” tem {size}MB; o máximo para imagens é {max}MB.":
    "“{name}” is {size}MB; the maximum for images is {max}MB.",
  "“{name}”: formato não suportado. Use MP4, MOV, AVI, MKV ou WEBM.":
    "“{name}”: unsupported format. Use MP4, MOV, AVI, MKV or WEBM.",
  "“{name}” tem {size}MB; o máximo para vídeos é {max}MB.":
    "“{name}” is {size}MB; the maximum for videos is {max}MB.",
  "“{name}”: envie uma imagem ou um vídeo.":
    "“{name}”: upload an image or a video.",
  "Limite de 10 mídias por post: {n} arquivo(s) não foram adicionados.":
    "Limit of 10 media per post: {n} file(s) were not added.",
  // Configurações: créditos, planos e recargas
  "Ações com IA bloqueadas por pagamento em atraso":
    "AI actions blocked due to overdue payment",
  "Pagamento da assinatura em atraso": "Subscription payment overdue",
  "Regularize o pagamento pelo link que o Asaas enviou por e-mail. As ações com IA voltam assim que o pagamento for confirmado; envios de WhatsApp e e-mail continuam funcionando.":
    "Settle the payment through the link Asaas emailed you. AI actions come back as soon as the payment is confirmed; WhatsApp and email sends keep working.",
  "Regularize até {date} pelo link que o Asaas enviou por e-mail para não bloquear as ações com IA.":
    "Settle it by {date} through the link Asaas emailed you so AI actions aren't blocked.",
  "sem desconto": "no discount",
  "Créditos de IA a cada ciclo": "AI credits every cycle",
  "Créditos que não expiram": "Credits that don't expire",
  "WhatsApp e e-mail": "WhatsApp and email",
  "Tabela de preços": "Price table",
  "Quanto maior o pacote, menor o preço por envio.":
    "The bigger the package, the lower the price per send.",
  Envios: "Sends",
  "Por envio": "Per send",
  Economia: "Savings",
  "Recomendado para você": "Recommended for you",
  "Você usou ~{usage} créditos nos últimos 30 dias: este plano cobre com folga.":
    "You used ~{usage} credits in the last 30 days: this plan covers it with room to spare.",
  "Você tem {count} envios": "You have {count} sends",
  "Comparar planos": "Compare plans",
  Recurso: "Feature",
  "Créditos por mês": "Credits per month",
  Incluso: "Included",
  "Não incluso": "Not included",
  "Até 3": "Up to 3",
  "Até 10": "Up to 10",
  Ilimitados: "Unlimited",
  Prioritário: "Priority",
  "Pagamento seguro via Asaas": "Secure payment via Asaas",
  "Pix, boleto ou cartão": "Pix, bank slip or card",
  "Nota fiscal emitida automaticamente": "Invoice issued automatically",
  "Perguntas frequentes": "Frequently asked questions",
  "Posso cancelar quando quiser?": "Can I cancel anytime?",
  "Sim. Para cancelar a assinatura, fale com o nosso suporte. Os créditos já liberados continuam no seu saldo.":
    "Yes. To cancel your subscription, contact our support. Credits already released stay in your balance.",
  "Os créditos acumulam?": "Do credits accumulate?",
  "Sim. Créditos de planos e de recargas somam ao saldo e não expiram.":
    "Yes. Credits from plans and top-ups add to your balance and don't expire.",
  "O que acontece se o saldo acabar?": "What happens if my balance runs out?",
  "As ações com IA ficam bloqueadas até você recarregar ou renovar o plano. O resto da plataforma continua funcionando.":
    "AI actions are blocked until you top up or renew your plan. The rest of the platform keeps working.",
  "Posso trocar de plano?": "Can I change plans?",
  "Sim. Fale com o nosso suporte para trocar de plano sem cobrança duplicada.":
    "Yes. Contact our support to switch plans without being charged twice.",
  "Como funcionam os envios de WhatsApp e e-mail?":
    "How do WhatsApp and email sends work?",
  "Eles usam o saldo de envios, separado dos créditos de IA. A franquia mensal do Virtus Chat é usada primeiro, e os pacotes comprados não expiram.":
    "They use your sends balance, separate from AI credits. The Virtus Chat monthly allowance is used first, and purchased packages don't expire.",
  "Envios (WhatsApp e e-mail)": "Sends (WhatsApp and email)",
  "{credits} liberados de uma vez": "{credits} released at once",
  "Pagamento único, sem assinatura": "One-time payment, no subscription",
  "Os créditos não expiram": "Credits don't expire",
  "Somam ao saldo atual": "Added to your current balance",
  "média {value}/dia": "avg {value}/day",
  "Ordenar por": "Sort by",
  "Mais caras": "Most expensive",
  "Mais usadas": "Most used",
  "A–Z": "A–Z",
  "Como os custos funcionam": "How costs work",
  "Ver multiplicadores por modelo": "See multipliers by model",
  "Saldo insuficiente": "Insufficient balance",
  "Envio de template (WhatsApp)": "Template send (WhatsApp)",
  "Pedido de permissão de ligação": "Call permission request",
  "Transcrição de áudio (WhatsApp)": "Audio transcription (WhatsApp)",
  "Ligação (WhatsApp)": "Call (WhatsApp)",
  "Resposta com IA (WhatsApp)": "AI reply (WhatsApp)",
  "Assistente de IA (WhatsApp)": "AI assistant (WhatsApp)",
  "Detecção de tarefas (WhatsApp)": "Task detection (WhatsApp)",
  "Envio de campanha de e-mail": "Email campaign send",
  "Resposta com IA (e-mail)": "AI reply (email)",
  "Conteúdo e posts": "Content and posts",
  "Imagem e vídeo": "Image and video",
  "WhatsApp e atendimento": "WhatsApp and support",
  "Ações de texto com IA mostram o custo base. Com modelos mais avançados, podem custar até {factor}× mais.":
    "AI text actions show the base cost. With more advanced models they can cost up to {factor}× more.",
  "Envios de templates do WhatsApp e de campanhas de e-mail não usam créditos: saem do saldo de envios.":
    "WhatsApp template and email campaign sends don't use credits: they come from your sends balance.",
  "Ver pacotes de envio": "See send packages",
  "Dá para ~{count} com seu saldo": "~{count} with your balance",
  "usou ~{count}× em 30 dias": "used ~{count}× in 30 days",
  Ontem: "Yesterday",
  crédito: "credit",
  "Renova em {date}": "Renews on {date}",
  "Comece a usar seus créditos": "Start using your credits",
  "Cada ação com IA desconta créditos do saldo. Veja quanto custam as mais usadas:":
    "Each AI action uses credits from your balance. Here's what the most common ones cost:",
  "Ver todos os custos": "See all costs",
  "Consumo diário": "Daily usage",
  "{date}: {amount} créditos": "{date}: {amount} credits",
  "últimos {days} dias": "last {days} days",
  "Consumo diário nos últimos {days} dias":
    "Daily usage over the last {days} days",
  "Para onde vão os créditos": "Where your credits go",
  Estornado: "Refunded",
  "Créditos adicionados": "Credits added",
  "envios de WhatsApp": "WhatsApp sends",
  "Seu saldo": "Your balance",
  "Duração estimada": "Estimated duration",
  "~{days} dias": "~{days} days",
  "sem consumo nos últimos 30 dias": "no usage in the last 30 days",
  "no ritmo dos últimos 30 dias": "at the pace of the last 30 days",
  "créditos no total, sem estornos": "credits in total, excluding refunds",
  "Buscar ação": "Search action",
  "Nenhuma ação encontrada": "No action found",
  Movimentações: "Transactions",
  Consumo: "Usage",
  Compras: "Purchases",
  "Não foi possível exportar o histórico.": "Could not export the history.",
  "Saldo, consumo, compras e planos da sua conta.":
    "Your account's balance, usage, purchases and plans.",
  "Tipo de uso": "Usage type",
  "Todos os tipos": "All types",
  Período: "Period",
  "Nenhum registro para esses filtros": "No records for these filters",
  "Sem envios disponíveis": "No sends available",
  "Franquia do mês": "Monthly allowance",
  Pacotes: "Packages",
  envios: "sends",
  "Custos por ação": "Cost per action",
  "Planos e recargas": "Plans and top-ups",
  "Créditos e consumo": "Credits and usage",
  "e-mails": "emails",
  "Seções de créditos": "Credit sections",
  "Saldo disponível": "Available balance",
  "créditos de IA": "AI credits",
  "créditos no total": "credits in total",
  "Dias de uso": "Days of use",
  "na plataforma": "on the platform",
  "E-mail Marketing": "Email Marketing",
  "WhatsApp (templates)": "WhatsApp (templates)",
  "Atividade recente": "Recent activity",
  "Ver histórico": "View history",
  "Não foi possível carregar a tabela de custos. Tente novamente.":
    "Could not load the cost table. Please try again.",
  "Gerencie sua conta, saldo de créditos e histórico de uso da IA.":
    "Manage your account, credit balance and AI usage history.",
  "{count} imagens": "{count} images",
  "{count} posts com IA": "{count} AI posts",
  "Seu plano atual": "Your current plan",
  "Não foi possível gerar o link de pagamento.":
    "Could not create the payment link.",
  "Erro ao processar pagamento.": "Error processing payment.",
  "Voltar aos planos": "Back to plans",
  "Ir para o pagamento": "Go to payment",
  Concluir: "Done",
  "O que comprar": "What to buy",
  "Ciclo de cobrança": "Billing cycle",
  "Não foi possível carregar os planos. Tente novamente.":
    "Could not load the plans. Please try again.",
  "Seu plano ({cycle})": "Your plan ({cycle})",
  "Mais popular": "Most popular",
  "Assinar {plan}": "Subscribe to {plan}",
  "Plano {plan} · {cycle}": "{plan} plan · {cycle}",
  "{credits} créditos por mês": "{credits} credits per month",
  "{credits} créditos liberados de uma vez ({monthly}/mês × {months})":
    "{credits} credits released at once ({monthly}/month × {months})",
  "Renova automaticamente a cada {period}. Cancele quando quiser.":
    "Renews automatically every {period}. Cancel anytime.",
  mês: "month",
  "{price} por {period}": "{price} per {period}",
  "economize {saving}%": "save {saving}%",
  "{credits} créditos/mês": "{credits} credits/month",
  "Melhor custo-benefício": "Best value",
  "Comprar {credits} créditos": "Buy {credits} credits",
  "Recarga {plan}": "{plan} top-up",
  "{credits} créditos de IA": "{credits} AI credits",
  "Pagamento único. Os créditos não expiram.":
    "One-time payment. Credits don't expire.",
  "{price} por crédito": "{price} per credit",
  "{credits} créditos": "{credits} credits",
  "A franquia mensal da assinatura do Virtus Chat é usada primeiro.":
    "The monthly allowance of the Virtus Chat subscription is used first.",
  "Não foi possível carregar os pacotes. Tente novamente.":
    "Could not load the packages. Please try again.",
  "Quantidade de envios": "Number of sends",
  "{count} envios": "{count} sends",
  "{price} por envio": "{price} per send",
  "Pacote {product}": "{product} package",
  "Pagamento único. O saldo do pacote não expira.":
    "One-time payment. The package balance doesn't expire.",
  "Comprar {count} envios": "Buy {count} sends",
  "Pagamento aberto": "Payment opened",
  "Confirmar compra": "Confirm purchase",
  "Você escolheu": "You chose",
  "Total a pagar": "Total due",
  "CPF ou CNPJ para a nota fiscal": "CPF or CNPJ for the invoice",
  "Informe 11 dígitos (CPF) ou 14 dígitos (CNPJ).":
    "Enter 11 digits (CPF) or 14 digits (CNPJ).",
  "O pagamento abre numa nova aba, no ambiente seguro do Asaas.":
    "Payment opens in a new tab, in Asaas's secure environment.",
  "Pagamento aberto numa nova aba": "Payment opened in a new tab",
  "O saldo é atualizado assim que o pagamento for confirmado (Pix na hora; boleto em até 3 dias úteis).":
    "Your balance is updated as soon as the payment is confirmed (Pix instantly; bank slip within 3 business days).",
  "Abrir o pagamento de novo": "Open the payment again",
  "Cada e-mail enviado em campanhas, automações ou pelo agente de IA desconta 1 envio.":
    "Each email sent in campaigns, automations or by the AI agent uses 1 send.",
  "Cada template de Marketing ou Utilidade enviado desconta 1 envio. Conversas abertas não consomem.":
    "Each Marketing or Utility template sent uses 1 send. Open conversations don't count.",
  Trimestral: "Quarterly",
  trimestre: "quarter",
  Semestral: "Semiannual",
  semestre: "semester",
  Anual: "Yearly",
  ano: "year",
  "Posts com IA e auto-resposta leve.": "AI posts and light auto-replies.",
  "Até 3 usuários": "Up to 3 users",
  "Acesso aos modelos base": "Access to base models",
  "Geração de texto": "Text generation",
  "Suporte padrão": "Standard support",
  "Geração de imagens e automação média.":
    "Image generation and medium automation.",
  "Até 10 usuários": "Up to 10 users",
  "Tudo do Starter": "Everything in Starter",
  "Geração de imagens": "Image generation",
  "Análise de sentimentos": "Sentiment analysis",
  "Suporte prioritário": "Priority support",
  "Muitos vídeos com IA e Lead Intel profundo.":
    "Lots of AI videos and deep Lead Intel.",
  "Usuários ilimitados": "Unlimited users",
  "Tudo do Pro": "Everything in Pro",
  "Geração de vídeos (avatares)": "Video generation (avatars)",
  "Múltiplas integrações": "Multiple integrations",
  "Gerente de contas": "Account manager",
  Emergência: "Emergency",
  Assinatura: "Subscription",
  "Recarga avulsa": "One-time top-up",
  "Envios (e-mail e WhatsApp)": "Sends (email and WhatsApp)",
  "Créditos de IA renovados a cada ciclo. Nos ciclos longos, todos os meses são liberados de uma vez.":
    "AI credits renewed every cycle. On longer cycles, all months are released at once.",
  "Créditos de IA avulsos, sem assinatura. Não expiram e somam ao saldo atual.":
    "One-off AI credits, no subscription. They don't expire and add to your current balance.",
  "Envios de campanhas, separados dos créditos de IA. O saldo dos pacotes não expira.":
    "Campaign sends, separate from AI credits. The package balance doesn't expire.",
};

const applyVars = (text: string, vars?: TranslationVars): string => {
  if (!vars) return text;
  return Object.entries(vars).reduce(
    (acc, [key, value]) =>
      acc.replace(new RegExp(`\\{${key}\\}`, "g"), String(value)),
    text,
  );
};

/** Traduz strings dinâmicas (atividade recente, tempos relativos, slugs de plataforma). */
const translateDynamic = (text: string, language: Language): string | null => {
  const relativePatterns: Array<{ re: RegExp; en: (n: string) => string }> = [
    { re: /^(\d+)\s*seg atrás$/i, en: (n) => `${n} sec ago` },
    { re: /^(\d+)\s*min atrás$/i, en: (n) => `${n} min ago` },
    { re: /^(\d+)h atrás$/i, en: (n) => `${n}h ago` },
    { re: /^(\d+)\s*dia atrás$/i, en: (n) => `${n} day ago` },
    { re: /^(\d+)\s*dias atrás$/i, en: (n) => `${n} days ago` },
  ];

  for (const { re, en } of relativePatterns) {
    const m = text.match(re);
    if (m) {
      return language === "en" ? en(m[1]) : text;
    }
  }

  const activityPatterns: Array<{
    re: RegExp;
    en: (platform: string) => string;
  }> = [
    {
      re: /^Post publicado no (.+)$/i,
      en: (p) => `Post published on ${p}`,
    },
    {
      re: /^Post agendado para (.+)$/i,
      en: (p) => `Post scheduled for ${p}`,
    },
    {
      re: /^Erro na publicação (.+)$/i,
      en: (p) => `Error publishing to ${p}`,
    },
  ];

  for (const { re, en } of activityPatterns) {
    const m = text.match(re);
    if (m) {
      const platform = getPlatformName(m[1]);
      return language === "en" ? en(platform) : text.replace(m[1], platform);
    }
  }

  // Só slug de plataforma (ex. badge)
  if (/^[a-z0-9]+(?:_[a-z0-9]+)+$/i.test(text.trim())) {
    return getPlatformName(text.trim());
  }

  return null;
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      const saved = localStorage.getItem("language");
      return saved === "en" || saved === "pt" ? saved : "pt";
    } catch {
      return "pt";
    }
  });

  React.useEffect(() => {
    document.documentElement.lang = language === "en" ? "en" : "pt-BR";
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem("language", lang);
    } catch (e) {
      console.error("Failed to save language setting", e);
    }
  };

  const t = (text: string, vars?: TranslationVars): string => {
    if (!text) return text;

    // 1) Lookup no dicionário (PT→EN). Em PT, mantém a chave e só aplica vars.
    let translated = text;
    if (language === "en") {
      if (translations[text]) {
        translated = translations[text];
      } else {
        const cleanText = text.trim();
        if (translations[cleanText]) {
          translated = translations[cleanText];
        } else {
          const match = Object.keys(translations).find(
            (key) => key.toLowerCase() === cleanText.toLowerCase(),
          );
          if (match) {
            translated = translations[match];
          } else {
            const dynamic = translateDynamic(text, "en");
            if (dynamic) translated = dynamic;
          }
        }
      }
    } else {
      // PT: humaniza slugs / atividade com plataforma técnica
      const dynamic = translateDynamic(text, "pt");
      if (dynamic) translated = dynamic;
    }

    return applyVars(translated, vars);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useTranslation must be used within a LanguageProvider");
  }
  return context;
};
